import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, storage } from './api'
import {
  buildAppointmentSubmitPayload,
  calculateAppointmentTimeByPreference,
  formatDateTime,
  formatDateTimeRangeText,
  formatAppointmentCode,
  getCurrentAppointmentCountLabel,
  getCurrentAppointmentCountLines,
  getAppointmentStatusLabel,
  getSiteAccentColor,
  getSiteLabel,
  getValueId,
  normalizeAppointmentBuilding,
  normalizeAppointmentName,
  normalizeExpectedPackageCount,
  normalizeRequiredAppointmentProfileField,
  normalizePhone,
  getLoginCodeCountdownSeconds,
  getProfileSaveState,
  getTopbarGreetingLabel,
  mergeIncrementalAppointmentHistory,
  mergeRecentSiteCache,
  normalizeSiteSummary,
  normalizeSitePreferences,
  normalizeAssistantMessages,
  normalizeAssistantSiteSlotPickerCard,
  normalizeCurrentAppointments,
  withAssistantSiteEntryFallback,
  resolveInstallEntryState,
  resolveInstallHelp,
  resolveProfileFromBoot,
  resolveAppointmentTimePreferenceLabel,
  getAppointmentLocationCellVisualLabel,
  normalizeAppointmentStorageAllocations,
  resolveAppointmentLocationCellNumbers,
  resolveAppointmentLocationSummary,
  resolveAppointmentLocationPreviewGrid,
  resolveCurrentAppointmentCardDisplay,
  resolveDefaultAppointmentForm,
  getAssistantAppointmentPollIntervalMs,
  getAssistantConversationRetryDelayMs,
  getAppointmentEventReconnectDelayMs,
  resolveAssistantVoiceGesture,
  getAssistantVoicePadLabel,
  shouldRefreshAppointmentHistory,
  shouldNotifyAssistantAppointmentStatusChange,
  shouldThrottleStationSync,
  shouldClearSessionForBootError,
  shouldDisableAppointmentEventStream,
  getBlockingCurrentAppointment,
  getNotificationAppointments,
  getNotificationAppointmentMessage,
  getAppointmentActionKind
} from './utils'

const DEFAULT_LOGIN = {
  phone: '',
  code: '',
  challengeId: '',
  countdown: 0,
  resendAvailableAt: 0,
  sending: false,
  verifying: false,
  identifySiteInput: '',
  identifySiteId: '',
  identifySiteLabel: '',
  identifyPhoneTail: '',
  identifyName: '',
  identifyBuilding: '',
  identifying: false,
  error: '',
  message: ''
}

const DEFAULT_SITE_STATE = {
  current: null,
  recentSites: [],
  siteCodeInput: '',
  loading: false,
  error: '',
  announcement: null,
  announcementExpanded: false,
  slots: [],
  userConfig: null,
  appointmentSettings: null,
  slotsLoading: false,
  busy: false
}

const DEFAULT_APPOINTMENT_STATE = {
  current: [],
  history: [],
  historyPage: 1,
  historyHasMore: true,
  historyLoadedAt: 0,
  historyLastAttemptAt: 0,
  historyNeedsRefresh: false,
  historySyncCursor: null,
  historyLoading: false,
  currentLoading: false,
  actionBusyId: '',
  error: '',
  form: resolveDefaultAppointmentForm({}),
  formSubmitting: false,
  formError: '',
  appointmentExpanded: true,
  historyDetail: null,
  copiedCode: ''
}

const LOGIN_CODE_RESEND_SECONDS = 120
const IDENTIFY_LOGIN_ENABLED = String(import.meta.env.VITE_ENABLE_IDENTIFY_LOGIN || '').trim().toLowerCase() === 'true'
const IDENTIFY_MATCH_TYPES = {
  NAME_OR_RECIPIENT: 'name_or_recipient',
  BUILDING: 'building'
}
const DEV_PREVIEW_MODE = import.meta.env.DEV && String(import.meta.env.VITE_PREVIEW_MODE || '').trim().toLowerCase() === 'true'
const DEV_PREVIEW_TOKEN = 'dev-preview-token'
const COOKIE_SESSION_TOKEN = 'cookie-session'
const ASSISTANT_INITIAL_MESSAGE_LIMIT = 20
const ASSISTANT_HISTORY_LOAD_THRESHOLD_PX = 48

const DEV_PREVIEW_USER = {
  _id: 'dev-preview-user',
  name: '微信11',
  phone: '13800138011',
  appointmentBuilding: '1230'
}

const DEV_PREVIEW_SITE = {
  _id: 'dev-preview-site',
  name: '菜鸟0',
  siteCode: 'DEV-001'
}

const DEV_PREVIEW_PROFILE = {
  name: DEV_PREVIEW_USER.name,
  building: DEV_PREVIEW_USER.appointmentBuilding
}

const DEV_PREVIEW_SLOTS = [
  { value: '18-19', label: '18点-19点', available: true },
  { value: '22-23', label: '22点-23点', available: true },
  { value: '10min', label: '10分钟左右', available: true },
  { value: '1hour', label: '1小时左右', available: true }
]

const DEV_PREVIEW_HISTORY = [
  {
    _id: 'dev-history-1',
    name: '妈妈',
    status: 'expired',
    appointment_time: '2026-04-20T23:11:00+08:00',
    time_preference: 'halfhour',
    expected_package_count: 1,
    pickup_code: 'A1088'
  },
  {
    _id: 'dev-history-2',
    name: '菜鸟0',
    status: 'completed',
    appointment_time: '2026-04-19T22:38:00+08:00',
    time_preference: '1hour',
    expected_package_count: 5,
    pickup_code: 'B2049'
  },
  {
    _id: 'dev-history-3',
    name: '菜鸟0',
    status: 'cancelled',
    appointment_time: '2026-04-11T12:26:00+08:00',
    time_preference: '1hour',
    expected_package_count: 1,
    pickup_code: 'C8812'
  },
  {
    _id: 'dev-history-4',
    name: '菜鸟0',
    status: 'completed',
    appointment_time: '2026-04-10T23:16:00+08:00',
    time_preference: '1hour',
    expected_package_count: 2,
    pickup_code: 'D3345'
  }
]

const EXPECTED_PACKAGE_COUNT_OPTIONS = Array.from({ length: 6 }, (_, index) => index + 1)

function createDevPreviewSiteState() {
  return {
    ...DEFAULT_SITE_STATE,
    current: DEV_PREVIEW_SITE,
    recentSites: [DEV_PREVIEW_SITE],
    slots: DEV_PREVIEW_SLOTS,
    userConfig: { sharingEnabled: true },
    appointmentSettings: { allowMessage: true },
    announcement: null,
    loading: false,
    slotsLoading: false,
    busy: false,
    error: ''
  }
}

function createDevPreviewAppointmentState() {
  return {
    ...DEFAULT_APPOINTMENT_STATE,
    current: [
      {
        _id: 'dev-notif-invalid',
        site_name: '菜鸟0',
        is_invalid: true,
        name: '王小二',
        phone_tail: '8888',
        building: '10号楼',
        time_preference: '1hour',
        createdAt: '2026-05-28T16:30:00+08:00',
        expected_package_count: 2
      },
      {
        _id: 'dev-notif-expired',
        site_name: '菜鸟0',
        status: 'expired',
        name: '李四',
        phone_tail: '9999',
        building: '5号楼',
        time_preference: 'halfhour',
        createdAt: '2026-05-28T15:20:00+08:00',
        expected_package_count: 1
      }
    ],
    history: DEV_PREVIEW_HISTORY,
    historyPage: 1,
    historyHasMore: false,
    historyLoadedAt: Date.now(),
    historyLastAttemptAt: Date.now(),
    historyNeedsRefresh: false,
    historySyncCursor: null,
    historyLoading: false,
    currentLoading: false,
    actionBusyId: '',
    error: '',
    form: resolveDefaultAppointmentForm(DEV_PREVIEW_USER),
    formSubmitting: false,
    formError: '',
    appointmentExpanded: true,
    historyDetail: null,
    copiedCode: ''
  }
}

function useHashRoute() {
  const readRoute = useCallback(() => {
    const hash = String(window.location.hash || '').replace(/^#/, '').trim()
    return hash || 'home'
  }, [])
  const [route, setRoute] = useState(readRoute)

  useEffect(() => {
    const handleHashChange = () => setRoute(readRoute())
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [readRoute])

  const navigate = useCallback((nextRoute) => {
    const normalized = String(nextRoute || 'home').trim() || 'home'
    window.location.hash = normalized
    setRoute(normalized)
  }, [])

  return [route, navigate]
}

function toErrorMessage(error, fallback = '请求失败，请稍后重试') {
  if (!error) return fallback
  if (typeof error === 'string') return error
  const base = String(error.message || error.error || fallback)
  const details = error.details
  if (Array.isArray(details) && details.length) {
    const hints = details.map((d) => d.msg || d.message || '').filter(Boolean)
    if (hints.length) return `${base}：${hints.join('；')}`
  }
  return base
}

function toConfirmPickedUpErrorMessage(error) {
  const code = String(error?.code || '').trim()
  if (code === 'APPOINTMENT_ALREADY_COMPLETED') {
    return '这条预约已完成取件，我已为你刷新最新状态'
  }
  if (code === 'APPOINTMENT_STATUS_NOT_PICKUP_READY') {
    return '当前预约还不能确认取件，请先确认包裹已入库'
  }
  if (code === 'APPOINTMENT_FORBIDDEN') {
    return '这条预约不属于当前账号，无法确认取件'
  }
  return toErrorMessage(error, '确认取件失败，请稍后重试')
}

function toIdentifyLoginErrorMessage(error) {
  const code = String(error?.code || '').trim()
  if (code === 'IDENTIFY_LOGIN_DISABLED') return '简识匹配登录暂未开放'
  if (code === 'IDENTIFY_LOGIN_INPUT_INVALID') return '请检查站点、尾号和匹配信息后重试'
  if (code === 'IDENTIFY_LOGIN_AMBIGUOUS') return '匹配到多个用户，请改用短信验证码登录'
  if (code === 'IDENTIFY_LOGIN_CONFLICT') return '当前资料存在冲突，请改用短信验证码登录'
  if (code === 'IDENTIFY_LOGIN_HIGH_RISK') return '当前环境暂不支持简识匹配登录，请改用短信验证码登录'
  if (code === 'IDENTIFY_LOGIN_QUOTA_EXCEEDED') return '当前站点的简识登录预约次数已达上限，请先完成取件或改用短信验证码登录'
  return toErrorMessage(error, '简识匹配登录失败，请稍后重试')
}

function resolveScannedStationInputValue(site, fallbackValue = '') {
  const normalizedFallback = String(fallbackValue || '').trim()
  const candidates = [
    site?.siteCode,
    site?.site_code,
    site?.code,
    getValueId(site),
    normalizedFallback
  ]
  return String(candidates.find((value) => String(value || '').trim()) || '').trim()
}

function getAuthStatusPresentation(user = {}) {
  const authStrength = String(user?.auth_strength || '').trim()
  const verificationStatus = String(user?.verification_status || '').trim()
  const phoneTailDigits = String(user?.phone_tail || user?.phone || '').replace(/\D/g, '').slice(-4)
  const hasPhoneTail = Boolean(phoneTailDigits)
  const tailLabel = hasPhoneTail ? `尾号 ${phoneTailDigits}` : ''

  if (authStrength === 'weak_identification' || verificationStatus === 'weak_identification') {
    return {
      label: '简识登录',
      tone: 'warning',
      detail: '完成一次成功取件后，将自动升级为已验证用户。',
      phoneMeta: hasPhoneTail ? `当前仅校验${tailLabel}` : '当前仅完成基础身份匹配',
      quotaHint: '完成一次成功取件前，每个站点最多可预约 2 次。'
    }
  }

  if (authStrength === 'verified_by_cycle' || verificationStatus === 'verified_by_cycle') {
    return {
      label: '已完成核验',
      tone: 'success',
      detail: '你已通过取件闭环完成身份确认，可继续正常预约。',
      phoneMeta: hasPhoneTail
        ? (user?.full_phone_available === false ? `已完成核验，保留${tailLabel}` : `手机号${tailLabel}`)
        : '',
      quotaHint: ''
    }
  }

  return {
    label: '手机号认证',
    tone: 'success',
    detail: '',
    phoneMeta: hasPhoneTail
      ? (user?.full_phone_available === false ? `当前保留${tailLabel}` : `手机号${tailLabel}`)
      : '',
    quotaHint: ''
  }
}

function resolveAppointmentCabinetLabel(appointment) {
  return String(
    appointment?.cabinetLabel ||
    appointment?.cabinet_label ||
    appointment?.pickup_info?.storage?.cabinetName ||
    appointment?.pickup_info?.cabinetLabel ||
    appointment?.cabinet_info?.cabinet_number ||
    appointment?.cabinet_info?.dev_name ||
    appointment?.cabinet_id?.cabinet_number ||
    appointment?.cabinet_id?.dev_name ||
    ''
  ).trim()
}

function getAppointmentSiteName(appointment) {
  return String(
    appointment?.site_name ||
    appointment?.site?.name ||
    appointment?.site_id?.name ||
    appointment?.station_name ||
    ''
  ).trim()
}

function getAppointmentSiteId(appointment) {
  return getValueId(appointment?.site_id) || getValueId(appointment?.site)
}

function getAppointmentCardAccent(appointment) {
  return getSiteAccentColor(getAppointmentSiteName(appointment) || getAppointmentSiteId(appointment))
}

function getSiteFromSelection(currentSite, recentSites) {
  return currentSite || recentSites[0] || null
}

function isDevPreviewSite(site = {}) {
  const normalizedId = getValueId(site)
  if (!site || typeof site !== 'object') return false
  const siteCode = String(site.siteCode || site.site_code || '').trim()
  const label = String(site.name || site.siteName || site.site_name || '').trim()
  return normalizedId === DEV_PREVIEW_SITE._id || siteCode === DEV_PREVIEW_SITE.siteCode || label === DEV_PREVIEW_SITE.name
}

function createInitialSiteState() {
  const { current, recentSites } = readSanitizedSiteCache()
  return {
    ...DEFAULT_SITE_STATE,
    current,
    recentSites
  }
}

function isDevPreviewProfile(profile = {}) {
  if (!profile || typeof profile !== 'object') return false
  const name = String(profile.name || '').trim()
  const building = String(profile.building || '').trim()
  return name === DEV_PREVIEW_PROFILE.name && building === DEV_PREVIEW_PROFILE.building
}

function readSanitizedSiteCache() {
  const current = normalizeSiteSummary(storage.readCurrentSite() || {})
  const recentSites = mergeRecentSiteCache(current, storage.readRecentSites())
  return {
    current: isDevPreviewSite(current) ? null : current,
    recentSites: recentSites.filter((item) => !isDevPreviewSite(item))
  }
}

function readSanitizedSiteCacheForUser(userId) {
  if (!userId) {
    return {
      current: null,
      recentSites: []
    }
  }

  const current = normalizeSiteSummary(storage.readCurrentSiteForUser(userId) || {})
  const recentSites = mergeRecentSiteCache(current, storage.readRecentSitesForUser(userId))
  return {
    current: isDevPreviewSite(current) ? null : current,
    recentSites: recentSites.filter((item) => !isDevPreviewSite(item))
  }
}

function normalizeServerSitePreferences(preferences = {}) {
  const normalized = normalizeSitePreferences(preferences || {})
  return {
    currentSite: normalized.currentSite,
    recentSites: normalized.recentSites
  }
}

function normalizeForSignature(value) {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForSignature(item))
  }

  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        const nextValue = normalizeForSignature(value[key])
        if (nextValue !== undefined) {
          acc[key] = nextValue
        }
        return acc
      }, {})
  }

  return value
}

function buildSiteBundleSignature(bundle = {}) {
  return JSON.stringify(normalizeForSignature(bundle))
}

function readSanitizedProfile() {
  const storedProfile = storage.readProfile()
  if (!DEV_PREVIEW_MODE && isDevPreviewProfile(storedProfile)) {
    return null
  }
  return storedProfile
}

function createInitialProfileState() {
  return normalizeProfileData(readSanitizedProfile() || {}, {})
}

function isObjectIdLike(value) {
  return /^[a-fA-F0-9]{24}$/.test(String(value || '').trim())
}

const looksLikeSiteQrPayload = (value) => /^SPK:SITE:/i.test(String(value || '').trim())

function getAssistantMessageCursor(message = {}) {
  return String(message?._id || message?.id || message?.clientMessageId || '').trim()
}

function resolveAssistantMessagesPage(payload = {}, fallbackMessages = []) {
  const data = payload && typeof payload === 'object' ? payload : {}
  const pagination = data.pagination && typeof data.pagination === 'object' ? data.pagination : {}
  const messages = Array.isArray(data.messages) ? data.messages : fallbackMessages
  const explicitCursor = String(
    data.nextBeforeCursor ||
    data.next_before_cursor ||
    pagination.nextBeforeCursor ||
    pagination.next_before_cursor ||
    ''
  ).trim()
  const hasMoreOlder = (
    data.hasMoreOlder === true ||
    data.has_more_older === true ||
    pagination.hasMoreOlder === true ||
    pagination.has_more_older === true ||
    Boolean(explicitCursor)
  )
  const oldestMessageCursor = getAssistantMessageCursor(messages[0])

  return {
    messages,
    hasMoreOlder,
    nextBeforeCursor: explicitCursor || (hasMoreOlder ? oldestMessageCursor : '')
  }
}

function mergeOlderAssistantMessages(existingMessages = [], olderMessages = []) {
  const currentMessages = Array.isArray(existingMessages) ? existingMessages : []
  const nextOlderMessages = Array.isArray(olderMessages) ? olderMessages : []
  const existingIds = new Set(currentMessages.map((message) => message.id))
  return [
    ...nextOlderMessages.filter((message) => !existingIds.has(message.id)),
    ...currentMessages
  ]
}

function hasDisplaySiteLabel(site) {
  return !!String(getSiteLabel(site)).trim()
}

function appointmentTimeLabel(appointment) {
  if (!appointment) return ''
  if (appointment.pickup_deadline) {
    return `应于 ${formatDateTimeRangeText(appointment.pickup_deadline)} 前取件`
  }
  if (appointment.appointment_time) {
    return formatDateTime(appointment.appointment_time)
  }
  return ''
}

function getProfileInitials(name) {
  const text = String(name || '').trim()
  if (!text) return '我'
  return text.slice(0, 1)
}

function normalizeProfileData(profile = {}, fallback = {}) {
  return {
    name: normalizeAppointmentName(profile.name || fallback.name || ''),
    building: normalizeAppointmentBuilding(profile.building || fallback.building || '')
  }
}

function createProfileFromUser(user = {}) {
  return normalizeProfileData({
    name: user.name || '',
    building: user.appointmentBuilding || user.appointment_building || user.building || user.buildingNo || user.building_no || ''
  })
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v3" />
      <path d="M12 19v3" />
      <path d="M2 12h3" />
      <path d="M19 12h3" />
      <path d="M4.9 4.9l2.1 2.1" />
      <path d="M17 17l2.1 2.1" />
      <path d="M19.1 4.9 17 7" />
      <path d="M7 17 4.9 19.1" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5Z" />
    </svg>
  )
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4.5 8.5A2.5 2.5 0 0 1 7 6h2.1l1.1-1.8A2 2 0 0 1 12 3.2h0a2 2 0 0 1 1.8 1L15 6H17a2.5 2.5 0 0 1 2.5 2.5v8A2.5 2.5 0 0 1 17 19H7a2.5 2.5 0 0 1-2.5-2.5v-8Z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  )
}

function MicrophoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="9" y="4" width="6" height="10" rx="3" />
      <path d="M6 11a6 6 0 0 0 12 0" />
      <path d="M12 18v3" />
      <path d="M8 21h8" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 20V8" />
      <path d="M6 12l6-6 6 6" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

function KeyboardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3.5" y="6.5" width="17" height="11" rx="2.5" />
      <path d="M6 10h0.01" />
      <path d="M9 10h0.01" />
      <path d="M12 10h0.01" />
      <path d="M15 10h0.01" />
      <path d="M18 10h0.01" />
      <path d="M7 13h10" />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="8" r="4" />
    </svg>
  )
}

function MapPinIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <line x1="9" y1="22" x2="9" y2="16" />
      <line x1="15" y1="22" x2="15" y2="16" />
      <line x1="9" y1="16" x2="15" y2="16" />
      <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01" />
    </svg>
  )
}

function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}



function AssistantInlineAppointmentConfirmCard({ card, onChoice, context = {}, dismissible = true }) {
  const compactMode = dismissible === false
  const expectedPackageCount = normalizeExpectedPackageCount(
    context.appointmentForm?.expectedPackageCount ?? card.expectedPackageCount,
    1
  )
  const visibleCountOptions = [
    ...EXPECTED_PACKAGE_COUNT_OPTIONS.slice(0, 5).map((count) => ({
      value: count,
      label: `${count}单`,
      selected: expectedPackageCount === count
    })),
    {
      value: 6,
      label: '6单+',
      selected: expectedPackageCount >= 6
    }
  ]
  const slotOptions = (Array.isArray(card.slotOptions) ? card.slotOptions : context.siteSlots)
    .filter((slot) => slot && typeof slot === 'object' && String(slot.value || '').trim())
    .map((slot) => ({
      value: String(slot.value || '').trim(),
      label: resolveAppointmentTimePreferenceLabel(slot.label || slot.value),
      available: slot.available !== false
    }))
  const selectedTime = String(context.appointmentForm?.selectedTime || '').trim()
  const siteLabel = String(card.siteLabel || context.currentSiteName || '当前驿站').trim()
  const slotLabel = String(
    resolveAppointmentTimePreferenceLabel(selectedTime) ||
    card.slotLabel ||
    '请选择时段'
  ).trim()
  const recipientLabel = String(context.profile?.name || '未设置').trim()
  const buildingLabel = String(context.profile?.building || '未设置').trim()
  const remarkLabel = String(context.appointmentForm?.message || '').trim()
  const metaParts = [
    `取件人 ${recipientLabel || '未设置'}`,
    `楼号 ${buildingLabel || '未设置'}`
  ]

  if (remarkLabel) {
    metaParts.push(`备注 ${remarkLabel}`)
  }

  return (
    <div className={`assistant-inline-confirm-card ${compactMode ? 'is-compact' : ''}`}>
      <div className="assistant-inline-confirm-summary-line">
        <strong>{siteLabel}</strong>
        <span aria-hidden="true">·</span>
        <strong>{slotLabel}</strong>
      </div>
      {context.appointmentSettings?.enabled === false ? (
        <div className="appointment-closed-notice" style={{ 
          margin: '16px 0', 
          padding: '24px 20px', 
          background: 'rgba(244, 63, 94, 0.08)', 
          border: '1px solid rgba(244, 63, 94, 0.18)', 
          borderRadius: '16px', 
          textAlign: 'center',
          color: '#fecdd3'
        }}>
          <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: '48px', height: '48px', margin: '0 auto 12px', display: 'block', fill: 'none', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' }}>
            <circle cx="12" cy="12" r="10" />
            <path d="m15 9-6 6" />
            <path d="m9 9 6 6" />
          </svg>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '800', color: '#fff1f2' }}>预约已关闭</h3>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5', opacity: 0.9 }}>当前驿站已关闭预约功能，暂停接收新的预约。</p>
        </div>
      ) : (
        <>
          {slotOptions.length ? (
            <div className="assistant-inline-confirm-slot-row">
              <div className="slot-grid assistant-slot-grid">
                {slotOptions.length ? slotOptions.map((slot) => (
                  <button
                    key={slot.value}
                    className={`slot-chip ${selectedTime === slot.value ? 'selected' : ''} ${slot.available === false ? 'disabled' : ''}`}
                    type="button"
                    onClick={() => onChoice?.(card, {
                      type: 'set_selected_time',
                      value: slot.value,
                      label: slot.label
                    })}
                    disabled={context.formSubmitting || slot.available === false}
                  >
                    <span>{slot.label}</span>
                    {slot.available === false ? <small>已满</small> : null}
                  </button>
                )) : null}
              </div>
            </div>
          ) : null}
          <div className="assistant-inline-confirm-count-label">预约单数：</div>
          <div className="assistant-inline-confirm-count-row">
            <div className="assistant-chip-grid assistant-confirm-count-chip-grid">
              {visibleCountOptions.map((option) => {
                const normalizedCount = normalizeExpectedPackageCount(option.value, 1)
                return (
                  <button
                    key={normalizedCount}
                    className={`assistant-chip ${option.selected ? 'selected' : ''}`}
                    type="button"
                    onClick={() => onChoice?.(card, {
                      type: 'set_expected_package_count',
                      value: String(normalizedCount)
                    })}
                    disabled={context.formSubmitting}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
            {compactMode ? (
              <button
                className="btn btn-primary assistant-inline-confirm-submit-btn"
                type="button"
                onClick={() => onChoice?.(card, { type: 'create_appointment', value: 'submit', label: '提交预约' })}
                disabled={context.formSubmitting}
              >
                {context.formSubmitting ? '提交中...' : '提交预约'}
              </button>
            ) : null}
          </div>
          {context.formError ? <div className="form-hint error">{context.formError}</div> : null}
        </>
      )}
      {!compactMode ? (
        <div className="assistant-inline-confirm-actions">
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => onChoice?.(card, { type: 'dismiss_appointment_confirm', value: 'cancel' })}
            disabled={context.formSubmitting}
          >
            取消
          </button>
          {context.appointmentSettings?.enabled !== false ? (
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => onChoice?.(card, { type: 'create_appointment', value: 'submit', label: '提交预约' })}
              disabled={context.formSubmitting}
            >
              {context.formSubmitting ? '提交中...' : '提交预约'}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="assistant-inline-confirm-meta" aria-label="预约补充信息">
        {metaParts.map((part) => (
          <span key={part}>{part}</span>
        ))}
      </div>
    </div>
  )
}

function AssistantSiteEntryCard({ card, onChoice, context = {} }) {
  const [siteIdValue, setSiteIdValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [resolved, setResolved] = useState(null)

  const description = String(
    String(card?.reason || '').trim() === 'no_site_found'
      ? '你还没有可用的站点，请先扫码或输入站点ID，我帮你查询站点信息。'
      : (card?.description || '你还没有可用的站点，请先扫码或输入站点ID，我帮你查询站点信息。')
  ).trim()
  const inputLabel = String(card?.inputLabel || '站点码').trim()
  const placeholder = String(card?.placeholder || '请输入站点码').trim()
  const scanLabel = String(card?.scanLabel || '扫码').trim()
  const submitLabel = String(card?.submitLabel || '确认').trim()

  const handleScanClick = useCallback(() => {
    setError('')
    void onChoice?.(card, {
      type: 'open_site_scanner',
      value: String(siteIdValue || '').trim()
    })
  }, [card, onChoice, siteIdValue])

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault()
    const nextValue = String(siteIdValue || '').trim()
    if (!nextValue) {
      setError('请输入站点码')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      const result = await onChoice?.(card, {
        type: 'submit_site_entry',
        value: nextValue
      })
      if (!result?.ok) {
        throw new Error(result?.error || '未找到驿站')
      }
      setResolved({
        siteId: result.siteId,
        siteLabel: result.siteLabel
      })
    } catch (submitError) {
      setError(toErrorMessage(submitError, '未找到驿站'))
    } finally {
      setSubmitting(false)
    }
  }, [card, onChoice, siteIdValue])

  if (resolved) {
    return (
      <AssistantInlineAppointmentConfirmCard
        card={{
          type: 'appointment_confirm_card',
          siteLabel: resolved.siteLabel || resolved.siteId || '当前驿站',
          slotOptions: context.siteSlots
        }}
        onChoice={onChoice}
        context={context}
        dismissible={false}
      />
    )
  }

  return (
    <div className="assistant-site-entry-card">
      <form className="assistant-site-entry-form" onSubmit={handleSubmit}>
        {description ? <div className="assistant-site-entry-description">{description}</div> : null}
        <div className="assistant-site-entry-primary-row">
          <input
            className="assistant-site-entry-input"
            type="text"
            value={siteIdValue}
            onChange={(event) => setSiteIdValue(event.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            inputMode="text"
            aria-label={inputLabel}
            disabled={submitting}
          />
          <div className="assistant-site-entry-action-row">
            <button className="btn btn-light assistant-site-entry-scan-btn" type="button" onClick={handleScanClick} disabled={submitting}>
              {scanLabel}
            </button>
            <button className="btn btn-primary assistant-site-entry-confirm-btn" type="submit" disabled={submitting}>
              {submitting ? '加载中...' : submitLabel}
            </button>
          </div>
        </div>
        {error ? <div className="form-hint error">{error}</div> : null}
      </form>
    </div>
  )
}

function AssistantActionCard({ card, onChoice, context = {} }) {
  if (!card || typeof card !== 'object') return null

  if (card.type === 'site_slot_picker') {
    const picker = normalizeAssistantSiteSlotPickerCard(card)
    return (
      <div className="assistant-card assistant-site-slot-card">
        {picker.title ? <div className="assistant-card-title">{picker.title}</div> : null}
        <div className="assistant-site-slot-list">
          {picker.sites.map((site) => (
            <div className="assistant-site-slot-group" key={site.siteId}>
              <div className="assistant-site-slot-site">{site.siteLabel}</div>
              <div className="assistant-site-slot-grid">
                {site.slots.map((slot) => (
                  <button
                    key={`${site.siteId}-${slot.value}`}
                    className="assistant-site-slot-choice"
                    type="button"
                    onClick={() => onChoice?.(card, {
                      type: 'select_site_slot',
                      site,
                      slot,
                      value: `${site.siteId}:${slot.value}`,
                      label: `${site.siteLabel} · ${slot.label}`
                    })}
                  >
                    <span>{slot.label}</span>
                    {slot.max > 0 ? <small>{slot.current}/{slot.max}</small> : null}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (card.type === 'site_entry_card') {
    return <AssistantSiteEntryCard card={card} onChoice={onChoice} context={context} />
  }

  if (card.type === 'quick_replies') {
    const replies = Array.isArray(card.replies) ? card.replies : []
    const quickReplyTitle = replies.length ? '可选项' : ''
    return (
      <div className="assistant-quick-replies-inline">
        {quickReplyTitle ? <div className="assistant-quick-replies-title">{quickReplyTitle}</div> : null}
        <div className="assistant-card-choice-grid">
          {replies.map((reply, index) => (
            <button
              key={reply.value || reply.label || index}
              className="assistant-card-choice"
              type="button"
              onClick={() => onChoice?.(card, reply)}
            >
              {reply.label || reply.value}
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (card.type === 'form_card') {
    const fields = Array.isArray(card.fields) ? card.fields : []
    const submitAction = card.submitAction && typeof card.submitAction === 'object' ? card.submitAction : null
    return (
      <div className="assistant-card assistant-form-card">
        {card.title ? <div className="assistant-card-title">{card.title}</div> : null}
        <div className="assistant-card-list">
          {fields.length ? fields.map((field, index) => (
            <div className="assistant-card-list-item" key={field.name || field.label || index}>
              <strong>{field.label || field.name}</strong>
              <span>{String(field.value || field.placeholder || '待填写')}</span>
            </div>
          )) : <div className="assistant-card-empty">暂无需要确认的信息</div>}
        </div>
        {submitAction?.type ? (
          <button
            className="assistant-card-submit"
            type="button"
            onClick={() => onChoice?.(card, {
              ...submitAction,
              value: submitAction.type,
              label: submitAction.label || '确认'
            })}
          >
            {submitAction.label || '确认'}
          </button>
        ) : null}
      </div>
    )
  }

  if (card.type === 'choice_card') {
    const choices = Array.isArray(card.choices) ? card.choices : []
    return (
      <div className="assistant-card assistant-choice-card">
        {card.title ? <div className="assistant-card-title">{card.title}</div> : null}
        <div className="assistant-card-choice-grid">
          {choices.map((choice, index) => (
            <button
              key={choice.value || choice.label || index}
              className="assistant-card-choice"
              type="button"
              onClick={() => onChoice?.(card, choice)}
            >
              {choice.label || choice.value}
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (card.type === 'history_list') {
    const items = Array.isArray(card.items) ? card.items.slice(0, 4) : []
    return (
      <div className="assistant-card assistant-history-card">
        {card.title ? <div className="assistant-card-title">{card.title}</div> : null}
        <div className="assistant-card-list">
          {items.length ? items.map((item, index) => (
            <div className="assistant-card-list-item" key={item._id || item.id || index}>
              <strong>{String(item.name || item.recipient || '预约记录').trim()}</strong>
              <span>{formatDateTime(item.appointment_time) || resolveAppointmentTimePreferenceLabel(item.time_preference) || getAppointmentStatusLabel(item.status)}</span>
            </div>
          )) : <div className="assistant-card-empty">暂无记录</div>}
        </div>
      </div>
    )
  }

  if (card.type === 'appointment_card') {
    const appointment = card.appointment || {}
    return (
      <div className="assistant-card assistant-appointment-card">
        {card.title ? <div className="assistant-card-title">{card.title}</div> : null}
        <div className="assistant-card-list-item">
          <strong>{String(appointment.name || appointment.recipient || '当前预约').trim()}</strong>
          <span>{getAppointmentStatusLabel(appointment.status)} · {resolveAppointmentTimePreferenceLabel(appointment.time_preference) || formatDateTime(appointment.appointment_time) || '暂无时段'}</span>
        </div>
      </div>
    )
  }

  if (card.type === 'appointment_preview') {
    const preview = card.preview || {}
    const statusLabel = String(preview.statusLabel || '').trim()
    const title = String(preview.title || '').trim()
    const summaryParts = [
      `取件人 ${String(preview.recipientLabel || '未设置').trim()}`,
      `尾号 ${String(preview.tailLabel || '----').trim()}`
    ].filter(Boolean)
    return (
      <div className="assistant-card assistant-appointment-preview-card">
        {(title || statusLabel) ? (
          <div className="assistant-appointment-preview-head">
            {title ? <div className="assistant-card-title assistant-appointment-preview-title">{title}</div> : null}
            {statusLabel ? <span className={`assistant-appointment-preview-status ${statusLabel === '取消' ? 'is-cancelled' : 'is-success'}`}>{statusLabel}</span> : null}
          </div>
        ) : null}
        <div className="assistant-appointment-preview-site-line">
          <span>驿站</span>
          <strong>{String(preview.siteLabel || '当前驿站').trim()}</strong>
        </div>
        <div className="assistant-appointment-preview-meta-line">
          {summaryParts.map((part, index) => (
            <span key={part} className="assistant-appointment-preview-meta-part">
              {part}
              {index < summaryParts.length - 1 ? <span className="assistant-appointment-preview-dot" aria-hidden="true">·</span> : null}
            </span>
          ))}
        </div>
      </div>
    )
  }

  if (card.type === 'current_appointment_card') {
    return <AssistantCurrentAppointmentSummary card={card} variant="inline" />
  }

  if (card.type === 'appointment_confirm_card') {
    return <AssistantInlineAppointmentConfirmCard card={card} onChoice={onChoice} context={context} />
  }

  return null
}

function AssistantCurrentAppointmentSummary({ card, variant = 'inline', onClose, onAction, busy = false }) {
  const [confirmState, setConfirmState] = useState(false)

  useEffect(() => {
    if (!confirmState) return
    const timer = setTimeout(() => {
      setConfirmState(false)
    }, 4000)
    return () => clearTimeout(timer)
  }, [confirmState])

  const apptId = card?.appointment?._id
  useEffect(() => {
    setConfirmState(false)
  }, [apptId, busy])

  if (!card || typeof card !== 'object') return null

  if (card.type !== 'current_appointment_card') return null

  const appointment = card.appointment || {}
  const tailLabel = String(card.tailLabel || '----').trim()
  const displayState = resolveCurrentAppointmentCardDisplay(appointment, { phoneTail: tailLabel })
  const statusLabel = displayState.statusLabel
  const showStatusTag = statusLabel && statusLabel !== '已入库'
  const siteLabel = String(card.siteLabel || getAppointmentSiteName(appointment) || '当前驿站').trim()
  const countLabel = getCurrentAppointmentCountLabel(appointment)
  const countLines = getCurrentAppointmentCountLines(appointment)
  const recipientLabel = String(card.recipientLabel || appointment.name || appointment.recipient || '未设置').trim()
  const buildingLabel = String(card.buildingLabel || appointment.building || '暂无').trim()
  const storageRemark = String(appointment.storage_remark || appointment.storageRemark || '').trim()
  const pickupLabel = displayState.pickupLabel
  const pickupValue = displayState.pickupValue
  const cabinetLabel = resolveAppointmentCabinetLabel(appointment)
  const locationGrid = resolveAppointmentLocationPreviewGrid(appointment)
  const locationLabel = resolveAppointmentLocationSummary(appointment)
  const locationCellNumbers = resolveAppointmentLocationCellNumbers(appointment, {
    cellCount: locationGrid.cellCount,
    cols: locationGrid.cols
  })
  const locationSummary = [cabinetLabel, locationLabel].filter(Boolean).join(' · ') || locationLabel
  const canConfirmPickedUp = ['inline', 'popover'].includes(variant) && typeof onAction === 'function' && String(appointment.status || '').trim() === 'stored' && String(appointment._id || '').trim()

  const expectedCount = normalizeExpectedPackageCount(
    appointment?.expected_package_count ?? appointment?.expectedPackageCount ?? 1,
    1
  )
  const storedCount = String(appointment?.status || '').trim().toLowerCase() === 'stored'
    ? normalizeExpectedPackageCount(
        appointment?.package_count ?? appointment?.packageCount ?? expectedCount,
        expectedCount
      )
    : 0

  const allocations = normalizeAppointmentStorageAllocations(appointment)
  const originalCodes = allocations
    .filter((alloc) => alloc.compartmentLabel && alloc.trackingNumber)
    .map((alloc) => `${alloc.compartmentLabel}: ${alloc.trackingNumber}`)

  return (
    <div className={`assistant-current-appointment-card ${variant === 'popover' ? 'is-popover' : 'is-inline'}`}>
      <div className="assistant-current-appointment-head">
        <div className="assistant-current-appointment-site-icon">
          <MapPinIcon />
        </div>
        <div className="assistant-current-appointment-copy">
          <strong title={siteLabel}>{siteLabel}</strong>
          <span className="assistant-current-appointment-site-sub">快递驿站</span>
        </div>
        <div className="assistant-current-appointment-head-actions">
          {statusLabel ? (
            <span className={`assistant-appointment-preview-status is-status-${String(appointment.status || '').trim().toLowerCase()}`}>
              {statusLabel}
            </span>
          ) : null}
          {variant === 'popover' ? (
            <button className="assistant-current-appointment-close-btn" type="button" onClick={onClose} aria-label="关闭当前预约浮层">
              <CloseIcon />
            </button>
          ) : null}
        </div>
      </div>

      <div className="assistant-current-appointment-code-row">
        {pickupLabel && pickupValue && pickupValue !== '待处理' ? (
          <div className="assistant-current-appointment-code-block" aria-label={`${pickupLabel} ${pickupValue}`}>
            <span className="code-label">{pickupLabel}</span>
            <strong className="code-value">{pickupValue}</strong>
          </div>
        ) : null}

        <div className={`assistant-current-appointment-count-banner ${pickupLabel && pickupValue && pickupValue !== '待处理' ? 'has-code' : 'is-fullwidth'}`}>
          <span className="count-banner-label">
            {String(appointment.status || '').trim() === 'pending' ? '待处理预约' : '待取件预约'}
          </span>
          <div className="count-banner-value">
            <strong>{storedCount || expectedCount}</strong>
            <span>件</span>
          </div>
        </div>
      </div>

      {locationSummary ? (
        <div className="assistant-current-appointment-location-block" aria-label={`位置 ${locationSummary}`}>
          <div className="assistant-current-appointment-location-head">
            <span>位置</span>
            <strong title={locationSummary}>{locationSummary}</strong>
          </div>
          {locationGrid.cellCount && locationCellNumbers.length ? (
            <div
              className="assistant-current-appointment-location-grid"
              aria-hidden="true"
              style={{ gridTemplateColumns: `repeat(${locationGrid.cols}, minmax(0, 1fr))` }}
            >
              {locationGrid.cells.map((cellNumber) => (
                <span
                  key={cellNumber}
                  className={`assistant-current-appointment-location-cell ${locationCellNumbers.includes(cellNumber) ? 'is-active' : ''}`}
                >
                  {getAppointmentLocationCellVisualLabel({ cellNumber, cols: locationGrid.cols })}
                </span>
              ))}
            </div>
          ) : null}
          {originalCodes.length ? (
            <div className="assistant-current-appointment-original-codes-list">
              {originalCodes.map((codeText, idx) => (
                <div key={idx} className="original-code-row">{codeText}</div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {((['pending', 'invalid', 'expired'].includes(String(appointment.status || '').trim().toLowerCase()) || appointment.is_invalid === true) || storageRemark) ? (
        <div className="assistant-current-appointment-details-grid">
          {((['pending', 'invalid', 'expired'].includes(String(appointment.status || '').trim().toLowerCase()) || appointment.is_invalid === true)) ? (
            <>
              <div className="detail-pill is-fullwidth">
                <div className="detail-pill-icon">
                  <UserIcon />
                </div>
                <span className="detail-pill-label">用户</span>
                <strong className="detail-pill-value">{recipientLabel}</strong>
              </div>
              <div className="detail-row-half">
                <div className="detail-pill">
                  <div className="detail-pill-icon">
                    <PhoneIcon />
                  </div>
                  <span className="detail-pill-label">尾号</span>
                  <strong className="detail-pill-value">{tailLabel}</strong>
                </div>
                <div className="detail-pill">
                  <div className="detail-pill-icon">
                    <BuildingIcon />
                  </div>
                  <span className="detail-pill-label">楼号</span>
                  <strong className="detail-pill-value">{buildingLabel}</strong>
                </div>
              </div>
            </>
          ) : null}
          {storageRemark ? (
            <div className="detail-pill is-fullwidth is-multiline">
              <div className="detail-pill-icon">
                <NoteIcon />
              </div>
              <span className="detail-pill-label">上架备注</span>
              <strong className="detail-pill-value">{storageRemark}</strong>
            </div>
          ) : null}
        </div>
      ) : null}

      {canConfirmPickedUp ? (
        <div className="assistant-current-appointment-actions">
          <button
            className={`btn assistant-current-appointment-action-btn ${confirmState ? 'is-confirming' : 'btn-secondary'}`}
            type="button"
            onClick={() => {
              if (confirmState) {
                onAction?.(appointment, 'confirm-user-picked-up')
                setConfirmState(false)
              } else {
                setConfirmState(true)
              }
            }}
            disabled={busy}
          >
            {busy ? '处理中...' : confirmState ? '确认已取件？' : '已取件'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function AssistantMessageBubble({ message, onChoice, cardContext }) {
  const cards = Array.isArray(message.cards) ? message.cards : []
  const hideBubbleText = cards.some((card) => ['site_entry_card'].includes(String(card?.type || '').trim()))
  return (
    <div className={`assistant-message ${message.role === 'user' ? 'user' : 'assistant'} ${message.pending ? 'pending' : ''}`}>
      <div className="assistant-message-bubble">
        {message.text && !hideBubbleText ? <div>{message.text}</div> : null}
        {cards.map((card, index) => (
          <AssistantActionCard
            key={`${message.id}-card-${index}`}
            card={card}
            onChoice={onChoice}
            context={cardContext}
          />
        ))}
        {message.error ? <div className="assistant-message-error">{message.error}</div> : null}
      </div>
    </div>
  )
}

function getInitialTheme() {
  const savedTheme = storage.readTheme()
  if (savedTheme === 'dark' || savedTheme === 'light') {
    return savedTheme
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light'
    }
  }
  return 'dark'
}

function getDeferredInstallPrompt() {
  if (typeof window === 'undefined') return null
  return window.__cabinetsInstallPrompt || null
}

function isStandaloneInstallContext() {
  if (typeof window === 'undefined') return false
  const standaloneMedia = window.matchMedia?.('(display-mode: standalone)').matches
  const fullscreenMedia = window.matchMedia?.('(display-mode: fullscreen)').matches
  const iosStandalone = window.navigator?.standalone === true
  return Boolean(standaloneMedia || fullscreenMedia || iosStandalone)
}

function getInstallHelpEnvironment() {
  if (typeof window === 'undefined') return {}
  return {
    userAgent: window.navigator?.userAgent || '',
    platform: window.navigator?.platform || '',
    maxTouchPoints: window.navigator?.maxTouchPoints || 0
  }
}

function createInitialLoginState() {
  const storedChallenge = storage.readLoginChallenge()
  if (!storedChallenge) {
    return { ...DEFAULT_LOGIN }
  }

  const countdown = getLoginCodeCountdownSeconds({
    resendAvailableAt: storedChallenge.resendAvailableAt
  })

  return {
    ...DEFAULT_LOGIN,
    phone: storedChallenge.phone,
    challengeId: storedChallenge.challengeId,
    resendAvailableAt: storedChallenge.resendAvailableAt,
    countdown,
    message: countdown > 0 ? '验证码已发送，请查看短信' : ''
  }
}

function createInitialAssistantMessages() {
  return [
    { id: 'assistant-hello', role: 'assistant', text: '有什么可以帮你的吗？', contentType: 'text', cards: [], actions: [], pending: false, error: '' }
  ]
}

const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '开发版'
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'

const renderPrivacyContent = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, idx) => {
    const trimmed = line.trim();
    if (trimmed === '---') {
      return <hr key={idx} className="settings-divider" style={{ margin: '16px 0' }} />;
    }
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      return (
        <p key={idx} style={{ margin: '0 0 10px', fontWeight: 'bold', color: 'var(--text)' }}>
          {trimmed.slice(2, -2)}
        </p>
      );
    }
    if (trimmed.startsWith('# ')) {
      return <h1 key={idx} style={{ fontSize: '18px', fontWeight: 'bold', margin: '20px 0 10px', color: 'var(--text)' }}>{trimmed.slice(2)}</h1>;
    }
    if (trimmed.startsWith('## ')) {
      return <h2 key={idx} style={{ fontSize: '16px', fontWeight: 'bold', margin: '18px 0 8px', color: 'var(--text)' }}>{trimmed.slice(3)}</h2>;
    }
    if (trimmed.startsWith('### ')) {
      return <h3 key={idx} style={{ fontSize: '14px', fontWeight: 'bold', margin: '16px 0 6px', color: 'var(--text)' }}>{trimmed.slice(4)}</h3>;
    }
    if (trimmed.includes('**')) {
      const parts = trimmed.split('**');
      return (
        <p key={idx} style={{ margin: '0 0 10px', lineHeight: '1.6', color: 'var(--text)' }}>
          {parts.map((part, pIdx) => pIdx % 2 === 1 ? <strong key={pIdx} style={{ fontWeight: 'bold' }}>{part}</strong> : part)}
        </p>
      );
    }
    return (
      <p key={idx} style={{ margin: '0 0 10px', lineHeight: '1.6', color: 'var(--text)' }}>
        {line}
      </p>
    );
  });
};

function App() {
  const [route, navigate] = useHashRoute()
  const initialToken = storage.readToken()
  const runtimeToken = !DEV_PREVIEW_MODE && initialToken === DEV_PREVIEW_TOKEN ? '' : initialToken
  const [session, setSession] = useState(() => (DEV_PREVIEW_MODE
    ? {
        token: DEV_PREVIEW_TOKEN,
        user: DEV_PREVIEW_USER
      }
    : {
        token: runtimeToken,
        user: null
      }))
  const [booting, setBooting] = useState(() => (!DEV_PREVIEW_MODE && Boolean(runtimeToken)))
  const [bootError, setBootError] = useState('')
  const [bootBannerDismissed, setBootBannerDismissed] = useState(false)
  const [authLoading, setAuthLoading] = useState(false)
  const [theme, setTheme] = useState(getInitialTheme)
  const [uiMode, setUiMode] = useState(() => storage.readUiMode())
  const [login, setLogin] = useState(() => createInitialLoginState())
  const [site, setSite] = useState(() => (DEV_PREVIEW_MODE ? createDevPreviewSiteState() : createInitialSiteState()))
  const [appointments, setAppointments] = useState(() => (DEV_PREVIEW_MODE ? createDevPreviewAppointmentState() : DEFAULT_APPOINTMENT_STATE))
  const [installPrompt, setInstallPrompt] = useState(getDeferredInstallPrompt)
  const [isInstalledApp, setIsInstalledApp] = useState(isStandaloneInstallContext)
  const isVirtual = Boolean(session.user?.virtual_identify_key)
  const isPendingVirtual = Boolean(session.user?.virtual_identify_key?.startsWith('PENDING-'))
  const [showInstallHelp, setShowInstallHelp] = useState(false)
  const [showStationDropdown, setShowStationDropdown] = useState(false)
  const [showSiteInfoModal, setShowSiteInfoModal] = useState(false)
  const [showHistoryPanel, setShowHistoryPanel] = useState(false)
  const [showPrivacyPanel, setShowPrivacyPanel] = useState(false)
  const [privacyContent, setPrivacyContent] = useState('')
  const [privacyLoading, setPrivacyLoading] = useState(false)
  const [privacyError, setPrivacyError] = useState('')
  const [showComposerMenu, setShowComposerMenu] = useState(false)
  const [showAccountDeleteConfirm, setShowAccountDeleteConfirm] = useState(false)
  const [showSiteIdPrompt, setShowSiteIdPrompt] = useState(false)
  const [showQrScanner, setShowQrScanner] = useState(false)
  const [siteIdPromptValue, setSiteIdPromptValue] = useState('')
  const [siteIdPromptHint, setSiteIdPromptHint] = useState('')
  const [siteIdPromptBusy, setSiteIdPromptBusy] = useState(false)
  const [siteIdPromptError, setSiteIdPromptError] = useState('')
  const [qrScannerBusy, setQrScannerBusy] = useState(false)
  const [qrScannerError, setQrScannerError] = useState('')
  const [siteSyncToast, setSiteSyncToast] = useState('')
  const [actionToast, setActionToast] = useState(null)
  const [profile, setProfile] = useState(() => (DEV_PREVIEW_MODE ? normalizeProfileData(DEV_PREVIEW_PROFILE, DEV_PREVIEW_PROFILE) : createInitialProfileState()))
  const [savedProfile, setSavedProfile] = useState(() => (DEV_PREVIEW_MODE ? normalizeProfileData(DEV_PREVIEW_PROFILE, DEV_PREVIEW_PROFILE) : createInitialProfileState()))
  const [profileSaving, setProfileSaving] = useState(false)
  const [accountDeleting, setAccountDeleting] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [assistantComposer, setAssistantComposer] = useState('')
  const [assistantInputMode, setAssistantInputMode] = useState('keyboard')
  const [assistantVoicePressing, setAssistantVoicePressing] = useState(false)
  const [assistantVoiceListening, setAssistantVoiceListening] = useState(false)
  const [assistantVoiceDraft, setAssistantVoiceDraft] = useState('')
  const [assistantVoiceCancelling, setAssistantVoiceCancelling] = useState(false)
  const [assistantSheet, setAssistantSheet] = useState('')
  const [assistantConversation, setAssistantConversation] = useState(null)
  const [assistantLoading, setAssistantLoading] = useState(false)
  const [assistantSending, setAssistantSending] = useState(false)
  const [assistantClearing, setAssistantClearing] = useState(false)
  const [assistantError, setAssistantError] = useState('')
  const [assistantConversationRetryTick, setAssistantConversationRetryTick] = useState(0)
  const [assistantHistoryHasMoreOlder, setAssistantHistoryHasMoreOlder] = useState(false)
  const [assistantHistoryOlderCursor, setAssistantHistoryOlderCursor] = useState('')
  const [assistantHistoryLoadingOlder, setAssistantHistoryLoadingOlder] = useState(false)
  const [assistantHistoryHydrated, setAssistantHistoryHydrated] = useState(false)
  const [assistantHistoryOlderError, setAssistantHistoryOlderError] = useState('')
  const [assistantConfirmDraft, setAssistantConfirmDraft] = useState(null)
  const [showAssistantCurrentAppointmentPanel, setShowAssistantCurrentAppointmentPanel] = useState(false)
  const [assistantCurrentAppointmentPanelStyle, setAssistantCurrentAppointmentPanelStyle] = useState(null)
  const [appointmentEventsConnected, setAppointmentEventsConnected] = useState(false)
  const [pushNotifications, setPushNotifications] = useState({
    supported: false,
    available: false,
    enabled: false,
    busy: false,
    error: ''
  })
  const [assistantMessages, setAssistantMessages] = useState(() => createInitialAssistantMessages())
  const assistantInputRef = useRef(null)
  const assistantTranscriptRef = useRef(null)
  const assistantCurrentAppointmentButtonRef = useRef(null)
  const assistantCurrentAppointmentPanelRef = useRef(null)
  const assistantRecognitionRef = useRef(null)
  const assistantVoiceTranscriptRef = useRef('')
  const assistantVoiceStartYRef = useRef(0)
  const assistantVoiceCancelRef = useRef(false)
  const assistantSubmitTextRef = useRef(null)
  const assistantAppointmentPollBusyRef = useRef(false)
  const assistantConversationRetryRef = useRef({ failedAttempts: 0, timer: 0 })
  const appointmentEventIdsRef = useRef(new Set())
  const appointmentStatusEventHandlerRef = useRef(null)
  const logoutHandlerRef = useRef(null)
  const appointmentEventsDisabledTokenRef = useRef('')
  const stationSyncStateRef = useRef({ siteId: '', syncedAt: 0 })
  const siteBundleSignatureRef = useRef({ siteId: '', signature: '' })
  const qrScannerVideoRef = useRef(null)
  const qrScannerStreamRef = useRef(null)
  const qrScannerFrameRef = useRef(0)
  const qrScannerLockRef = useRef(false)

  const scrollAssistantTranscriptToBottom = useCallback((behavior = 'smooth') => {
    const transcript = assistantTranscriptRef.current
    if (!transcript || typeof window === 'undefined') return
    window.requestAnimationFrame(() => {
      transcript.scrollTo({
        top: transcript.scrollHeight,
        behavior
      })
    })
  }, [])

  const preserveAssistantTranscriptScrollPosition = useCallback((previousScrollHeight, previousScrollTop) => {
    const transcript = assistantTranscriptRef.current
    if (!transcript || typeof window === 'undefined') return
    window.requestAnimationFrame(() => {
      const nextScrollHeight = transcript.scrollHeight
      transcript.scrollTop = Math.max(0, previousScrollTop + (nextScrollHeight - previousScrollHeight))
    })
  }, [])

  const resetAssistantHistoryPagination = useCallback(() => {
    setAssistantHistoryHasMoreOlder(false)
    setAssistantHistoryOlderCursor('')
    setAssistantHistoryLoadingOlder(false)
    setAssistantHistoryHydrated(false)
    setAssistantHistoryOlderError('')
  }, [])

  const selectedSite = useMemo(() => {
    const candidate = getSiteFromSelection(site.current, site.recentSites)
    return hasDisplaySiteLabel(candidate) ? candidate : null
  }, [site.current, site.recentSites])
  const currentSiteId = getValueId(selectedSite)
  const currentSiteName = getSiteLabel(selectedSite)
  const hasSiteContext = Boolean(currentSiteId)
  const stationOptions = useMemo(() => {
    const options = Array.isArray(site.recentSites) ? site.recentSites : []
    return options
      .filter((item) => hasDisplaySiteLabel(item))
      .map((item) => ({
        value: getValueId(item),
        label: getSiteLabel(item),
        accentColor: getSiteAccentColor(getSiteLabel(item)),
        accentSoftColor: `${getSiteAccentColor(getSiteLabel(item))}14`
      }))
  }, [site.recentSites])
  const currentStationIndex = useMemo(() => {
    if (!stationOptions.length || !currentSiteId) return -1
    return stationOptions.findIndex((item) => item.value === currentSiteId)
  }, [currentSiteId, stationOptions])
  const currentDateLabel = useMemo(() => {
    const now = new Date()
    const weekdayLabels = ['日', '一', '二', '三', '四', '五', '六']
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${month}月${day}日 周${weekdayLabels[now.getDay()]}`
  }, [])
  const themeActionLabel = theme === 'dark' ? '切换到亮色主题' : '切换到暗色主题'
  const userConfig = site.userConfig || {}
  const appointmentSettings = site.appointmentSettings || {}
  const showShareOption = userConfig.sharingEnabled === true
  const showRemarkField = appointmentSettings.allowMessage === true
  const profilePhone = String(session.user?.phone || '').trim()
  const profilePhoneTail = String(session.user?.phone_tail || profilePhone).replace(/\D/g, '').slice(-4) || '----'
  const authStatusPresentation = useMemo(() => getAuthStatusPresentation(session.user || {}), [session.user])
  const isWeakIdentifySession = String(session.user?.auth_strength || '').trim() === 'weak_identification'
  const profileAvatarLabel = getProfileInitials(profile.name || session.user?.name || '我')
  const profileSaveState = useMemo(() => getProfileSaveState({ profile, savedProfile }), [profile, savedProfile])
  const { normalizedProfile, canSaveProfile } = profileSaveState
  const assistantModeActive = uiMode === 'assistant'
  const currentAppointment = Array.isArray(appointments.current)
    ? (currentSiteId
        ? appointments.current.find((item) => {
            const apptSiteId = getValueId(item?.site_id) || getValueId(item?.site)
            return apptSiteId === currentSiteId
          })
        : appointments.current[0]) || null
    : null
  const blockingCurrentAppointment = getBlockingCurrentAppointment(appointments.current, currentSiteId)
  const hasBlockingCurrentAppointment = Boolean(blockingCurrentAppointment)
  const notificationAppointments = useMemo(() => getNotificationAppointments(appointments.current), [appointments.current])
  const currentAssistantAppointment = currentAppointment
  const assistantAppointmentLocked = assistantModeActive && hasBlockingCurrentAppointment
  const assistantHasText = Boolean(String(assistantComposer || '').trim())
  const currentAppointmentSummaryCard = useMemo(() => {
    if (!blockingCurrentAppointment) return null
    return {
      type: 'current_appointment_card',
      appointment: blockingCurrentAppointment,
      statusLabel: getAppointmentStatusLabel(blockingCurrentAppointment.status),
      siteLabel: getAppointmentSiteName(blockingCurrentAppointment) || '当前驿站',
      timeLabel: appointmentTimeLabel(blockingCurrentAppointment) || resolveAppointmentTimePreferenceLabel(blockingCurrentAppointment.time_preference) || '暂无',
      countLabel: getCurrentAppointmentCountLabel(blockingCurrentAppointment),
      recipientLabel: String(blockingCurrentAppointment.name || blockingCurrentAppointment.recipient || profile.name || '未设置').trim(),
      tailLabel: profilePhoneTail
    }
  }, [blockingCurrentAppointment, profile.name, profilePhoneTail])
  const assistantCurrentAppointmentCard = assistantAppointmentLocked ? currentAppointmentSummaryCard : null

  const openAssistantCurrentAppointmentPanel = useCallback(() => {
    if (!assistantCurrentAppointmentCard) return
    const trigger = assistantCurrentAppointmentButtonRef.current
    if (!trigger) return

    const triggerRect = trigger.getBoundingClientRect()
    const panelWidth = Math.min(360, window.innerWidth - 24)
    let left = triggerRect.left
    left = Math.min(Math.max(12, left), window.innerWidth - panelWidth - 12)

    setAssistantCurrentAppointmentPanelStyle({
      top: `${triggerRect.top}px`,
      left: `${left}px`,
      width: `${panelWidth}px`
    })
    setShowAssistantCurrentAppointmentPanel((prev) => !prev)
  }, [assistantCurrentAppointmentCard])

  const closeAssistantCurrentAppointmentPanel = useCallback(() => {
    setShowAssistantCurrentAppointmentPanel(false)
    setAssistantCurrentAppointmentPanelStyle(null)
  }, [])

  useEffect(() => {
    if (!assistantAppointmentLocked) return
    setAssistantConfirmDraft(null)
    setAssistantSheet('')
    setShowComposerMenu(false)
    setShowAssistantCurrentAppointmentPanel(false)
    setAssistantCurrentAppointmentPanelStyle(null)
  }, [assistantAppointmentLocked])

  useEffect(() => {
    if (!assistantConfirmDraft) return
    scrollAssistantTranscriptToBottom()
  }, [assistantConfirmDraft, scrollAssistantTranscriptToBottom])

  useEffect(() => {
    if (!showAssistantCurrentAppointmentPanel) return

    const handlePointerDown = (event) => {
      const target = event.target
      if (assistantCurrentAppointmentPanelRef.current?.contains(target)) return
      if (assistantCurrentAppointmentButtonRef.current?.contains(target)) return
      closeAssistantCurrentAppointmentPanel()
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeAssistantCurrentAppointmentPanel()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeAssistantCurrentAppointmentPanel, showAssistantCurrentAppointmentPanel])

  useEffect(() => {
    const root = document.documentElement
    root.dataset.theme = theme
    root.style.colorScheme = theme === 'dark' ? 'dark' : 'light'
    storage.writeTheme(theme)

    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) {
      meta.setAttribute('content', theme === 'dark' ? '#07111f' : '#f4efe6')
    }
  }, [theme])

  useEffect(() => {
    const hasProfileValue = String(profile.name || profile.building || '').trim()
    if (!hasProfileValue) {
      storage.writeProfile(null)
      return
    }
    storage.writeProfile(profile)
  }, [profile])

  useEffect(() => {
    storage.writeUiMode(uiMode)
  }, [uiMode])

  useEffect(() => {
    if (!assistantModeActive) return undefined

    const { body, documentElement: root } = document
    const previousBodyOverflow = body.style.overflow
    const previousRootOverflow = root.style.overflow
    const previousRootOverscroll = root.style.overscrollBehavior

    body.style.overflow = 'hidden'
    root.style.overflow = 'hidden'
    root.style.overscrollBehavior = 'none'

    return () => {
      body.style.overflow = previousBodyOverflow
      root.style.overflow = previousRootOverflow
      root.style.overscrollBehavior = previousRootOverscroll
    }
  }, [assistantModeActive])

  useEffect(() => {
    if (!assistantModeActive) return undefined
    scrollAssistantTranscriptToBottom('auto')
    const timer = window.setTimeout(() => {
      scrollAssistantTranscriptToBottom('auto')
    }, 80)
    return () => window.clearTimeout(timer)
  }, [assistantHistoryHydrated, assistantModeActive, scrollAssistantTranscriptToBottom])

  useEffect(() => {
    if (!siteSyncToast) return undefined
    const timer = window.setTimeout(() => {
      setSiteSyncToast('')
    }, 2200)
    return () => window.clearTimeout(timer)
  }, [siteSyncToast])

  useEffect(() => {
    if (!actionToast?.message) return undefined
    const timer = window.setTimeout(() => {
      setActionToast(null)
    }, 2200)
    return () => window.clearTimeout(timer)
  }, [actionToast])

  const ensurePushSubscription = useCallback(async ({ requestPermission = false } = {}) => {
    const supported = Boolean(
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
    if (!supported || !session.token) {
      setPushNotifications((prev) => ({ ...prev, supported, available: false, enabled: false }))
      return false
    }

    setPushNotifications((prev) => ({ ...prev, supported, busy: true, error: '' }))
    try {
      const keyRes = await api.notifications.getPushPublicKey()
      const publicKey = String(keyRes?.data?.publicKey || '').trim()
      const available = keyRes?.data?.enabled === true && Boolean(publicKey)
      if (!available) {
        setPushNotifications({ supported, available: false, enabled: false, busy: false, error: '后台通知未配置' })
        return false
      }

      let permission = Notification.permission
      if (permission === 'default' && requestPermission) {
        permission = await Notification.requestPermission()
      }
      if (permission !== 'granted') {
        setPushNotifications({ supported, available: true, enabled: false, busy: false, error: permission === 'denied' ? '浏览器已拒绝通知权限' : '' })
        return false
      }

      const registration = await navigator.serviceWorker.ready
      const existingSubscription = await registration.pushManager.getSubscription()
      const subscription = existingSubscription || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: api.notifications.urlBase64ToUint8Array(publicKey)
      })
      await api.notifications.savePushSubscription(subscription.toJSON())
      setPushNotifications({ supported, available: true, enabled: true, busy: false, error: '' })
      return true
    } catch (error) {
      setPushNotifications((prev) => ({ ...prev, supported, busy: false, enabled: false, error: toErrorMessage(error, '通知订阅失败') }))
      return false
    }
  }, [session.token])

  const disablePushSubscription = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return
    setPushNotifications((prev) => ({ ...prev, busy: true, error: '' }))
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await api.notifications.removePushSubscription(subscription.toJSON())
        await subscription.unsubscribe()
      }
      setPushNotifications((prev) => ({ ...prev, enabled: false, busy: false, error: '' }))
    } catch (error) {
      setPushNotifications((prev) => ({ ...prev, busy: false, error: toErrorMessage(error, '关闭通知失败') }))
    }
  }, [])

  const handlePushNotificationToggle = useCallback(() => {
    if (pushNotifications.enabled) {
      void disablePushSubscription()
      return
    }
    void ensurePushSubscription({ requestPermission: true })
  }, [disablePushSubscription, ensurePushSubscription, pushNotifications.enabled])

  useEffect(() => {
    const supported = Boolean(
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
    setPushNotifications((prev) => ({ ...prev, supported }))
    if (!session.token || !supported || Notification.permission !== 'granted') return
    void ensurePushSubscription({ requestPermission: false })
  }, [ensurePushSubscription, session.token])

  const handleThemeToggle = useCallback(() => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'))
  }, [])

  const handleUiModeToggle = useCallback(() => {
    setUiMode((current) => (current === 'assistant' ? 'appointment' : 'assistant'))
  }, [])

  const applyAssistantStatePatch = useCallback((statePatch = {}) => {
    if (!statePatch || typeof statePatch !== 'object') return

    if (Array.isArray(statePatch.history)) {
      setAppointments((prev) => ({
        ...prev,
        history: statePatch.history,
        historyPage: 1,
        historyHasMore: false
      }))
    }

    if (statePatch.currentAppointment !== undefined) {
      setAppointments((prev) => ({
        ...prev,
        current: statePatch.currentAppointment ? [statePatch.currentAppointment] : []
      }))
    }

    if (statePatch.appointmentForm && typeof statePatch.appointmentForm === 'object') {
      setAppointments((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          ...statePatch.appointmentForm
        }
      }))
    }

    if (Array.isArray(statePatch.recentSites)) {
      setSite((prev) => ({
        ...prev,
        recentSites: statePatch.recentSites
      }))
    }
  }, [])

  const loadAssistantConversation = useCallback(async () => {
    if (DEV_PREVIEW_MODE || !session.token) return false
    setAssistantLoading(true)
    setAssistantError('')
    setAssistantHistoryLoadingOlder(false)
    setAssistantHistoryOlderError('')
    setAssistantHistoryHydrated(false)
    try {
      const conversationRes = await api.assistant.createConversation({ domain: 'appointment' })
      const conversation = conversationRes?.data?.conversation || null
      const initialMessages = Array.isArray(conversationRes?.data?.messages) ? conversationRes.data.messages : []
      if (!conversation?._id) {
        throw new Error('未获取到小助手会话')
      }
      assistantConversationRetryRef.current.failedAttempts = 0
      setAssistantConversation(conversation)
      const messagesRes = await api.assistant.getMessages(conversation._id, { limit: ASSISTANT_INITIAL_MESSAGE_LIMIT })
      const messagePage = resolveAssistantMessagesPage(messagesRes?.data || {}, initialMessages)
      const remoteMessages = messagePage.messages
      const normalizedMessages = normalizeAssistantMessages(remoteMessages).map(withAssistantSiteEntryFallback)
      setAssistantHistoryHasMoreOlder(messagePage.hasMoreOlder)
      setAssistantHistoryOlderCursor(messagePage.nextBeforeCursor)
      setAssistantHistoryHydrated(true)
      if (normalizedMessages.length) {
        setAssistantMessages(normalizedMessages)
        scrollAssistantTranscriptToBottom('auto')
      }
      return true
    } catch (error) {
      setAssistantError(toErrorMessage(error, '小助手会话加载失败'))
      return false
    } finally {
      setAssistantLoading(false)
    }
  }, [scrollAssistantTranscriptToBottom, session.token])

  const handleAssistantClearHistory = useCallback(async () => {
    if (assistantClearing) return
    setAssistantClearing(true)
    setAssistantError('')
    setAssistantConfirmDraft(null)
    setShowAssistantCurrentAppointmentPanel(false)
    setShowComposerMenu(false)
    setAssistantSheet('')
    try {
      if (DEV_PREVIEW_MODE || !assistantConversation?._id) {
        setAssistantConversation(null)
        setAssistantMessages(createInitialAssistantMessages())
        resetAssistantHistoryPagination()
        return
      }

      await api.assistant.archiveConversation(assistantConversation._id)
      setAssistantMessages([])
      setAssistantConversation(null)
      resetAssistantHistoryPagination()
    } catch (error) {
      setAssistantError(toErrorMessage(error, '清除聊天记录失败'))
    } finally {
      setAssistantClearing(false)
    }
  }, [assistantClearing, assistantConversation, resetAssistantHistoryPagination])

  useEffect(() => {
    if (!session.token) {
      setAssistantConversation(null)
      resetAssistantHistoryPagination()
      const retryState = assistantConversationRetryRef.current
      window.clearTimeout(retryState.timer)
      retryState.timer = 0
      retryState.failedAttempts = 0
      return
    }
    if (!assistantModeActive) {
      const retryState = assistantConversationRetryRef.current
      window.clearTimeout(retryState.timer)
      retryState.timer = 0
      return
    }
    if (!assistantModeActive || assistantConversation || assistantLoading) return
    const retryState = assistantConversationRetryRef.current
    if (retryState.timer) return

    const runLoad = async () => {
      const loaded = await loadAssistantConversation()
      if (loaded) {
        window.clearTimeout(retryState.timer)
        retryState.timer = 0
        retryState.failedAttempts = 0
        return
      }

      const delay = getAssistantConversationRetryDelayMs({
        failedAttempts: retryState.failedAttempts
      })
      retryState.failedAttempts += 1
      retryState.timer = window.setTimeout(() => {
        retryState.timer = 0
        setAssistantConversationRetryTick((value) => value + 1)
      }, delay)
    }

    void runLoad()
  }, [assistantConversation, assistantConversationRetryTick, assistantLoading, assistantModeActive, loadAssistantConversation, resetAssistantHistoryPagination, session.token])

  const stopAssistantVoiceRecognition = useCallback(() => {
    const recognition = assistantRecognitionRef.current
    assistantRecognitionRef.current = null
    assistantVoiceCancelRef.current = false
    assistantVoiceStartYRef.current = 0
    if (!recognition) return
    try {
      recognition.onresult = null
      recognition.onerror = null
      recognition.onend = null
      recognition.stop()
    } catch (error) {
      void error
    }
  }, [])

  useEffect(() => () => {
    stopAssistantVoiceRecognition()
  }, [stopAssistantVoiceRecognition])

  const handleAssistantInputModeToggle = useCallback(() => {
    setAssistantInputMode((current) => {
      const nextMode = current === 'keyboard' ? 'voice' : 'keyboard'
      if (nextMode === 'keyboard') {
        stopAssistantVoiceRecognition()
        setAssistantVoicePressing(false)
        setAssistantVoiceListening(false)
        setAssistantVoiceDraft('')
        setAssistantVoiceCancelling(false)
      } else {
        setAssistantVoiceDraft('')
        setAssistantVoiceCancelling(false)
      }
      return nextMode
    })
  }, [stopAssistantVoiceRecognition])

  const handleAssistantVoiceRecognitionEnd = useCallback(() => {
    const transcript = String(assistantVoiceTranscriptRef.current || '').trim()
    const cancelled = assistantVoiceCancelRef.current
    assistantRecognitionRef.current = null
    assistantVoiceTranscriptRef.current = ''
    assistantVoiceCancelRef.current = false
    assistantVoiceStartYRef.current = 0
    setAssistantVoicePressing(false)
    setAssistantVoiceListening(false)
    setAssistantVoiceDraft('')
    setAssistantVoiceCancelling(false)
    if (!cancelled && transcript) {
      void assistantSubmitTextRef.current?.(transcript, { syncAppointmentMessage: true })
    }
  }, [])

  const handleAssistantVoiceRecognitionError = useCallback(() => {
    assistantVoiceTranscriptRef.current = ''
    assistantRecognitionRef.current = null
    assistantVoiceCancelRef.current = false
    assistantVoiceStartYRef.current = 0
    setAssistantVoicePressing(false)
    setAssistantVoiceListening(false)
    setAssistantVoiceCancelling(false)
    setAssistantVoiceDraft('按住说话')
  }, [])

  const startAssistantVoiceRecognition = useCallback(() => {
    if (assistantVoiceListening || assistantRecognitionRef.current) return
    if (typeof window === 'undefined') return
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognitionCtor) {
      setAssistantVoiceDraft('当前浏览器不支持语音输入')
      return
    }

    try {
      const recognition = new SpeechRecognitionCtor()
      recognition.lang = 'zh-CN'
      recognition.interimResults = true
      recognition.continuous = false
      recognition.maxAlternatives = 1
      assistantVoiceTranscriptRef.current = ''
      assistantVoiceCancelRef.current = false
      setAssistantVoiceListening(true)
      setAssistantVoiceCancelling(false)
      setAssistantVoiceDraft('')

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((result) => String(result[0]?.transcript || ''))
          .join('')
          .trim()
        assistantVoiceTranscriptRef.current = transcript
        setAssistantVoiceDraft(transcript || '松开发送')
      }
      recognition.onerror = handleAssistantVoiceRecognitionError
      recognition.onend = handleAssistantVoiceRecognitionEnd
      assistantRecognitionRef.current = recognition
      recognition.start()
    } catch (error) {
      handleAssistantVoiceRecognitionError(error)
    }
  }, [assistantVoiceListening, handleAssistantVoiceRecognitionEnd, handleAssistantVoiceRecognitionError])

  const stopAssistantVoiceCapture = useCallback(({ cancel = false } = {}) => {
    if (cancel) {
      assistantVoiceCancelRef.current = true
      assistantVoiceTranscriptRef.current = ''
      setAssistantVoiceDraft('')
      setAssistantVoiceCancelling(false)
    }
    if (!assistantVoiceListening && !assistantRecognitionRef.current) {
      if (cancel) {
        assistantVoiceCancelRef.current = false
        assistantVoiceStartYRef.current = 0
      }
      return
    }
    try {
      assistantRecognitionRef.current?.stop()
    } catch (error) {
      void error
      handleAssistantVoiceRecognitionEnd()
    }
  }, [assistantVoiceListening, handleAssistantVoiceRecognitionEnd])

  const handleAssistantVoicePressStart = useCallback((event) => {
    event.preventDefault()
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId)
    } catch (error) {
      void error
    }
    assistantVoiceStartYRef.current = Number(event.clientY) || 0
    assistantVoiceCancelRef.current = false
    setAssistantVoicePressing(true)
    setAssistantVoiceCancelling(false)
    startAssistantVoiceRecognition()
  }, [startAssistantVoiceRecognition])

  const handleAssistantVoicePressMove = useCallback((event) => {
    if (!assistantVoiceListening && !assistantRecognitionRef.current) return
    const cancelling = resolveAssistantVoiceGesture({
      startY: assistantVoiceStartYRef.current,
      currentY: event.clientY
    })
    assistantVoiceCancelRef.current = cancelling
    setAssistantVoiceCancelling(cancelling)
  }, [assistantVoiceListening])

  const handleAssistantVoicePressEnd = useCallback((event) => {
    event.preventDefault()
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    } catch (error) {
      void error
    }
    setAssistantVoicePressing(false)
    stopAssistantVoiceCapture({ cancel: assistantVoiceCancelRef.current })
  }, [stopAssistantVoiceCapture])

  const handleAssistantVoicePressCancel = useCallback((event) => {
    event.preventDefault()
    setAssistantVoicePressing(false)
    stopAssistantVoiceCapture({ cancel: true })
  }, [stopAssistantVoiceCapture])

  const openProfilePanel = useCallback(() => {
    navigate('settings')
  }, [navigate])

  const closeProfilePanel = useCallback(() => {
    setShowAccountDeleteConfirm(false)
    navigate('home')
  }, [navigate])

  const openHistoryPanel = useCallback(() => {
    setShowHistoryPanel(true)
  }, [])

  const closeHistoryPanel = useCallback(() => {
    setShowHistoryPanel(false)
    setAppointments((prev) => ({ ...prev, historyDetail: null, copiedCode: '' }))
  }, [])

  const openPrivacyPanel = useCallback(async () => {
    setShowPrivacyPanel(true)
    setPrivacyLoading(true)
    setPrivacyError('')
    try {
      const response = await api.agreements.getPrivacy()
      if (response && response.success && response.data && response.data.content) {
        setPrivacyContent(response.data.content)
      } else {
        throw new Error(response?.error || response?.message || '获取隐私政策失败')
      }
    } catch (error) {
      console.error('获取隐私政策失败:', error)
      setPrivacyError(error?.message || '隐私政策加载失败，请稍后重试')
    } finally {
      setPrivacyLoading(false)
    }
  }, [])

  const closePrivacyPanel = useCallback(() => {
    setShowPrivacyPanel(false)
  }, [])

  const openComposerMenu = useCallback(() => {
    setShowComposerMenu(true)
  }, [])

  const closeComposerMenu = useCallback(() => {
    setShowComposerMenu(false)
  }, [])

  const handleProfileFieldChange = useCallback((field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: normalizeRequiredAppointmentProfileField(field, value, prev[field])
    }))
  }, [])

  const handleProfilePanelSubmit = useCallback(async (event) => {
    event.preventDefault()
    if (!canSaveProfile) return
    setProfileSaving(true)
    setProfileError('')
    try {
      const response = await api.auth.updateProfile({
        name: normalizedProfile.name,
        appointmentBuilding: normalizedProfile.building
      })
      const updatedUser = response?.data?.user || null
      if (!updatedUser) {
        throw new Error('保存后未返回最新资料')
      }

      const nextProfile = resolveProfileFromBoot({
        authenticated: true,
        sessionUser: updatedUser
      })
      setSession((prev) => ({ ...prev, user: updatedUser }))
      setProfile(nextProfile)
      setSavedProfile(nextProfile)
      storage.writeProfile(nextProfile)
      navigate('home')
    } catch (error) {
      setProfileError(toErrorMessage(error, '个人信息保存失败'))
    } finally {
      setProfileSaving(false)
    }
  }, [canSaveProfile, navigate, normalizedProfile])

  const toggleStationDropdown = useCallback(() => {
    setShowStationDropdown((current) => !current)
  }, [])

  const scanQRCode = useCallback(() => {
    const hasCameraSupport = typeof navigator !== 'undefined'
      && Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    setShowHistoryPanel(false)
    setShowStationDropdown(false)
    setSiteIdPromptError('')
    setSiteIdPromptHint(hasCameraSupport
      ? '当前页面未接入相机扫码，请输入站点码或粘贴完整扫码内容。'
      : '当前设备不支持相机功能，请输入站点码或粘贴完整扫码内容。')
    setSiteIdPromptValue(String(selectedSite?.siteCode || site.siteCodeInput || '').trim())
    setShowSiteIdPrompt(true)
  }, [selectedSite, site.siteCodeInput])

  const closeQrScanner = useCallback(() => {
    setShowQrScanner(false)
    setQrScannerBusy(false)
    setQrScannerError('')
  }, [])

  const handleIdentifySiteScan = useCallback(() => {
    const hasCameraSupport = typeof navigator !== 'undefined'
      && Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    const hasBarcodeDetectorSupport = typeof window !== 'undefined' && 'BarcodeDetector' in window
    if (!hasCameraSupport || !hasBarcodeDetectorSupport) {
      setLogin((prev) => ({
        ...prev,
        error: hasCameraSupport
          ? '当前浏览器暂不支持站点扫码，请手动输入站点ID'
          : '当前设备不支持相机功能，请手动输入站点ID'
      }))
      return
    }
    setQrScannerError('')
    setShowQrScanner(true)
  }, [])

  const closeSiteIdPrompt = useCallback(() => {
    setShowSiteIdPrompt(false)
    setSiteIdPromptValue('')
    setSiteIdPromptHint('')
    setSiteIdPromptBusy(false)
    setSiteIdPromptError('')
  }, [])

  const loadCurrentAppointments = useCallback(async ({ force = false } = {}) => {
    if (DEV_PREVIEW_MODE) {
      setAppointments(createDevPreviewAppointmentState())
      return
    }

    setAppointments((prev) => ({ ...prev, currentLoading: true, error: '' }))
    try {
      const currentRes = await api.appointments.current({ force })
      setAppointments((prev) => ({
        ...prev,
        current: normalizeCurrentAppointments(currentRes),
        currentLoading: false
      }))
    } catch (error) {
      setAppointments((prev) => ({
        ...prev,
        currentLoading: false,
        error: toErrorMessage(error)
      }))
    }
  }, [])

  const loadAppointmentHistory = useCallback(async ({ force = false } = {}) => {
    if (DEV_PREVIEW_MODE) {
      setAppointments(createDevPreviewAppointmentState())
      return
    }

    const requestStartedAt = Date.now()

    const incrementalSyncEnabled = Boolean(
      appointments.historyLoadedAt &&
      appointments.historySyncCursor?.updatedAt &&
      appointments.historySyncCursor?.id
    )

    setAppointments((prev) => ({
      ...prev,
      historyLoading: true,
      historyLastAttemptAt: requestStartedAt,
      error: ''
    }))
    try {
      const historyRes = await api.appointments.my(
        incrementalSyncEnabled
          ? {
              includeStats: 0,
              syncSince: appointments.historySyncCursor.updatedAt,
              syncSinceId: appointments.historySyncCursor.id
            }
          : {
              page: 1,
              limit: 20,
              includeStats: 0
            }
      )
      const nextCursor = historyRes?.data?.syncCursor || null
      setAppointments((prev) => ({
        ...prev,
        history: incrementalSyncEnabled
          ? mergeIncrementalAppointmentHistory(
              prev.history,
              Array.isArray(historyRes?.data?.appointments) ? historyRes.data.appointments : []
            )
          : (Array.isArray(historyRes?.data?.appointments) ? historyRes.data.appointments : []),
        historyPage: incrementalSyncEnabled ? prev.historyPage : 1,
        historyHasMore: incrementalSyncEnabled
          ? prev.historyHasMore
          : Number(historyRes?.data?.pagination?.pages || 1) > 1,
        historyLoadedAt: Date.now(),
        historyLastAttemptAt: requestStartedAt,
        historyNeedsRefresh: false,
        historySyncCursor: nextCursor || prev.historySyncCursor || null,
        historyLoading: false
      }))
    } catch (error) {
      setAppointments((prev) => ({
        ...prev,
        historyLastAttemptAt: requestStartedAt,
        historyLoading: false,
        error: toErrorMessage(error)
      }))
    }
  }, [appointments.historyLoadedAt, appointments.historySyncCursor])

  useEffect(() => {
    if (!showHistoryPanel || DEV_PREVIEW_MODE || appointments.historyLoading) return
    const historyRefreshAnchorAt = Math.max(
      Number(appointments.historyLoadedAt) || 0,
      Number(appointments.historyLastAttemptAt) || 0
    )
    if (!shouldRefreshAppointmentHistory({
      historyLoadedAt: historyRefreshAnchorAt,
      historyNeedsRefresh: appointments.historyNeedsRefresh
    })) return
    void loadAppointmentHistory()
  }, [
    appointments.historyLastAttemptAt,
    appointments.historyLoadedAt,
    appointments.historyLoading,
    appointments.historyNeedsRefresh,
    loadAppointmentHistory,
    showHistoryPanel
  ])

  const loadSiteBundle = useCallback(async (siteId, options = {}) => {
    const { force = false } = options
    if (DEV_PREVIEW_MODE && !force) {
      return
    }

    const normalizedSiteId = String(siteId || '').trim()
    if (!normalizedSiteId) {
      siteBundleSignatureRef.current = { siteId: '', signature: '' }
      setSite((prev) => ({
        ...prev,
        announcement: null,
        slots: [],
        userConfig: null,
        appointmentSettings: null,
        slotsLoading: false
      }))
      return
    }

    if (!force && shouldThrottleStationSync({
      siteId: normalizedSiteId,
      lastSyncedSiteId: stationSyncStateRef.current.siteId,
      lastSyncedAt: stationSyncStateRef.current.syncedAt
    })) {
      return false
    }

    stationSyncStateRef.current = {
      siteId: normalizedSiteId,
      syncedAt: Date.now()
    }

    setSite((prev) => ({ ...prev, slotsLoading: true, loading: true, error: '' }))
    const [announcementRes, slotsRes, configRes] = await Promise.allSettled([
      api.sites.latestAnnouncement(normalizedSiteId),
      api.appointments.availableSlots(normalizedSiteId),
      api.sites.userConfig(normalizedSiteId)
    ])

    const slotSettings = slotsRes.status === 'fulfilled' ? slotsRes.value?.data?.settings || null : null
    const userConfig = configRes.status === 'fulfilled'
      ? configRes.value?.data || null
      : null

    const nextBundle = {
      announcement: announcementRes.status === 'fulfilled'
        ? announcementRes.value?.data?.announcement || null
        : null,
      slots: slotsRes.status === 'fulfilled' && Array.isArray(slotsRes.value?.data?.slots)
        ? slotsRes.value.data.slots.map((slot) => ({
            value: String(slot?.value || '').trim(),
            label: String(slot?.label || '').trim(),
            available: slot?.available !== false
          }))
        : [],
      userConfig,
      appointmentSettings: slotSettings
    }
    const nextSignature = buildSiteBundleSignature(nextBundle)
    const previousSignature = siteBundleSignatureRef.current
    const allRequestsSucceeded = announcementRes.status === 'fulfilled'
      && slotsRes.status === 'fulfilled'
      && configRes.status === 'fulfilled'
    if (
      allRequestsSucceeded
      && previousSignature.siteId === normalizedSiteId
      && previousSignature.signature
      && previousSignature.signature !== nextSignature
    ) {
      setSiteSyncToast('驿站数据已更新')
    }
    siteBundleSignatureRef.current = {
      siteId: normalizedSiteId,
      signature: nextSignature
    }

    setSite((prev) => ({
      ...prev,
      announcement: nextBundle.announcement,
      slots: slotsRes.status === 'fulfilled' && Array.isArray(slotsRes.value?.data?.slots)
        ? slotsRes.value.data.slots
        : [],
      userConfig: userConfig || prev.userConfig,
      appointmentSettings: slotSettings || prev.appointmentSettings,
      slotsLoading: false,
      loading: false
    }))
  }, [])

  const commitSiteSelection = useCallback(async (nextSite, nextRecentSites = [], options = {}) => {
    const normalizedCurrent = normalizeSiteSummary(nextSite)
    if (!normalizedCurrent) {
      return null
    }

    const normalizedRecent = mergeRecentSiteCache(normalizedCurrent, nextRecentSites)
    let resolvedPreferences = {
      currentSite: normalizedCurrent,
      recentSites: normalizedRecent
    }

    const shouldSyncRemote = options.syncRemote !== false && !DEV_PREVIEW_MODE
    if (shouldSyncRemote) {
      try {
        if (session.user?.virtual_identify_key?.startsWith('PENDING-')) {
          const response = await api.auth.identifyBindSite(normalizedCurrent.id)
          const token = response?.data?.token
          const user = response?.data?.user
          if (token && user) {
            storage.writeToken(token)
            setSession({ token, user })
          }
          const serverPreferences = normalizeServerSitePreferences(user || {})
          resolvedPreferences = {
            currentSite: serverPreferences.currentSite || normalizedCurrent,
            recentSites: serverPreferences.recentSites.length ? serverPreferences.recentSites : normalizedRecent
          }
        } else {
          const response = await api.users.setCurrentSite(normalizedCurrent.id)
          const serverPreferences = normalizeServerSitePreferences(response?.data || {})
          resolvedPreferences = {
            currentSite: serverPreferences.currentSite || normalizedCurrent,
            recentSites: serverPreferences.recentSites.length ? serverPreferences.recentSites : normalizedRecent
          }
        }
      } catch (persistError) {
        // Keep the locally cached station even if preference sync fails.
        void persistError
      }
    }

    storage.writeCurrentSite(resolvedPreferences.currentSite)
    storage.writeRecentSites(resolvedPreferences.recentSites)
    const sessionUserId = getValueId(session.user)
    if (sessionUserId) {
      storage.writeCurrentSiteForUser(sessionUserId, resolvedPreferences.currentSite)
      storage.writeRecentSitesForUser(sessionUserId, resolvedPreferences.recentSites)
    }

    setSite((prev) => ({
      ...prev,
      current: resolvedPreferences.currentSite,
      recentSites: resolvedPreferences.recentSites,
      siteCodeInput: '',
      busy: false,
      error: ''
    }))

    return {
      current: resolvedPreferences.currentSite,
      recentSites: resolvedPreferences.recentSites
    }
  }, [session.user, setSession])

  const syncStationSelection = useCallback(async (nextSite, nextRecentSites = [], options = {}) => {
    const normalizedSite = normalizeSiteSummary(nextSite)
    if (!normalizedSite) {
      return null
    }

    const normalizedSiteId = getValueId(normalizedSite)
    const shouldSkip = options.force !== true && shouldThrottleStationSync({
      siteId: normalizedSiteId,
      lastSyncedSiteId: stationSyncStateRef.current.siteId,
      lastSyncedAt: stationSyncStateRef.current.syncedAt
    })

    if (shouldSkip) {
      return {
        current: normalizedSite,
        recentSites: mergeRecentSiteCache(normalizedSite, nextRecentSites),
        skipped: true
      }
    }

    stationSyncStateRef.current = {
      siteId: normalizedSiteId,
      syncedAt: Date.now()
    }

    setSite((prev) => ({ ...prev, busy: true, error: '' }))
    try {
      const nextRecent = mergeRecentSiteCache(normalizedSite, nextRecentSites)
      const committed = await commitSiteSelection(normalizedSite, nextRecent, options)
      if (!committed) {
        throw new Error('站点信息保存失败')
      }
      await loadSiteBundle(getValueId(committed.current), { ...options, force: true })
      await loadCurrentAppointments({ force: true })
      return {
        ...committed,
        skipped: false
      }
    } catch (error) {
      setSite((prev) => ({
        ...prev,
        busy: false,
        error: toErrorMessage(error)
      }))
      return null
    }
  }, [commitSiteSelection, loadCurrentAppointments, loadSiteBundle, site.recentSites])

  const resolveStationByInput = useCallback(async (inputValue) => {
    let normalizedInput = String(inputValue || '').trim()
    
    // Systematically handle URL inputs (e.g. from pasted or scanned links)
    if (/^https?:\/\//i.test(normalizedInput)) {
      try {
        const url = new URL(normalizedInput)
        const siteParam = url.searchParams.get('site') || 
                          url.searchParams.get('siteId') || 
                          url.searchParams.get('site_id') || 
                          url.searchParams.get('code')
        if (siteParam) {
          normalizedInput = siteParam.trim()
        } else {
          const segments = url.pathname.split('/').filter(Boolean)
          if (segments.length > 0) {
            normalizedInput = segments[segments.length - 1].trim()
          }
        }
      } catch {
        // Fall back to the raw input if URL parsing fails
      }
    }

    if (!normalizedInput) {
      throw new Error('请输入站点码')
    }

    const tryResolveById = async () => {
      const response = await api.sites.getById(normalizedInput)
      return response?.data?.site || response?.data || null
    }

    const tryResolveByQrPayload = async () => {
      const response = await api.sites.scanSiteQRCode(normalizedInput)
      return response?.data || null
    }

    const tryResolveByCode = async () => {
      const response = await api.sites.getByCode(normalizedInput)
      return response?.data?.site || response?.data || null
    }

    const attempts = []
    if (looksLikeSiteQrPayload(normalizedInput)) {
      attempts.push(tryResolveByQrPayload)
    } else if (isObjectIdLike(normalizedInput)) {
      attempts.push(tryResolveById, tryResolveByCode)
    } else {
      attempts.push(tryResolveByCode)
    }

    let lastError = null
    for (const attempt of attempts) {
      try {
        const payload = await attempt()
        const normalizedSite = normalizeSiteSummary(payload)
        if (normalizedSite && normalizedSite.id) {
          return normalizedSite
        }
      } catch (error) {
        lastError = error
      }
    }

    if (lastError) {
      throw lastError
    }

    throw new Error('未找到驿站')
  }, [])

  const handleSiteIdPromptSubmit = useCallback(async (event) => {
    event.preventDefault()
    const siteId = String(siteIdPromptValue || '').trim()
    if (!siteId) {
      setSiteIdPromptError('请输入站点码')
      return
    }

    setSiteIdPromptBusy(true)
    setSiteIdPromptError('')

    try {
      let nextSite = await resolveStationByInput(siteId)
      if (!nextSite && DEV_PREVIEW_MODE && String(siteId).trim() === String(DEV_PREVIEW_SITE.siteCode).trim()) {
        nextSite = normalizeSiteSummary(DEV_PREVIEW_SITE)
      }
      if (!nextSite) {
        throw new Error('未找到驿站')
      }
      const nextRecent = mergeRecentSiteCache(nextSite, site.recentSites)
      await commitSiteSelection(nextSite, nextRecent)
      closeSiteIdPrompt()
      await loadSiteBundle(getValueId(nextSite), { force: DEV_PREVIEW_MODE ? false : true })
      await loadCurrentAppointments({ force: true })
    } catch (error) {
      setSiteIdPromptError(toErrorMessage(error))
    } finally {
      setSiteIdPromptBusy(false)
    }
  }, [closeSiteIdPrompt, commitSiteSelection, loadCurrentAppointments, loadSiteBundle, resolveStationByInput, site.recentSites, siteIdPromptValue])

  useEffect(() => {
    if (!showQrScanner) return undefined

    const hasCameraSupport = typeof navigator !== 'undefined'
      && Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    const hasBarcodeDetectorSupport = typeof window !== 'undefined' && 'BarcodeDetector' in window

    if (!hasCameraSupport || !hasBarcodeDetectorSupport) {
      setQrScannerBusy(false)
      setQrScannerError(hasCameraSupport ? '当前浏览器暂不支持站点扫码' : '当前设备不支持相机功能')
      return undefined
    }

    let cancelled = false
    qrScannerLockRef.current = false

    const stopScanner = () => {
      if (typeof window !== 'undefined' && qrScannerFrameRef.current) {
        window.cancelAnimationFrame(qrScannerFrameRef.current)
      }
      qrScannerFrameRef.current = 0
      const currentStream = qrScannerStreamRef.current
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop())
      }
      qrScannerStreamRef.current = null
      const video = qrScannerVideoRef.current
      if (video) {
        video.pause?.()
        video.srcObject = null
      }
    }

    const startScanner = async () => {
      setQrScannerBusy(true)
      setQrScannerError('')
      try {
        const BarcodeDetectorCtor = window.BarcodeDetector
        const detector = new BarcodeDetectorCtor({ formats: ['qr_code'] })
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' }
          }
        })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        qrScannerStreamRef.current = stream
        const video = qrScannerVideoRef.current
        if (!video) {
          throw new Error('未找到扫码预览')
        }
        video.srcObject = stream
        video.setAttribute('playsinline', 'true')
        await video.play()
        setQrScannerBusy(false)

        const detectFrame = async () => {
          if (cancelled) return
          const currentVideo = qrScannerVideoRef.current
          if (currentVideo && currentVideo.readyState >= 2 && !qrScannerLockRef.current) {
            try {
              const codes = await detector.detect(currentVideo)
              const detected = Array.isArray(codes)
                ? codes.find((item) => String(item?.rawValue || '').trim())
                : null
              const rawValue = String(detected?.rawValue || '').trim()
              if (rawValue) {
                qrScannerLockRef.current = true
                let nextValue = rawValue
                try {
                  const resolvedSite = await resolveStationByInput(rawValue)
                  nextValue = resolveScannedStationInputValue(resolvedSite, rawValue)
                } catch {
                  nextValue = rawValue
                }
                setLogin((prev) => ({
                  ...prev,
                  identifySiteInput: nextValue,
                  error: ''
                }))
                setShowQrScanner(false)
                return
              }
            } catch {
              // Ignore per-frame detector errors and keep scanning.
            }
          }
          qrScannerFrameRef.current = window.requestAnimationFrame(() => {
            void detectFrame()
          })
        }

        qrScannerFrameRef.current = window.requestAnimationFrame(() => {
          void detectFrame()
        })
      } catch (error) {
        setQrScannerBusy(false)
        setQrScannerError(toErrorMessage(error, '摄像头启动失败，请改为手动输入站点ID'))
      }
    }

    void startScanner()

    return () => {
      cancelled = true
      qrScannerLockRef.current = false
      stopScanner()
    }
  }, [resolveStationByInput, showQrScanner])

  const bootstrap = useCallback(async () => {
    if (DEV_PREVIEW_MODE) {
      setBootError('')
      setBooting(false)
      setSession({
        token: DEV_PREVIEW_TOKEN,
        user: DEV_PREVIEW_USER
      })
      setSite(createDevPreviewSiteState())
      setAppointments(createDevPreviewAppointmentState())
      return
    }

    const token = storage.readToken()
    const hasLocalToken = Boolean(token)
    const restoreAnonymousLocalState = () => {
      if (storage.readCurrentSite() || storage.readRecentSites().length || storage.readProfile()) {
        const { current: sanitizedCurrentSite, recentSites: sanitizedRecentSites } = readSanitizedSiteCache()
        const sanitizedProfile = readSanitizedProfile()
        if (sanitizedCurrentSite || sanitizedRecentSites.length) {
          storage.writeCurrentSite(sanitizedCurrentSite)
          storage.writeRecentSites(sanitizedRecentSites)
        } else {
          storage.clearSiteCache()
        }
        storage.writeProfile(sanitizedProfile)
        setSite((prev) => ({
          ...prev,
          current: sanitizedCurrentSite,
          recentSites: sanitizedRecentSites
        }))
        const nextProfile = normalizeProfileData(sanitizedProfile || {}, {})
        setProfile(nextProfile)
        setSavedProfile(nextProfile)
      }
    }

    if (token === DEV_PREVIEW_TOKEN) {
      storage.clearSession()
      storage.clearSiteCache()
      storage.writeProfile(null)
      setSession({ token: '', user: null })
      setSite(createInitialSiteState())
      setAppointments(DEFAULT_APPOINTMENT_STATE)
      {
        const nextProfile = createInitialProfileState()
        setProfile(nextProfile)
        setSavedProfile(nextProfile)
      }
      setBootError('')
      setBooting(false)
      return
    }

    setBooting(true)
    setBootError('')

    let urlSiteCode = ''
    try {
      const pathParts = window.location.pathname.split('/')
      const siteIndex = pathParts.indexOf('site')
      if (siteIndex !== -1 && pathParts[siteIndex + 1]) {
        urlSiteCode = pathParts[siteIndex + 1]
      }

      if (!urlSiteCode) {
        const urlParams = new URLSearchParams(window.location.search)
        urlSiteCode = urlParams.get('siteCode') || ''
      }

      if (!urlSiteCode && window.location.hash.includes('siteCode')) {
        const hashQuery = window.location.hash.split('?')[1]
        if (hashQuery) {
          const hashParams = new URLSearchParams(hashQuery)
          urlSiteCode = hashParams.get('siteCode') || ''
        }
      }
      urlSiteCode = String(urlSiteCode || '').trim()
    } catch (e) {
      void e
    }

    let scannedSite = null
    if (urlSiteCode) {
      try {
        const response = await api.sites.getByCode(urlSiteCode)
        const rawSite = response?.data?.site || response?.data || null
        scannedSite = normalizeSiteSummary(rawSite)
        if (scannedSite) {
          storage.writeCurrentSite(scannedSite)
          const currentRecent = storage.readRecentSites()
          storage.writeRecentSites(mergeRecentSiteCache(scannedSite, currentRecent))
          localStorage.setItem('pending_scanned_site', JSON.stringify(scannedSite))
          
          setLogin((prev) => ({
            ...prev,
            identifySiteInput: scannedSite.siteCode || scannedSite.id || ''
          }))
        } else {
          setBootError('扫码驿站不存在或已停用')
        }
      } catch (err) {
        console.error('Failed to load site from url siteCode:', err)
        setBootError(toErrorMessage(err, '扫码驿站加载失败，可能已被停用'))
      } finally {
        try {
          const cleanUrl = new URL(window.location.href)
          let urlChanged = false

          if (cleanUrl.searchParams.has('siteCode')) {
            cleanUrl.searchParams.delete('siteCode')
            urlChanged = true
          }

          if (cleanUrl.hash && cleanUrl.hash.includes('siteCode')) {
            const parts = cleanUrl.hash.split('?')
            const hashPath = parts[0]
            const hashQuery = parts[1] || ''
            const hashParams = new URLSearchParams(hashQuery)
            hashParams.delete('siteCode')
            const newHashQuery = hashParams.toString()
            cleanUrl.hash = newHashQuery ? `${hashPath}?${newHashQuery}` : hashPath
            urlChanged = true
          }

          if (cleanUrl.pathname.includes('/site/')) {
            cleanUrl.pathname = '/'
            urlChanged = true
          }

          if (urlChanged) {
            window.history.replaceState({}, document.title, cleanUrl.toString())
          }
        } catch (urlErr) {
          void urlErr
        }
      }
    }

    try {
      const meRes = await api.auth.me()
      let currentUser = meRes?.data?.user || null

      let effectiveScannedSite = scannedSite
      if (!effectiveScannedSite && currentUser) {
        const pendingJson = localStorage.getItem('pending_scanned_site')
        if (pendingJson) {
          try {
            effectiveScannedSite = JSON.parse(pendingJson)
          } catch (e) {
            void e
          }
        }
      }

      const currentUserId = getValueId(currentUser)
      const { current: localCurrentSite, recentSites: localRecentSites } = readSanitizedSiteCacheForUser(currentUserId)
      setSession({ token: token || COOKIE_SESSION_TOKEN, user: currentUser })
      const prefRes = await api.users.syncCurrentSitePreferences()
      const { currentSite: remoteCurrentSite, recentSites: remoteRecentSites } = normalizeServerSitePreferences(prefRes?.data || {})
      let resolvedCurrentSite = effectiveScannedSite || remoteCurrentSite
      let resolvedRecentSites = remoteRecentSites.length ? remoteRecentSites : localRecentSites

      if (effectiveScannedSite) {
        resolvedRecentSites = mergeRecentSiteCache(effectiveScannedSite, resolvedRecentSites)
        try {
          if (currentUser?.virtual_identify_key?.startsWith('PENDING-')) {
            const response = await api.auth.identifyBindSite(effectiveScannedSite.id)
            const nextToken = response?.data?.token
            const nextUser = response?.data?.user
            if (nextToken && nextUser) {
              storage.writeToken(nextToken)
              currentUser = nextUser
              setSession({ token: nextToken, user: nextUser })
            }
          } else {
            await api.users.setCurrentSite(effectiveScannedSite.id)
          }
          localStorage.removeItem('pending_scanned_site')
        } catch (syncError) {
          void syncError
        }
      }

      if (!resolvedCurrentSite && (localCurrentSite || localRecentSites.length)) {
        try {
          const restoreSource = localCurrentSite || localRecentSites[0]
          if (restoreSource) {
            const restoreRes = await api.users.setCurrentSite(getValueId(restoreSource))
            const restoredPreferences = normalizeServerSitePreferences(restoreRes?.data || {})
            resolvedCurrentSite = restoredPreferences.currentSite || localCurrentSite || normalizeSiteSummary(restoreSource)
            resolvedRecentSites = restoredPreferences.recentSites.length ? restoredPreferences.recentSites : resolvedRecentSites
          }
        } catch (restoreError) {
          void restoreError
          resolvedCurrentSite = resolvedCurrentSite || localCurrentSite || null
        }
      }

      const fallbackSite = resolvedCurrentSite || resolvedRecentSites[0] || null
      const fallbackRecentSites = resolvedRecentSites

      const nextProfile = resolveProfileFromBoot({
        authenticated: true,
        sessionUser: currentUser
      })
      setProfile(nextProfile)
      setSavedProfile(nextProfile)
      setSite((prev) => ({
        ...prev,
        current: fallbackSite,
        recentSites: fallbackRecentSites,
        siteCodeInput: '',
        loading: false,
        error: ''
      }))
      if (fallbackSite) {
        storage.writeCurrentSite(fallbackSite)
        storage.writeRecentSites(fallbackRecentSites)
        if (currentUserId) {
          storage.writeCurrentSiteForUser(currentUserId, fallbackSite)
          storage.writeRecentSitesForUser(currentUserId, fallbackRecentSites)
        }
      }
      setAppointments((prev) => ({
        ...prev,
        form: resolveDefaultAppointmentForm(currentUser)
      }))
      await Promise.all([
        loadCurrentAppointments({ force: true }),
        loadSiteBundle(getValueId(fallbackSite))
      ])
    } catch (error) {
      if (shouldClearSessionForBootError(error)) {
        if (hasLocalToken) {
          storage.clearSession()
          storage.clearSiteCache()
          storage.writeProfile(null)
          setBootError(toErrorMessage(error, '登录已失效，请重新登录'))
        } else {
          restoreAnonymousLocalState()
          setBootError('')
        }
        setSession({ token: '', user: null })
      } else {
        if (!hasLocalToken) {
          restoreAnonymousLocalState()
          setSession({ token: '', user: null })
        }
        setBootError(toErrorMessage(error, hasLocalToken
          ? '登录状态已保留，部分数据加载失败，请稍后刷新重试'
          : '登录状态检查失败，请稍后刷新重试'))
      }
      setBootBannerDismissed(false)
    } finally {
      setBooting(false)
    }
  }, [loadCurrentAppointments, loadSiteBundle])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  useEffect(() => {
    const refreshDisplayMode = () => {
      setIsInstalledApp(isStandaloneInstallContext())
    }
    const refreshInstallPrompt = () => {
      setInstallPrompt(getDeferredInstallPrompt())
    }
    const handleAppInstalled = () => {
      setInstallPrompt(null)
      setIsInstalledApp(true)
      setShowInstallHelp(false)
    }

    refreshDisplayMode()
    refreshInstallPrompt()

    const standaloneQuery = window.matchMedia?.('(display-mode: standalone)')
    const fullscreenQuery = window.matchMedia?.('(display-mode: fullscreen)')
    standaloneQuery?.addEventListener?.('change', refreshDisplayMode)
    fullscreenQuery?.addEventListener?.('change', refreshDisplayMode)
    window.addEventListener('cabinets-install-prompt-ready', refreshInstallPrompt)
    window.addEventListener('cabinets-app-installed', handleAppInstalled)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      standaloneQuery?.removeEventListener?.('change', refreshDisplayMode)
      fullscreenQuery?.removeEventListener?.('change', refreshDisplayMode)
      window.removeEventListener('cabinets-install-prompt-ready', refreshInstallPrompt)
      window.removeEventListener('cabinets-app-installed', handleAppInstalled)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  useEffect(() => {
    if (session.token && selectedSite) {
      loadSiteBundle(currentSiteId)
    }
  }, [currentSiteId, loadSiteBundle, selectedSite, session.token])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type !== 'appointment-notification-click') return
      void loadCurrentAppointments({ force: true })
      setAppointments((prev) => ({ ...prev, historyNeedsRefresh: true }))
      navigate('home')
    }
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
    }
  }, [loadCurrentAppointments, navigate])

  useEffect(() => {
    if (!session.user || storage.readProfile()) return
    const nextProfile = normalizeProfileData(createProfileFromUser(session.user), createProfileFromUser(session.user))
    setProfile(nextProfile)
    setSavedProfile(nextProfile)
  }, [session.user])

  const syncLoginCountdown = useCallback(() => {
    setLogin((prev) => {
      const nextCountdown = getLoginCodeCountdownSeconds({
        resendAvailableAt: prev.resendAvailableAt
      })
      if (nextCountdown === prev.countdown) {
        return prev
      }
      return {
        ...prev,
        countdown: nextCountdown
      }
    })
  }, [])

  useEffect(() => {
    syncLoginCountdown()
    document.addEventListener('visibilitychange', syncLoginCountdown)
    window.addEventListener('pageshow', syncLoginCountdown)

    if (getLoginCodeCountdownSeconds({ resendAvailableAt: login.resendAvailableAt }) <= 0) {
      return () => {
        document.removeEventListener('visibilitychange', syncLoginCountdown)
        window.removeEventListener('pageshow', syncLoginCountdown)
      }
    }

    const timer = window.setInterval(syncLoginCountdown, 1000)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', syncLoginCountdown)
      window.removeEventListener('pageshow', syncLoginCountdown)
    }
  }, [login.resendAvailableAt, syncLoginCountdown])

  const handleLoginSendCode = useCallback(async (event) => {
    event.preventDefault()
    const phone = normalizePhone(login.phone)
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setLogin((prev) => ({ ...prev, error: '请输入有效的手机号码' }))
      return
    }

    setLogin((prev) => ({ ...prev, sending: true, error: '', message: '' }))
    try {
      const response = await api.auth.sendLoginCode(phone)
      const challengeId = response?.data?.challenge_id || response?.data?.challengeId || response?.data?.ticket || ''
      const resendAvailableAt = Date.now() + LOGIN_CODE_RESEND_SECONDS * 1000
      storage.writeLoginChallenge({
        phone,
        challengeId,
        resendAvailableAt
      })
      setLogin((prev) => ({
        ...prev,
        phone,
        challengeId,
        sending: false,
        countdown: LOGIN_CODE_RESEND_SECONDS,
        resendAvailableAt,
        message: '验证码已发送，请查看短信'
      }))
    } catch (error) {
      setLogin((prev) => ({
        ...prev,
        sending: false,
        error: toErrorMessage(error)
      }))
    }
  }, [login.phone])

  const handleLoginVerify = useCallback(async (event) => {
    event.preventDefault()
    const phone = normalizePhone(login.phone)
    const code = String(login.code || '').trim()
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setLogin((prev) => ({ ...prev, error: '请输入有效的手机号码' }))
      return
    }
    if (!/^\d{6}$/.test(code)) {
      setLogin((prev) => ({ ...prev, error: '请输入6位验证码' }))
      return
    }
    if (!login.challengeId) {
      setLogin((prev) => ({ ...prev, error: '请先发送验证码' }))
      return
    }

    setAuthLoading(true)
    setLogin((prev) => ({ ...prev, verifying: true, error: '' }))
    try {
      const verifyRes = await api.auth.verifyLoginCode({
        phone,
        code,
        challengeId: login.challengeId
      })
      const proofToken = verifyRes?.data?.proof_token || verifyRes?.data?.proofToken || ''
      if (!proofToken) {
        throw new Error('未获取到验证凭证')
      }
      const consumeRes = await api.auth.consumeVerificationProof(proofToken)
      const token = consumeRes?.data?.token || ''
      const user = consumeRes?.data?.user || null

      if (token) {
        storage.writeToken(token)
      }

      storage.clearLoginChallenge()
      setSession({ token: token || COOKIE_SESSION_TOKEN, user: user || null })
      setBootError('')
      setLogin(createInitialLoginState())
      navigate('home')
      await bootstrap()
    } catch (error) {
      setLogin((prev) => ({
        ...prev,
        verifying: false,
        error: toErrorMessage(error)
      }))
      setAuthLoading(false)
      return
    }
    setLogin((prev) => ({ ...prev, verifying: false }))
    setAuthLoading(false)
  }, [bootstrap, login.challengeId, login.code, login.phone, navigate])

  const handleIdentifyLogin = useCallback(async (event) => {
    event.preventDefault()
    if (!IDENTIFY_LOGIN_ENABLED) {
      setLogin((prev) => ({ ...prev, error: '简识匹配登录暂未开放' }))
      return
    }

    const siteInput = String(login.identifySiteInput || '').trim()
    const phoneTail = String(login.identifyPhoneTail || '').replace(/\D/g, '').slice(-4)
    const normalizedNameValue = normalizeAppointmentName(login.identifyName)
    const normalizedBuildingValue = normalizeAppointmentBuilding(login.identifyBuilding)

    if (!/^\d{4}$/.test(phoneTail)) {
      setLogin((prev) => ({ ...prev, error: '请输入4位手机尾号' }))
      return
    }
    if (!normalizedNameValue && !normalizedBuildingValue) {
      setLogin((prev) => ({
        ...prev,
          error: '请填写取件人或楼号'
      }))
      return
    }

    const matchType = normalizedNameValue
      ? IDENTIFY_MATCH_TYPES.NAME_OR_RECIPIENT
      : IDENTIFY_MATCH_TYPES.BUILDING
    const normalizedMatchValue = normalizedNameValue || normalizedBuildingValue

    setAuthLoading(true)
    setLogin((prev) => ({ ...prev, identifying: true, error: '', message: '' }))
    try {
      const resolvedSite = siteInput ? await resolveStationByInput(siteInput) : null
      const resolvedSiteId = getValueId(resolvedSite)
      if (siteInput && !resolvedSiteId) {
        throw new Error('未找到驿站')
      }

      const response = await api.auth.identifyLogin({
        siteId: resolvedSiteId || undefined,
        phoneTail,
        matchType,
        matchValue: normalizedMatchValue
      })
      const token = response?.data?.token || ''
      const user = response?.data?.user || null
      const nextRecentSites = resolvedSite ? mergeRecentSiteCache(resolvedSite, site.recentSites) : site.recentSites

      if (token) {
        storage.writeToken(token)
      }

      storage.clearLoginChallenge()
      if (resolvedSite) {
        storage.writeCurrentSite(resolvedSite)
        storage.writeRecentSites(nextRecentSites)
        setSite((prev) => ({
          ...prev,
          current: resolvedSite,
          recentSites: nextRecentSites
        }))
      }
      setSession({ token: token || COOKIE_SESSION_TOKEN, user: user || null })
      setBootError('')
      setLogin(createInitialLoginState())
      navigate('home')
      await bootstrap()
    } catch (error) {
      setLogin((prev) => ({
        ...prev,
        identifying: false,
        error: toIdentifyLoginErrorMessage(error)
      }))
      setAuthLoading(false)
      return
    }
    setLogin((prev) => ({ ...prev, identifying: false }))
    setAuthLoading(false)
  }, [bootstrap, login.identifyBuilding, login.identifyName, login.identifyPhoneTail, login.identifySiteInput, navigate, resolveStationByInput, site.recentSites])

  const handleLogout = useCallback(async () => {
    setShowHistoryPanel(false)
    setShowAccountDeleteConfirm(false)
    if (DEV_PREVIEW_MODE) {
      setBootError('')
      setAuthLoading(false)
      storage.clearLoginChallenge()
      setLogin(createInitialLoginState())
      setSession({
        token: 'dev-preview-token',
        user: DEV_PREVIEW_USER
      })
      setSite(createDevPreviewSiteState())
      setAppointments(createDevPreviewAppointmentState())
      navigate('home')
      return
    }

    try {
      await api.auth.logout()
    } catch (error) {
      void error
    } finally {
      storage.clearSession()
      storage.clearSiteCache()
      localStorage.removeItem('pending_scanned_site')
      storage.writeProfile(null)
      setLogin(createInitialLoginState())
      setSession({ token: '', user: null })
      setSite(DEFAULT_SITE_STATE)
      setAppointments(DEFAULT_APPOINTMENT_STATE)
      navigate('login')
    }
  }, [navigate])

  const handleAccountDeleteRequest = useCallback((event) => {
    event.preventDefault()
    setProfileError('')
    setShowAccountDeleteConfirm(true)
  }, [])

  const handleAccountDeleteCancel = useCallback(() => {
    setShowAccountDeleteConfirm(false)
  }, [])

  const handleAccountDelete = useCallback(async () => {
    setAccountDeleting(true)
    setProfileError('')
    try {
      await api.auth.deleteAccount()
      storage.clearSession()
      storage.clearSiteCache()
      storage.writeProfile(null)
      setLogin(createInitialLoginState())
      setSession({ token: '', user: null })
      setSite(DEFAULT_SITE_STATE)
      setAppointments(DEFAULT_APPOINTMENT_STATE)
      setShowAccountDeleteConfirm(false)
      navigate('login')
    } catch (error) {
      setProfileError(toErrorMessage(error, '账号注销失败，请稍后重试'))
    } finally {
      setAccountDeleting(false)
    }
  }, [navigate])

  const handleInstall = useCallback(async () => {
    if (!installPrompt) {
      setShowInstallHelp(true)
      return
    }
    installPrompt.prompt()
    await installPrompt.userChoice.catch(() => null)
    if (typeof window !== 'undefined') {
      window.__cabinetsInstallPrompt = null
    }
    setInstallPrompt(null)
  }, [installPrompt])

  const handleInstallHelpClose = useCallback(() => {
    setShowInstallHelp(false)
  }, [])

  const installEntryState = resolveInstallEntryState({
    isStandalone: isInstalledApp,
    hasNativePrompt: Boolean(installPrompt)
  })
  const installHelp = resolveInstallHelp(getInstallHelpEnvironment())
  const installHelpModal = showInstallHelp ? (
    <div className="modal-backdrop" onClick={handleInstallHelpClose} role="presentation">
      <div className="modal-card install-help-card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="install-help-title">
        <div className="card-head install-help-head">
          <div>
            <h2 id="install-help-title">{installHelp.title}</h2>
            <p>{installHelp.message}</p>
          </div>
        </div>
        <ol className="install-help-steps">
          {installHelp.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <div className="inline-actions detail-modal-actions">
          <button className="btn btn-primary" type="button" onClick={handleInstallHelpClose}>知道了</button>
        </div>
      </div>
    </div>
  ) : null

  const handleSiteCodeSubmit = useCallback(async (event) => {
    event.preventDefault()
    const code = String(site.siteCodeInput || '').trim()
    if (!code) {
      setSite((prev) => ({ ...prev, error: '请输入驿站编码' }))
      return
    }

    setSite((prev) => ({ ...prev, busy: true, error: '' }))
    try {
      let nextSite = await resolveStationByInput(code)
      if (!nextSite && DEV_PREVIEW_MODE && String(code).trim() === String(DEV_PREVIEW_SITE.siteCode).trim()) {
        nextSite = normalizeSiteSummary(DEV_PREVIEW_SITE)
      }
      if (!nextSite) {
        throw new Error('未找到驿站')
      }

      const synced = await syncStationSelection(nextSite, site.recentSites)
      if (!synced) {
        throw new Error('站点信息保存失败')
      }
      closeSiteIdPrompt()
    } catch (error) {
      setSite((prev) => ({
        ...prev,
        busy: false,
        error: toErrorMessage(error)
      }))
      return
    } finally {
      setSite((prev) => ({
        ...prev,
        busy: false
      }))
    }
  }, [closeSiteIdPrompt, resolveStationByInput, site.recentSites, site.siteCodeInput, syncStationSelection])

  const handleRecentSiteSelect = useCallback(async (nextSite) => {
    const normalizedSite = normalizeSiteSummary(nextSite)
    if (!normalizedSite) return null
    try {
      const synced = await syncStationSelection(normalizedSite, site.recentSites)
      if (!synced) {
        throw new Error('站点信息保存失败')
      }
      return synced
    } catch (error) {
      setSite((prev) => ({
        ...prev,
        busy: false,
        error: toErrorMessage(error)
      }))
      return null
    }
  }, [site.recentSites, syncStationSelection])

  const handleStationChange = useCallback(async (event) => {
    const index = Number(event?.currentTarget?.dataset?.index ?? event?.detail?.value)
    if (!Number.isInteger(index) || index < 0 || index >= stationOptions.length) return
    const nextSite = site.recentSites[index]
    if (!nextSite) return
    setShowStationDropdown(false)
    await handleRecentSiteSelect(nextSite)
  }, [handleRecentSiteSelect, site.recentSites, stationOptions.length])

  const pushAssistantMessage = useCallback((role, text) => {
    const value = String(text || '').trim()
    if (!value) return
    setAssistantMessages((prev) => ([
      ...prev,
      {
        id: `${Date.now()}-${prev.length + 1}`,
        role,
        text: value,
        contentType: 'text',
        cards: [],
        actions: [],
        pending: false,
        error: ''
      }
    ]))
    scrollAssistantTranscriptToBottom()
  }, [scrollAssistantTranscriptToBottom])

  const pushAssistantAppointmentPreview = useCallback((preview, options = {}) => {
    const title = String(options.title || '').trim()
    const statusLabel = String(options.statusLabel || '').trim()
    setAssistantMessages((prev) => ([
      ...prev,
      {
        id: `${Date.now()}-${prev.length + 1}`,
        role: 'assistant',
        text: String(options.text || '').trim(),
        contentType: 'text',
        cards: [{
          type: 'appointment_preview',
          preview: {
            title,
            statusLabel,
            siteLabel: preview?.siteLabel || '',
            timeLabel: preview?.timeLabel || '',
            countLabel: preview?.countLabel || '',
            recipientLabel: preview?.recipientLabel || '',
            tailLabel: preview?.tailLabel || ''
          }
        }],
        actions: [],
        pending: false,
        error: ''
      }
    ]))
    scrollAssistantTranscriptToBottom()
  }, [scrollAssistantTranscriptToBottom])

  const pushAssistantCurrentAppointmentStatusCard = useCallback((appointment, options = {}) => {
    if (!appointment) return
    const statusLabel = getAppointmentStatusLabel(appointment.status)
    const siteLabel = getAppointmentSiteName(appointment) || currentSiteName || '当前驿站'
    const timeLabel = appointmentTimeLabel(appointment) || resolveAppointmentTimePreferenceLabel(appointment.time_preference) || '暂无'
    setAssistantMessages((prev) => ([
      ...prev,
      {
        id: `${Date.now()}-${prev.length + 1}`,
        role: 'assistant',
        text: String(options.text || `预约状态已更新：${statusLabel}`).trim(),
        contentType: 'card',
        cards: [{
          type: 'current_appointment_card',
          title: options.title || '最新预约状态',
          appointment,
          statusLabel,
          siteLabel,
          timeLabel,
          countLabel: getCurrentAppointmentCountLabel(appointment),
          recipientLabel: String(appointment.name || appointment.recipient || profile.name || '未设置').trim(),
          tailLabel: profilePhoneTail
        }],
        actions: [],
        pending: false,
        error: ''
      }
    ]))
    scrollAssistantTranscriptToBottom()
  }, [currentSiteName, profile.name, profilePhoneTail, scrollAssistantTranscriptToBottom])

  const handleAppointmentStatusEvent = useCallback((payload = {}) => {
    if (!payload || typeof payload !== 'object') return
    const appointment = payload.appointment && typeof payload.appointment === 'object' ? payload.appointment : null
    const appointmentId = String(payload.appointmentId || appointment?._id || appointment?.id || '').trim()
    const eventId = `${appointmentId}:${payload.status || appointment?.status || ''}:${payload.occurredAt || appointment?.updatedAt || ''}`
    if (appointmentEventIdsRef.current.has(eventId)) return
    appointmentEventIdsRef.current.add(eventId)
    if (appointmentEventIdsRef.current.size > 80) {
      appointmentEventIdsRef.current = new Set(Array.from(appointmentEventIdsRef.current).slice(-40))
    }

    setAppointments((prev) => {
      const currentList = Array.isArray(prev.current) ? prev.current : []
      const nextCurrent = appointment
        ? [
            appointment,
            ...currentList.filter((item) => String(item?._id || item?.id || '') !== appointmentId)
          ]
        : currentList
      return {
        ...prev,
        current: nextCurrent,
        historyNeedsRefresh: true,
        currentLoading: false,
        error: ''
      }
    })

    if (assistantModeActive && appointment) {
      pushAssistantCurrentAppointmentStatusCard(appointment, {
        text: payload.message || `预约状态已更新：${payload.statusLabel || getAppointmentStatusLabel(appointment.status)}`,
        title: '预约状态更新'
      })
    }

    if (showHistoryPanel) {
      void loadAppointmentHistory({ force: true })
    }
  }, [assistantModeActive, loadAppointmentHistory, pushAssistantCurrentAppointmentStatusCard, showHistoryPanel])

  useEffect(() => {
    appointmentStatusEventHandlerRef.current = handleAppointmentStatusEvent
    logoutHandlerRef.current = handleLogout
  }, [handleAppointmentStatusEvent, handleLogout])

  useEffect(() => {
    if (DEV_PREVIEW_MODE || !session.token || appointmentEventsConnected || !currentAssistantAppointment) {
      return undefined
    }

    let stableRounds = 0
    let disposed = false
    let timer = 0

    const scheduleNextPoll = (changed = false) => {
      if (disposed) return
      const delay = getAssistantAppointmentPollIntervalMs({
        stableRounds,
        changed
      })
      timer = window.setTimeout(() => {
        void pollCurrentAppointment()
      }, delay)
    }

    const pollCurrentAppointment = async () => {
      if (disposed) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        scheduleNextPoll(false)
        return
      }
      if (assistantAppointmentPollBusyRef.current) {
        scheduleNextPoll(false)
        return
      }

      assistantAppointmentPollBusyRef.current = true
      let changed = false
      try {
        const response = await api.appointments.current()
        const nextAppointments = Array.isArray(response?.data?.appointments) ? response.data.appointments : []
        const nextAppointment = nextAppointments[0] || null
        changed = shouldNotifyAssistantAppointmentStatusChange(currentAssistantAppointment, nextAppointment)
        if (changed && assistantModeActive) {
          pushAssistantCurrentAppointmentStatusCard(nextAppointment)
          stableRounds = 0
        } else if (changed) {
          stableRounds = 0
        } else {
          stableRounds += 1
        }
        setAppointments((prev) => ({
          ...prev,
          current: nextAppointments,
          currentLoading: false,
          error: ''
        }))
      } catch (error) {
        setAppointments((prev) => ({
          ...prev,
          currentLoading: false,
          error: toErrorMessage(error)
        }))
      } finally {
        assistantAppointmentPollBusyRef.current = false
        scheduleNextPoll(changed)
      }
    }

    scheduleNextPoll(false)
    return () => {
      disposed = true
      window.clearTimeout(timer)
    }
  }, [
    assistantModeActive,
    appointmentEventsConnected,
    currentAssistantAppointment,
    getAssistantAppointmentPollIntervalMs,
    pushAssistantCurrentAppointmentStatusCard,
    session.token
  ])

  useEffect(() => {
    if (
      DEV_PREVIEW_MODE ||
      !session.token ||
      appointmentEventsDisabledTokenRef.current === session.token
    ) {
      setAppointmentEventsConnected(false)
      return undefined
    }

    const controller = new AbortController()
    let disposed = false
    let reconnectTimer = 0
    let failedReconnectAttempts = 0

    const scheduleReconnect = () => {
      const delay = getAppointmentEventReconnectDelayMs({
        failedAttempts: failedReconnectAttempts
      })
      failedReconnectAttempts += 1
      reconnectTimer = window.setTimeout(connect, delay)
    }

    const connect = () => {
      if (disposed) return
      api.appointments.subscribeToEvents({
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === 'connected') {
            failedReconnectAttempts = 0
            setAppointmentEventsConnected(true)
            return
          }
          if (event.type === 'appointment.updated') {
            appointmentStatusEventHandlerRef.current?.(event.data)
          }
        },
        onError: (error) => {
          setAppointmentEventsConnected(false)
          if (shouldDisableAppointmentEventStream(error)) {
            appointmentEventsDisabledTokenRef.current = session.token
            disposed = true
            controller.abort()
            return
          }
          if (error?.status === 401) {
            disposed = true
            controller.abort()
            void logoutHandlerRef.current?.()
          }
        }
      }).then(() => {
        if (disposed || controller.signal.aborted) return
        setAppointmentEventsConnected(false)
        scheduleReconnect()
      }).catch((error) => {
        if (disposed || controller.signal.aborted) return
        if (shouldDisableAppointmentEventStream(error)) {
          appointmentEventsDisabledTokenRef.current = session.token
          disposed = true
          return
        }
        setAppointmentEventsConnected(false)
        scheduleReconnect()
      })
    }

    connect()
    return () => {
      disposed = true
      setAppointmentEventsConnected(false)
      window.clearTimeout(reconnectTimer)
      controller.abort()
    }
  }, [session.token])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined
    const handleServiceWorkerPushMessage = (event) => {
      if (event.data?.type !== 'appointment-push') return
      const pushedEvent = event.data?.event && typeof event.data.event === 'object' ? event.data.event : null
      if (pushedEvent) {
        handleAppointmentStatusEvent(pushedEvent)
        return
      }
      void loadCurrentAppointments({ force: true })
      setAppointments((prev) => ({ ...prev, historyNeedsRefresh: true }))
    }

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerPushMessage)
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerPushMessage)
    }
  }, [handleAppointmentStatusEvent, loadCurrentAppointments])

  const closeAssistantSheet = useCallback(() => {
    setAssistantSheet('')
  }, [])

  const loadOlderAssistantMessages = useCallback(async () => {
    if (!assistantModeActive || !assistantHistoryHasMoreOlder || assistantHistoryLoadingOlder) return
    if (DEV_PREVIEW_MODE || !assistantConversation?._id) return

    const transcript = assistantTranscriptRef.current
    const cursor = String(assistantHistoryOlderCursor || assistantMessages[0]?.id || '').trim()
    if (!cursor) {
      setAssistantHistoryHasMoreOlder(false)
      setAssistantHistoryOlderCursor('')
      return
    }

    const previousScrollHeight = transcript?.scrollHeight || 0
    const previousScrollTop = transcript?.scrollTop || 0
    setAssistantHistoryLoadingOlder(true)
    setAssistantHistoryOlderError('')

    try {
      const response = await api.assistant.getMessages(assistantConversation._id, {
        limit: ASSISTANT_INITIAL_MESSAGE_LIMIT,
        beforeId: cursor
      })
      const messagePage = resolveAssistantMessagesPage(response?.data || {}, [])
      const olderMessages = normalizeAssistantMessages(messagePage.messages).map(withAssistantSiteEntryFallback)
      const nextBeforeCursor = messagePage.nextBeforeCursor || getAssistantMessageCursor(messagePage.messages[0]) || olderMessages[0]?.id || ''

      setAssistantHistoryHasMoreOlder(messagePage.hasMoreOlder && Boolean(nextBeforeCursor))
      setAssistantHistoryOlderCursor(messagePage.hasMoreOlder ? nextBeforeCursor : '')

      if (!olderMessages.length) {
        setAssistantHistoryHasMoreOlder(false)
        setAssistantHistoryOlderCursor('')
        return
      }

      setAssistantMessages((prev) => mergeOlderAssistantMessages(prev, olderMessages))
      preserveAssistantTranscriptScrollPosition(previousScrollHeight, previousScrollTop)
    } catch (error) {
      setAssistantHistoryOlderError(toErrorMessage(error, '更早消息加载失败'))
    } finally {
      setAssistantHistoryLoadingOlder(false)
    }
  }, [
    assistantConversation,
    assistantHistoryHasMoreOlder,
    assistantHistoryLoadingOlder,
    assistantHistoryOlderCursor,
    assistantMessages,
    assistantModeActive,
    preserveAssistantTranscriptScrollPosition
  ])

  const handleAssistantTranscriptScroll = useCallback(() => {
    if (!assistantModeActive || !assistantHistoryHasMoreOlder || assistantHistoryLoadingOlder) return
    const transcript = assistantTranscriptRef.current
    if (!transcript || transcript.scrollTop > ASSISTANT_HISTORY_LOAD_THRESHOLD_PX) return
    void loadOlderAssistantMessages()
  }, [assistantHistoryHasMoreOlder, assistantHistoryLoadingOlder, assistantModeActive, loadOlderAssistantMessages])

  const submitAssistantText = useCallback(async (rawText, options = {}) => {
    const nextMessage = String(rawText || '').trim()
    if (!nextMessage || assistantSending) return
    const syncAppointmentMessage = options.syncAppointmentMessage !== false
    const clientMessageId = `pwa-${Date.now()}-${Math.random().toString(16).slice(2)}`
    const localMessageId = `local-${clientMessageId}`
    setAssistantMessages((prev) => ([
      ...prev,
      {
        id: localMessageId,
        role: 'user',
        text: nextMessage,
        contentType: 'text',
        cards: [],
        actions: [],
        pending: true,
        error: ''
      }
    ]))
    if (syncAppointmentMessage) {
      setAppointments((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          message: nextMessage
        }
      }))
    }
    setAssistantComposer('')
    setAssistantSending(true)
    setAssistantError('')
    scrollAssistantTranscriptToBottom()

    try {
      let conversation = assistantConversation
      if (!conversation?._id && !DEV_PREVIEW_MODE) {
        const conversationRes = await api.assistant.createConversation({ domain: 'appointment' })
        conversation = conversationRes?.data?.conversation || null
        setAssistantConversation(conversation)
      }

      if (!conversation?._id || DEV_PREVIEW_MODE) {
        setAssistantMessages((prev) => prev.map((item) => (
          item.id === localMessageId ? { ...item, pending: false } : item
        )))
        scrollAssistantTranscriptToBottom()
        pushAssistantMessage('assistant', '已记录，我会根据当前页面信息继续帮你。')
        return
      }

      const response = await api.assistant.sendMessage(conversation._id, {
        text: nextMessage,
        clientMessageId,
        context: {
          siteId: currentSiteId,
          appointmentForm: {
            selectedTime: appointments.form.selectedTime,
            expectedPackageCount: normalizeExpectedPackageCount(appointments.form.expectedPackageCount, 1),
            acceptShare: showShareOption ? !!appointments.form.acceptShare : false,
            message: showRemarkField ? String(appointments.form.message || '').trim() : ''
          },
          route,
          uiMode
        }
      })
      const data = response?.data || {}
      if (data.conversation) {
        setAssistantConversation(data.conversation)
      }
      applyAssistantStatePatch(data.statePatch)
      const remoteMessages = normalizeAssistantMessages(data.messages || []).map(withAssistantSiteEntryFallback)
      setAssistantMessages((prev) => ([
        ...prev.filter((item) => item.id !== localMessageId),
        ...remoteMessages
      ]))
      scrollAssistantTranscriptToBottom()
    } catch (error) {
      setAssistantMessages((prev) => prev.map((item) => (
        item.id === localMessageId
          ? { ...item, pending: false, error: toErrorMessage(error, '发送失败') }
          : item
      )))
      setAssistantError(toErrorMessage(error, '小助手暂时不可用'))
    } finally {
      setAssistantSending(false)
    }
  }, [assistantConversation, assistantSending, appointments.form.acceptShare, appointments.form.expectedPackageCount, appointments.form.message, appointments.form.selectedTime, applyAssistantStatePatch, currentSiteId, pushAssistantMessage, route, scrollAssistantTranscriptToBottom, showRemarkField, showShareOption, uiMode])

  assistantSubmitTextRef.current = submitAssistantText

  const handleAssistantQuickAction = useCallback((action) => {
    if (assistantAppointmentLocked && ['site', 'time', 'count', 'share', 'clear'].includes(action)) {
      setShowComposerMenu(false)
      return
    }
    if (action === 'site') {
      setShowComposerMenu(false)
      setAssistantSheet('site')
      return
    }
    if (action === 'time') {
      setShowComposerMenu(false)
      setAssistantSheet('time')
      return
    }
    if (action === 'count') {
      setShowComposerMenu(false)
      setAssistantSheet('')
      let nextCount = EXPECTED_PACKAGE_COUNT_OPTIONS[0]
      setAppointments((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          expectedPackageCount: (() => {
            const currentCount = normalizeExpectedPackageCount(prev.form.expectedPackageCount, 1)
            const currentIndex = EXPECTED_PACKAGE_COUNT_OPTIONS.indexOf(currentCount)
            const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % EXPECTED_PACKAGE_COUNT_OPTIONS.length : 0
            nextCount = EXPECTED_PACKAGE_COUNT_OPTIONS[nextIndex]
            return nextCount
          })()
        }
      }))
      pushAssistantMessage('assistant', `单数已切换为 ${nextCount} 单。`)
      return
    }
    if (action === 'share') {
      if (!showShareOption) return
      setShowComposerMenu(false)
      setAssistantSheet('')
      let nextShare = false
      setAppointments((prev) => {
        nextShare = !prev.form.acceptShare
        return {
          ...prev,
          form: {
            ...prev.form,
            acceptShare: nextShare
          }
        }
      })
      pushAssistantMessage('assistant', nextShare ? '已开启共享预约。' : '已关闭共享预约。')
      return
    }
    if (action === 'clear') {
      setShowComposerMenu(false)
      setAssistantComposer('')
      return
    }
    if (action === 'pickup') {
      setShowComposerMenu(false)
      if (assistantAppointmentLocked) {
        openAssistantCurrentAppointmentPanel()
        return
      }
      void submitAssistantText('预约取件', { syncAppointmentMessage: false })
      return
    }
  }, [assistantAppointmentLocked, openAssistantCurrentAppointmentPanel, pushAssistantMessage, showShareOption, submitAssistantText])

  const handleAssistantComposerSend = useCallback(async (event) => {
    event.preventDefault()
    void submitAssistantText(assistantComposer, { syncAppointmentMessage: true })
  }, [assistantComposer, submitAssistantText])

  const handleAppointmentFieldChange = useCallback((field, value) => {
    setAppointments((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [field]: value
      }
    }))
  }, [])

  const handleAssistantStationSelect = useCallback(async (index) => {
    const nextSite = site.recentSites[index]
    if (!nextSite) return
    await handleRecentSiteSelect(nextSite)
    pushAssistantMessage('user', `选驿站：${getSiteLabel(nextSite)}`)
    closeAssistantSheet()
  }, [closeAssistantSheet, handleRecentSiteSelect, pushAssistantMessage, site.recentSites])

  const handleAssistantTimeSelect = useCallback((value) => {
    const nextValue = String(value || '').trim()
    if (!nextValue) return
    handleAppointmentFieldChange('selectedTime', nextValue)
    pushAssistantMessage('user', `选时段：${resolveAppointmentTimePreferenceLabel(nextValue)}`)
    closeAssistantSheet()
  }, [closeAssistantSheet, handleAppointmentFieldChange, pushAssistantMessage])

  const submitAppointmentFromCurrentState = useCallback(async ({ source = 'form' } = {}) => {
    const isAssistantSource = source === 'assistant'
    if (appointments.currentLoading) {
      const message = '正在同步当前预约，请稍后再提交'
      setAppointments((prev) => ({ ...prev, formError: message }))
      if (isAssistantSource) pushAssistantMessage('assistant', message)
      return false
    }
    if (hasBlockingCurrentAppointment) {
      const message = '当前已有未完成预约，请完成或取消后再提交新的预约'
      setAppointments((prev) => ({ ...prev, formError: message }))
      if (isAssistantSource) pushAssistantMessage('assistant', message)
      return false
    }
    if (!currentSiteId) {
      const message = '请先选择驿站'
      setAppointments((prev) => ({ ...prev, formError: message }))
      if (isAssistantSource) pushAssistantMessage('assistant', message)
      return false
    }

    const normalizedForm = {
      ...appointments.form,
      name: normalizeAppointmentName(profile.name || ''),
      building: normalizeAppointmentBuilding(profile.building || ''),
      expectedPackageCount: normalizeExpectedPackageCount(appointments.form.expectedPackageCount, 1),
      acceptShare: showShareOption ? !!appointments.form.acceptShare : false,
      message: showRemarkField ? String(appointments.form.message || '').trim() : ''
    }
    if (!normalizedForm.name && !normalizedForm.building) {
      const message = '请先在右上角完善个人信息'
      setAppointments((prev) => ({ ...prev, formError: message }))
      if (isAssistantSource) pushAssistantMessage('assistant', message)
      return false
    }
    if (!normalizedForm.selectedTime) {
      const message = '请选择预约时间段'
      setAppointments((prev) => ({ ...prev, formError: message }))
      if (isAssistantSource) pushAssistantMessage('assistant', message)
      return false
    }

    if (DEV_PREVIEW_MODE) {
      const appointmentTime = calculateAppointmentTimeByPreference(normalizedForm.selectedTime)
      const siteLabel = getSiteLabel(selectedSite)
      const timeLabel = formatDateTime(appointmentTime) || resolveAppointmentTimePreferenceLabel(normalizedForm.selectedTime) || '暂无'
      const previewAppointment = {
        _id: `dev-${Date.now()}`,
        name: normalizedForm.name,
        recipient: normalizedForm.name,
        building: normalizedForm.building,
        site_id: currentSiteId,
        site_name: getSiteLabel(selectedSite),
        status: 'pending',
        appointment_time: appointmentTime,
        time_preference: normalizedForm.selectedTime,
        expected_package_count: normalizedForm.expectedPackageCount,
        accept_share: normalizedForm.acceptShare,
        message: normalizedForm.message,
        pickup_code_ori: ''
      }
      setAppointments((prev) => ({
        ...prev,
        formSubmitting: false,
        formError: '',
        history: [previewAppointment, ...prev.history],
        historyPage: 1,
        historyHasMore: false,
        form: {
          ...prev.form,
          selectedTime: '',
          message: ''
        }
      }))
      if (isAssistantSource) {
        pushAssistantAppointmentPreview(
          {
            siteLabel,
            timeLabel,
            countLabel: `${normalizedForm.expectedPackageCount} 单`,
            recipientLabel: normalizedForm.name,
            tailLabel: profilePhoneTail
          },
          {
            title: `预约：${siteLabel} · ${resolveAppointmentTimePreferenceLabel(normalizedForm.selectedTime) || timeLabel} · ${normalizedForm.expectedPackageCount}单`,
            statusLabel: '已提交',
            text: '预约已提交'
          }
        )
      }
      return true
    }

    setAppointments((prev) => ({ ...prev, formSubmitting: true, formError: '' }))
    try {
      const appointmentTime = calculateAppointmentTimeByPreference(normalizedForm.selectedTime)
      const siteLabel = getSiteLabel(selectedSite)
      const timeLabel = formatDateTime(appointmentTime) || resolveAppointmentTimePreferenceLabel(normalizedForm.selectedTime) || '暂无'
      const payload = buildAppointmentSubmitPayload({
        siteId: currentSiteId,
        appointmentTime,
        appointmentForm: normalizedForm
      })
      await api.appointments.create(payload)
      setAppointments((prev) => ({
        ...prev,
        formSubmitting: false,
        formError: '',
        form: {
          ...prev.form,
          selectedTime: '',
          message: ''
        }
      }))
      await loadCurrentAppointments({ force: true })
      if (showHistoryPanel) {
        await loadAppointmentHistory({ force: true })
      } else {
        setAppointments((prev) => ({ ...prev, historyNeedsRefresh: true }))
      }
      if (isAssistantSource) {
        pushAssistantAppointmentPreview(
          {
            siteLabel,
            timeLabel,
            countLabel: `${normalizedForm.expectedPackageCount} 单`,
            recipientLabel: normalizedForm.name,
            tailLabel: profilePhoneTail
          },
          {
            title: `预约：${siteLabel} · ${resolveAppointmentTimePreferenceLabel(normalizedForm.selectedTime) || timeLabel} · ${normalizedForm.expectedPackageCount}单`,
            statusLabel: '已提交',
            text: showHistoryPanel ? '预约已提交，我已经同步到当前预约和历史记录。' : '预约已提交，我已经同步到当前预约。'
          }
        )
      }
      return true
    } catch (error) {
      const message = String(error?.code || '').trim() === 'IDENTIFY_LOGIN_QUOTA_EXCEEDED'
        ? '当前站点的简识登录预约次数已达上限，请先完成取件或改用短信验证码登录'
        : toErrorMessage(error)
      setAppointments((prev) => ({
        ...prev,
        formSubmitting: false,
        formError: message
      }))
      if (isAssistantSource) pushAssistantMessage('assistant', message)
      return false
    }
  }, [appointments.currentLoading, appointments.form, currentSiteId, hasBlockingCurrentAppointment, loadAppointmentHistory, loadCurrentAppointments, profile.building, profile.name, profilePhoneTail, pushAssistantAppointmentPreview, pushAssistantMessage, selectedSite, showHistoryPanel, showRemarkField, showShareOption])

  const handleAssistantCardChoice = useCallback(async (card, choice) => {
    const value = String(choice?.value || '').trim()
    if (!value) return
    if (assistantAppointmentLocked && ['select_site_slot', 'quick_replies', 'choice_card', 'form_card'].includes(card?.type)) {
      return
    }
    if (choice?.type === 'select_site_slot') {
      const siteChoice = choice.site || {}
      const slotChoice = choice.slot || {}
      const nextSite = normalizeSiteSummary(siteChoice.site || {
        _id: siteChoice.siteId,
        id: siteChoice.siteId,
        name: siteChoice.siteLabel
      })
      const nextTime = String(slotChoice.value || '').trim()
      if (!nextSite || !nextTime) return
      const synced = await handleRecentSiteSelect(nextSite)
      if (!synced) {
        pushAssistantMessage('assistant', '驿站同步失败，请稍后重试。')
        return
      }
      handleAppointmentFieldChange('selectedTime', nextTime)
      setAssistantConfirmDraft({
        site: nextSite,
        siteLabel: getSiteLabel(nextSite) || siteChoice.siteLabel || '当前驿站',
        slot: slotChoice,
        slotLabel: slotChoice.label || resolveAppointmentTimePreferenceLabel(nextTime)
      })
      pushAssistantMessage('user', `预约：${getSiteLabel(nextSite) || siteChoice.siteLabel} · ${slotChoice.label || resolveAppointmentTimePreferenceLabel(nextTime)}`)
      return
    }
    if (card?.type === 'appointment_confirm_card' && choice?.type === 'set_selected_time') {
      handleAppointmentFieldChange('selectedTime', value)
      setAppointments((prev) => ({
        ...prev,
        formError: ''
      }))
      return
    }
    if (card?.type === 'appointment_confirm_card' && choice?.type === 'set_expected_package_count') {
      handleAppointmentFieldChange('expectedPackageCount', value)
      return
    }
    if (card?.type === 'site_entry_card' && choice?.type === 'open_site_scanner') {
      scanQRCode()
      return { ok: true }
    }
    if (card?.type === 'site_entry_card' && choice?.type === 'submit_site_entry') {
      const siteId = String(choice?.value || '').trim()
      if (!siteId) {
        return { ok: false, error: '请输入站点码' }
      }

      try {
        let nextSite = await resolveStationByInput(siteId)
        if (!nextSite && DEV_PREVIEW_MODE && siteId === String(DEV_PREVIEW_SITE.siteCode).trim()) {
          nextSite = normalizeSiteSummary(DEV_PREVIEW_SITE)
        }
        if (!nextSite) {
          throw new Error('未找到驿站')
        }

        const nextRecent = mergeRecentSiteCache(nextSite, site.recentSites)
        const committed = await commitSiteSelection(nextSite, nextRecent)
        const resolvedSite = committed?.current || nextSite
        const resolvedSiteId = getValueId(resolvedSite)
        const resolved = {
          siteId: resolvedSiteId,
          siteLabel: getSiteLabel(resolvedSite) || siteId
        }

        await loadSiteBundle(resolvedSiteId, { force: DEV_PREVIEW_MODE ? false : true })
        await loadCurrentAppointments({ force: true })
        setAppointments((prev) => ({
          ...prev,
          formError: '',
          form: {
            ...prev.form,
            selectedTime: ''
          }
        }))
        return { ok: true, ...resolved }
      } catch (submitError) {
        return { ok: false, error: toErrorMessage(submitError, '未找到驿站') }
      }
    }
    if (card?.type === 'appointment_confirm_card' && choice?.type === 'dismiss_appointment_confirm') {
      setAssistantConfirmDraft(null)
      return
    }
    if (['form_card', 'appointment_confirm_card'].includes(card?.type) && choice?.type === 'create_appointment') {
      pushAssistantMessage('user', choice.label || '提交预约')
      const submitted = await submitAppointmentFromCurrentState({ source: 'assistant' })
      if (submitted && card?.type === 'appointment_confirm_card') {
        setAssistantConfirmDraft(null)
      }
      return
    }
    if (card?.type === 'quick_replies') {
      setAssistantComposer(String(choice?.label || value).trim())
      assistantInputRef.current?.focus?.()
      return
    }
    if (card?.type === 'choice_card' && String(card.title || '').includes('驿站')) {
      const nextSite = site.recentSites.find((item) => getValueId(item) === value)
      if (nextSite) {
        await handleRecentSiteSelect(nextSite)
        pushAssistantMessage('user', `选驿站：${getSiteLabel(nextSite)}`)
      }
    }
  }, [assistantAppointmentLocked, commitSiteSelection, handleAppointmentFieldChange, handleRecentSiteSelect, loadCurrentAppointments, loadSiteBundle, pushAssistantMessage, resolveStationByInput, scanQRCode, site.recentSites, submitAppointmentFromCurrentState])

  const handleToggleAnnouncement = useCallback(() => {
    setSite((prev) => ({ ...prev, announcementExpanded: !prev.announcementExpanded }))
  }, [])

  const handleToggleAppointmentForm = useCallback(() => {
    setAppointments((prev) => ({
      ...prev,
      appointmentExpanded: !prev.appointmentExpanded
    }))
  }, [])

  const handleAppointmentSubmit = useCallback(async (event) => {
    event.preventDefault()
    await submitAppointmentFromCurrentState({ source: 'form' })
  }, [submitAppointmentFromCurrentState])

  const handleAppointmentAction = useCallback(async (appointment, actionKind) => {
    const appointmentId = String(appointment?._id || '').trim()
    if (!appointmentId || !actionKind) return
    const siteLabel = String(appointment?.site_name || appointment?.siteLabel || currentSiteName || '当前驿站').trim()
    const timePreferenceLabel = resolveAppointmentTimePreferenceLabel(appointment?.time_preference)
    const timeLabel = formatDateTime(appointment?.appointment_time) || timePreferenceLabel || '暂无'
    const title = `预约：${siteLabel} · ${timePreferenceLabel || timeLabel}`
    const preview = {
      siteLabel,
      timeLabel,
      recipientLabel: String(appointment?.name || appointment?.recipient || profile.name || '未设置').trim(),
      tailLabel: profilePhoneTail
    }
    const isConfirmPickedUpAction = actionKind === 'confirm-user-picked-up'
    const shouldMirrorInChat = assistantModeActive && actionKind === 'cancel'
    if (DEV_PREVIEW_MODE) {
      setAppointments((prev) => ({
        ...prev,
        error: '',
        history: prev.history.map((item) => {
          if (String(item?._id || '') !== appointmentId) return item
          if (actionKind === 'cancel') return { ...item, status: 'cancelled' }
          if (actionKind === 'dismiss-invalid') return { ...item, status: 'invalid' }
          if (actionKind === 'dismiss-expired') return { ...item, status: 'completed' }
          if (actionKind === 'dismiss-eviction') return { ...item, status: 'completed' }
          if (actionKind === 'confirm-user-picked-up') {
            return {
              ...item,
              status: 'completed',
              pickup_info: {
                ...(item?.pickup_info && typeof item.pickup_info === 'object' ? item.pickup_info : {}),
                method: 'user_marked'
              }
            }
          }
          return item
        })
      }))
      if (isConfirmPickedUpAction) {
        setActionToast({ tone: 'success', message: '已确认取件' })
      }
      if (shouldMirrorInChat) {
        pushAssistantAppointmentPreview(preview, {
          title: `${title} · ${normalizeExpectedPackageCount(appointment?.expected_package_count || 1, 1)}单`,
          statusLabel: '取消',
          text: `预约：${siteLabel} · ${timePreferenceLabel || timeLabel} · 取消`
        })
      }
      return
    }

    setAppointments((prev) => ({ ...prev, actionBusyId: appointmentId, error: '' }))
    try {
      if (actionKind === 'cancel') {
        await api.appointments.cancel(appointmentId, '')
      } else if (actionKind === 'dismiss-invalid') {
        await api.appointments.dismissInvalid(appointmentId)
      } else if (actionKind === 'dismiss-expired') {
        await api.appointments.dismissExpired(appointmentId)
      } else if (actionKind === 'dismiss-eviction') {
        await api.appointments.dismissEviction(appointmentId)
      } else if (actionKind === 'confirm-user-picked-up') {
        await api.appointments.confirmPickedUpByUser(appointmentId)
      }
      await loadCurrentAppointments({ force: true })
      if (showHistoryPanel) {
        await loadAppointmentHistory({ force: true })
      } else {
        setAppointments((prev) => ({ ...prev, historyNeedsRefresh: true }))
      }
      if (isConfirmPickedUpAction) {
        setActionToast({ tone: 'success', message: '已确认取件' })
      }
      if (shouldMirrorInChat) {
        pushAssistantAppointmentPreview(preview, {
          title: `${title} · ${normalizeExpectedPackageCount(appointment?.expected_package_count || 1, 1)}单`,
          statusLabel: '取消',
          text: `预约：${siteLabel} · ${timePreferenceLabel || timeLabel} · 取消`
        })
      }
    } catch (error) {
      if (isConfirmPickedUpAction) {
        const errorCode = String(error?.code || '').trim()
        if (['APPOINTMENT_ALREADY_COMPLETED', 'APPOINTMENT_STATUS_NOT_PICKUP_READY'].includes(errorCode)) {
          try {
            await loadCurrentAppointments({ force: true })
            if (showHistoryPanel) {
              await loadAppointmentHistory({ force: true })
            } else {
              setAppointments((prev) => ({ ...prev, historyNeedsRefresh: true }))
            }
          } catch (refreshError) {
          }
        }
        setActionToast({ tone: 'error', message: toConfirmPickedUpErrorMessage(error) })
      } else {
        setAppointments((prev) => ({ ...prev, error: toErrorMessage(error) }))
      }
    } finally {
      setAppointments((prev) => ({ ...prev, actionBusyId: '' }))
    }
  }, [assistantModeActive, currentSiteName, loadAppointmentHistory, loadCurrentAppointments, profile.name, profilePhoneTail, pushAssistantAppointmentPreview, showHistoryPanel])

  const handleHistoryLoadMore = useCallback(async () => {
    if (appointments.historyLoading || !appointments.historyHasMore) return
    if (DEV_PREVIEW_MODE) {
      setAppointments((prev) => ({ ...prev, historyLoading: false, historyHasMore: false }))
      return
    }

    const nextPage = appointments.historyPage + 1
    const requestStartedAt = Date.now()
    setAppointments((prev) => ({
      ...prev,
      historyLoading: true,
      historyLastAttemptAt: requestStartedAt
    }))
    try {
      const response = await api.appointments.my({ page: nextPage, limit: 20, includeStats: 0 })
      const nextItems = Array.isArray(response?.data?.appointments) ? response.data.appointments : []
      setAppointments((prev) => ({
        ...prev,
        history: [...prev.history, ...nextItems],
        historyPage: nextPage,
        historyHasMore: nextPage < Number(response?.data?.pagination?.pages || nextPage),
        historyLoadedAt: Date.now(),
        historyLastAttemptAt: requestStartedAt,
        historyNeedsRefresh: false,
        historyLoading: false
      }))
    } catch (error) {
      setAppointments((prev) => ({
        ...prev,
        historyLastAttemptAt: requestStartedAt,
        historyLoading: false,
        error: toErrorMessage(error)
      }))
    }
  }, [appointments.historyHasMore, appointments.historyLoading, appointments.historyPage])

  const handleOpenHistoryDetail = useCallback((appointment) => {
    setAppointments((prev) => ({ ...prev, historyDetail: appointment, copiedCode: '' }))
  }, [])

  const handleCloseHistoryDetail = useCallback(() => {
    setAppointments((prev) => ({ ...prev, historyDetail: null, copiedCode: '' }))
  }, [])

  const handleCopyPickupCode = useCallback(async (appointment) => {
    const code = formatAppointmentCode(appointment)
    if (!code) return
    try {
      await navigator.clipboard.writeText(code)
      setAppointments((prev) => ({ ...prev, copiedCode: '已复制取件码' }))
    } catch (error) {
      setAppointments((prev) => ({ ...prev, copiedCode: '复制失败，请手动选择' }))
    }
  }, [])

  const historyCards = useMemo(() => appointments.history.map((item) => ({
    ...item,
    accentColor: getAppointmentCardAccent(item)
  })), [appointments.history])

  const themeToggleButton = (
    <button
      className={`icon-btn theme-toggle ${theme}`}
      type="button"
      onClick={handleThemeToggle}
      aria-label={themeActionLabel}
      title={themeActionLabel}
    >
      <span className={`theme-toggle-icon ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`} aria-hidden="true">
        {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
      </span>
    </button>
  )

  if (booting) {
    return (
      <div className="app-shell splash-shell">
        <div className="theme-fab">{themeToggleButton}</div>
        <div className="splash-card">
          <div className="splash-orb splash-orb-a" />
          <div className="splash-orb splash-orb-b" />
          <div className="splash-title">正在加载你的站点与预约数据</div>
          <div className="splash-text">请稍等，正在同步登录状态。</div>
        </div>
      </div>
    )
  }

  if (!session.token && !DEV_PREVIEW_MODE) {
    return (
      <>
        {bootError && !bootBannerDismissed ? (
          <div className="toast toast-error boot-banner" role="alert" aria-live="assertive">
            <span className="boot-banner-text">{bootError}</span>
            <button
              className="boot-banner-close"
              type="button"
              aria-label="关闭提示"
              onClick={() => setBootBannerDismissed(true)}
            >
              ×
            </button>
          </div>
        ) : null}
        <div className={`app-shell login-shell ${isInstalledApp ? 'is-installed-app' : ''}`}>
          <div className="theme-fab">{themeToggleButton}</div>
          <div className="login-topline" />
          <div className="login-hero">
            <h1>秒取-轻应用</h1>
            {IDENTIFY_LOGIN_ENABLED ? null : <p>手机号验证后，通过尾号一键预约。</p>}
          </div>
          <form className="card login-card login-compact" onSubmit={IDENTIFY_LOGIN_ENABLED ? handleIdentifyLogin : handleLoginVerify}>
            {IDENTIFY_LOGIN_ENABLED ? (
                <>
                  <div className="login-identify-grid">
                    <label className="field login-identify-field">
                      <span>手机尾号<em className="login-identify-required" aria-hidden="true">*</em></span>
                      <input
                        value={login.identifyPhoneTail}
                        onChange={(event) => setLogin((prev) => ({ ...prev, identifyPhoneTail: event.target.value.replace(/\D/g, '').slice(-4) }))}
                        inputMode="numeric"
                        placeholder="1234"
                        autoComplete="tel"
                      />
                    </label>
                    <label className="field login-identify-field">
                      <span>站点ID（选填）</span>
                      <div className="login-identify-input-shell">
                        <input
                          value={login.identifySiteInput}
                          onChange={(event) => setLogin((prev) => ({ ...prev, identifySiteInput: event.target.value }))}
                          placeholder="1A2B3C"
                          autoComplete="new-password"
                          name="identify-site-code"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                        />
                        <button className="login-identify-scan-btn" type="button" onClick={handleIdentifySiteScan} aria-label="扫码输入站点ID" title="扫码输入站点ID">
                          <CameraIcon />
                        </button>
                      </div>
                    </label>
                    <label className="field login-identify-field">
                      <span>取件人</span>
                      <input
                        value={login.identifyName}
                        onChange={(event) => setLogin((prev) => ({ ...prev, identifyName: event.target.value }))}
                        placeholder="张"
                        autoComplete="off"
                      />
                    </label>
                    <label className="field login-identify-field">
                      <span>楼号</span>
                      <input
                        value={login.identifyBuilding}
                        onChange={(event) => setLogin((prev) => ({ ...prev, identifyBuilding: event.target.value }))}
                        placeholder="18"
                        autoComplete="off"
                      />
                    </label>
                  </div>
                  <button className="btn btn-primary btn-block login-submit-btn" type="submit" disabled={authLoading || login.identifying}>
                    {login.identifying ? '开始使用中...' : '开始使用'}
                </button>
              </>
            ) : (
              <>
                <label className="field">
                  <span>手机号</span>
                  <input
                    value={login.phone}
                    onChange={(event) => setLogin((prev) => ({ ...prev, phone: event.target.value }))}
                    inputMode="numeric"
                    placeholder="请输入手机号"
                    autoComplete="tel"
                  />
                </label>
                <div className="field login-code-field">
                  <span>短信验证码</span>
                  <div className="login-code-row">
                    <div className="login-code-input-wrapper">
                      <input
                        className="login-code-input"
                        value={login.code}
                        onChange={(event) => setLogin((prev) => ({ ...prev, code: event.target.value }))}
                        inputMode="numeric"
                        placeholder="验证码"
                        autoComplete="one-time-code"
                      />
                      {login.code ? (
                        <button
                          className="login-code-clear-btn"
                          type="button"
                          onClick={() => setLogin((prev) => ({ ...prev, code: '' }))}
                          aria-label="清空验证码"
                        >
                          <CloseIcon />
                        </button>
                      ) : null}
                    </div>
                    <button className="btn btn-secondary login-send-btn" type="button" onClick={handleLoginSendCode} disabled={login.sending || login.countdown > 0}>
                      {login.countdown > 0 ? `${login.countdown}s 重发` : '发送验证码'}
                    </button>
                  </div>
                </div>
                <button className="btn btn-primary btn-block login-submit-btn" type="submit" disabled={authLoading || login.verifying}>
                  {login.verifying ? '登录中...' : '登录'}
                </button>
              </>
            )}
            {installEntryState !== 'installed' ? (
              <div className="login-install-entry">
                <button className="btn btn-secondary btn-block login-install-btn" type="button" onClick={handleInstall}>安装应用</button>
                <p className="login-install-hint">安装后可从桌面快速打开，验证码也更方便查看。</p>
              </div>
            ) : null}
            <div className="login-status" aria-live="polite">
              {login.error ? <span className="form-hint error">{login.error}</span> : login.message ? <span className="form-hint success">{login.message}</span> : null}
            </div>
          </form>
        </div>
        {installHelpModal}
      </>
    )
  }

  const settingsRouteActive = route === 'settings'
  const announcement = site.announcement
  const slots = Array.isArray(site.slots) ? site.slots : []
  const siteAccent = getSiteAccentColor(getSiteLabel(selectedSite))
  const historyDetail = appointments.historyDetail
  const isSiteBusy = site.loading || site.slotsLoading
  const isAppointmentsBusy = appointments.currentLoading
  const isHistoryBusy = appointments.historyLoading
  const showFloatingTheme = booting || (!session.token && !DEV_PREVIEW_MODE)
  const assistantVoicePadLabel = getAssistantVoicePadLabel({
    listening: assistantVoiceListening,
    draft: assistantVoiceDraft,
    cancelling: assistantVoiceCancelling
  })

  return (
    <div className={`app-shell ${assistantModeActive ? 'assistant-shell' : ''}`}>
      {showFloatingTheme ? <div className="theme-fab">{themeToggleButton}</div> : null}
      {actionToast?.message ? (
        <div className={`toast ${actionToast.tone === 'error' ? 'toast-error' : 'toast-success'} sync-toast`} role={actionToast.tone === 'error' ? 'alert' : 'status'} aria-live={actionToast.tone === 'error' ? 'assertive' : 'polite'}>{actionToast.message}</div>
      ) : siteSyncToast ? <div className="toast toast-info sync-toast" role="status" aria-live="polite">{siteSyncToast}</div> : null}
      <header className="topbar">
        <div className="topbar-copy">
          <div className="topbar-kicker">{getTopbarGreetingLabel(profile, session.user)}</div>
          <div className="topbar-date">{currentDateLabel}</div>
        </div>
        <div className="topbar-actions">
          {installEntryState !== 'installed' ? (
            <button className="icon-btn topbar-action-btn topbar-install-btn" type="button" onClick={handleInstall}>安装</button>
          ) : null}
          <button className="profile-trigger" type="button" onClick={openProfilePanel} aria-label="个人信息" title="个人信息">
            <span className="profile-avatar">{profileAvatarLabel}</span>
          </button>
        </div>
      </header>

      {settingsRouteActive ? (
        <section className="settings-page">
          <div className="settings-panel card settings-panel-compact">
            <section className="settings-section">
              <form className="profile-drawer-form settings-form settings-form-compact" onSubmit={handleProfilePanelSubmit}>
                <div className="settings-profile-grid">
                  <div className="settings-profile-head">
                    <div className="settings-profile-head-copy">
                      <div className="settings-profile-title-row">
                        <div className="settings-profile-head-title">个人信息</div>
                        <span className={`settings-profile-badge ${authStatusPresentation.tone === 'warning' ? 'is-warning' : 'is-success'}`}>{authStatusPresentation.label}</span>
                      </div>
                      <div className="settings-profile-meta">
                        {authStatusPresentation.phoneMeta ? <span className="settings-profile-meta-text">{authStatusPresentation.phoneMeta}</span> : null}
                      </div>
                      {authStatusPresentation.detail ? <div className="settings-profile-head-subtitle">{authStatusPresentation.detail}</div> : null}
                    </div>
                    <button
                      className="settings-close-btn"
                      type="button"
                      onClick={closeProfilePanel}
                      aria-label="关闭个人设置"
                      title="关闭个人设置"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  <div className="settings-profile-fields">
                    <label className="settings-profile-field">
                      <span>取件人</span>
                      <input
                        value={profile.name}
                        onChange={(event) => handleProfileFieldChange('name', event.target.value)}
                        placeholder="请输入取件人"
                        aria-label="取件人"
                      />
                    </label>
                    <label className="settings-profile-field">
                      <span>楼号</span>
                      <input
                        value={profile.building}
                        onChange={(event) => handleProfileFieldChange('building', event.target.value)}
                        placeholder="楼号"
                        aria-label="楼号"
                      />
                    </label>
                  </div>
                  <div className="settings-profile-actions">
                    <button className="btn btn-primary settings-save-btn" type="submit" disabled={!canSaveProfile || profileSaving}>保存</button>
                  </div>
                  {authStatusPresentation.quotaHint ? <div className="settings-profile-tip">{authStatusPresentation.quotaHint}</div> : null}
                </div>
              </form>
              {profileError ? <div className="form-hint error settings-profile-error">{profileError}</div> : null}
            </section>

            <div className="settings-divider" />

            <section className="settings-section settings-mode-section">
              <div className="settings-section-label">UI切换</div>
              <button
                className={`settings-switch ${assistantModeActive ? 'is-dialog' : 'is-fixed'}`}
                type="button"
                onClick={handleUiModeToggle}
                aria-label="切换 UI 模式"
                title="切换 UI 模式"
                aria-pressed={assistantModeActive}
              >
                <span className="settings-switch-track" aria-hidden="true">
                  <span className="settings-switch-thumb" />
                </span>
                <span className="settings-switch-text">{assistantModeActive ? '对话' : '固定'}</span>
              </button>
            </section>

            <div className="settings-divider" />

            <section className="settings-section settings-theme-section">
              <div className="settings-theme-label">主题切换</div>
              <button
                className={`settings-switch ${theme === 'dark' ? 'is-dark' : 'is-light'}`}
                type="button"
                onClick={handleThemeToggle}
                aria-label={themeActionLabel}
                title={themeActionLabel}
                aria-pressed={theme === 'dark'}
              >
                <span className="settings-switch-track" aria-hidden="true">
                  <span className="settings-switch-thumb">
                    <span className="settings-switch-icon">
                      {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
                    </span>
                  </span>
                </span>
                <span className="settings-switch-text">{theme === 'dark' ? '暗色' : '亮色'}</span>
              </button>
            </section>

            {!isVirtual && (
              <>
                <div className="settings-divider" />
                <section className="settings-section settings-notification-section">
                  <div className="settings-theme-label">通知提醒</div>
                  <button
                    className={`settings-switch ${pushNotifications.enabled ? 'is-light' : 'is-fixed'}`}
                    type="button"
                    onClick={handlePushNotificationToggle}
                    aria-label="切换后台通知提醒"
                    title={pushNotifications.error || '切换后台通知提醒'}
                    aria-pressed={pushNotifications.enabled}
                    disabled={!pushNotifications.supported || pushNotifications.busy || (!pushNotifications.available && Boolean(pushNotifications.error))}
                  >
                    <span className="settings-switch-track" aria-hidden="true">
                      <span className="settings-switch-thumb" />
                    </span>
                    <span className="settings-switch-text">{pushNotifications.enabled ? '开启' : '关闭'}</span>
                  </button>
                </section>
                {pushNotifications.error ? <div className="settings-inline-error">{pushNotifications.error}</div> : null}
              </>
            )}

            {!isVirtual && (
              <>
                <div className="settings-divider" />
                <button className="settings-menu-btn" type="button" onClick={openHistoryPanel}>
                  <span>历史记录</span>
                  <span className="settings-menu-btn-meta">查看</span>
                </button>
                <div className="settings-divider" />
                <button className="settings-menu-btn" type="button" onClick={openPrivacyPanel}>
                  <span>隐私政策</span>
                  <span className="settings-menu-btn-meta">查看</span>
                </button>
              </>
            )}

            {DEV_PREVIEW_MODE ? null : (
              <>
                <div className="settings-divider" />
                <button className="settings-logout-link" type="button" onClick={handleLogout}>
                  退出登录
                </button>
                {!isVirtual && (
                  <>
                    <div className="settings-divider" />
                    {showAccountDeleteConfirm ? (
                      <div className="settings-logout-confirm settings-account-delete-confirm" role="alert">
                        <span>确认注销当前账号？此操作不可恢复。</span>
                        <div className="settings-logout-confirm-actions">
                          <button className="btn btn-ghost settings-confirm-btn" type="button" onClick={handleAccountDeleteCancel} disabled={accountDeleting}>取消</button>
                          <button className="btn btn-primary settings-confirm-btn settings-danger-confirm-btn" type="button" onClick={handleAccountDelete} disabled={accountDeleting}>
                            {accountDeleting ? '注销中...' : '确认注销'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <a
                        className="settings-danger-link"
                        href="#delete-account"
                        onClick={handleAccountDeleteRequest}
                        aria-expanded={showAccountDeleteConfirm}
                      >
                        注销账号
                      </a>
                    )}
                  </>
                )}

                <div className="settings-divider" />
                <button className="btn btn-ghost settings-close-panel-btn" type="button" onClick={closeProfilePanel}>关闭</button>
              </>
            )}
            <div className="settings-divider" />
            <div className="settings-build-info" style={{ textAlign: 'center', fontSize: '11px', color: 'var(--muted)', opacity: 0.7, padding: '12px 0 4px' }}>
              版本: v{appVersion} (构建时间: {buildTime})
            </div>
          </div>
        </section>
      ) : null}

      {showHistoryPanel ? (
        <div className="history-panel-backdrop" onClick={closeHistoryPanel} role="presentation">
          <aside className="history-panel" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="历史记录">
            <div className="history-panel-head">
              <div>
                <h2>预约历史</h2>
              </div>
              <div className="history-panel-actions">
                <button className="icon-btn" type="button" onClick={() => loadAppointmentHistory({ force: true })} disabled={isHistoryBusy}>
                  {isHistoryBusy ? '加载中...' : '刷新'}
                </button>
                <button className="icon-btn" type="button" onClick={closeHistoryPanel}>关闭</button>
              </div>
            </div>
            {appointments.error ? <div className="form-hint error">{appointments.error}</div> : null}
            <div className="history-list history-panel-list">
              {isHistoryBusy && !historyCards.length ? (
                <>
                  <div className="history-skeleton" />
                  <div className="history-skeleton" />
                  <div className="history-skeleton" />
                </>
              ) : null}
              {historyCards.map((item) => {
                const packageCount = item.expected_package_count || item.package_count
                const historyTitle = getAppointmentSiteName(item) || '预约记录'
                const timeText = formatDateTime(item.appointment_time) || resolveAppointmentTimePreferenceLabel(item.time_preference) || '暂无'
                const slotText = resolveAppointmentTimePreferenceLabel(item.time_preference) || '暂无'
                const historyPickupCode = formatAppointmentCode(item)
                return (
                  <button
                    key={item._id}
                    type="button"
                    className="history-row"
                    style={{ '--accent': item.accentColor }}
                    onClick={() => handleOpenHistoryDetail(item)}
                  >
                    <div className="history-row-inner">
                      <div className="history-info-col">
                        <div className="history-title-wrap">
                          <div className="history-title">{historyTitle}</div>
                          <div className="history-subline">预约时间：{timeText}</div>
                          {historyPickupCode ? <div className="history-subline">取件码：{historyPickupCode}</div> : null}
                          {item.status === 'completed' && (item.picked_at || item.completed_at) ? (
                            <div className="history-subline">取件时间：{formatDateTime(item.picked_at || item.completed_at)}</div>
                          ) : null}
                        </div>
                        <div className="history-meta-row">
                          <span>取件时段：{slotText}</span>
                          {packageCount ? <span>{packageCount}单</span> : null}
                        </div>
                      </div>
                      <div className="history-action-col">
                        <span className={`status-badge status-${String(item.status || '').replace(/[^a-z0-9-]/g, '-')}`}>
                          {getAppointmentStatusLabel(item.status)}
                        </span>
                      </div>
                    </div>
                  </button>
                )
              })}
              {!historyCards.length && !isHistoryBusy ? <div className="empty-state">暂无历史记录</div> : null}
              {appointments.historyHasMore && historyCards.length ? (
                <button className="btn btn-ghost btn-block" type="button" onClick={handleHistoryLoadMore} disabled={appointments.historyLoading}>
                  {appointments.historyLoading ? '加载中...' : '加载更多'}
                </button>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}

      {showPrivacyPanel ? (
        <div className="history-panel-backdrop" onClick={closePrivacyPanel} role="presentation">
          <aside className="history-panel" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="隐私政策">
            <div className="history-panel-head">
              <div>
                <h2>隐私政策</h2>
              </div>
              <div className="history-panel-actions">
                <button className="icon-btn" type="button" onClick={closePrivacyPanel}>关闭</button>
              </div>
            </div>
            <div className="history-list history-panel-list" style={{ userSelect: 'text', paddingBottom: '20px' }}>
              {privacyLoading ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)', fontWeight: 800, fontSize: '13px' }}>加载中...</div>
              ) : privacyError ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--danger)', fontWeight: 800, fontSize: '13px' }}>{privacyError}</div>
              ) : (
                renderPrivacyContent(privacyContent)
              )}
            </div>
          </aside>
        </div>
      ) : null}

      {showSiteIdPrompt ? (
        <div className="site-id-backdrop" onClick={closeSiteIdPrompt} role="presentation">
          <section className="site-id-card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="输入站点码">
            <div className="site-id-head">
              <div>
                <h2>输入站点码</h2>
              </div>
              <button className="icon-btn" type="button" onClick={closeSiteIdPrompt}>关闭</button>
            </div>
            <p className="site-id-hint">{siteIdPromptHint || '当前页面未接入相机扫码，请输入站点码或粘贴完整扫码内容。'}</p>
            <form className="site-id-form" onSubmit={handleSiteIdPromptSubmit}>
              <label className="field">
                <span>站点码</span>
                <div className="input-with-clear">
                  <input
                    value={siteIdPromptValue}
                    onChange={(event) => {
                      setSiteIdPromptValue(event.target.value)
                      setSiteIdPromptError('')
                    }}
                    placeholder="请输入站点码"
                    autoFocus
                  />
                  {siteIdPromptValue ? (
                    <button
                      className="input-clear-btn"
                      type="button"
                      onClick={() => {
                        setSiteIdPromptValue('')
                        setSiteIdPromptError('')
                      }}
                      aria-label="清除"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  ) : null}
                </div>
              </label>
              {siteIdPromptError ? <div className="form-hint error">{siteIdPromptError}</div> : null}
              <button className="btn btn-primary btn-block" type="submit" disabled={siteIdPromptBusy}>
                {siteIdPromptBusy ? '加载中...' : '加载驿站'}
              </button>
            </form>
          </section>
        </div>
      ) : null}

      {showQrScanner ? (
        <div className="qr-scanner-backdrop" onClick={closeQrScanner} role="presentation">
          <section className="qr-scanner-card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="扫码输入站点ID">
            <div className="qr-scanner-head">
              <div>
                <h2>扫码输入站点ID</h2>
                <p>将站点二维码放入框内，识别后会自动填写。</p>
              </div>
              <button className="icon-btn" type="button" onClick={closeQrScanner}>关闭</button>
            </div>
            <div className="qr-scanner-preview">
              <video ref={qrScannerVideoRef} className="qr-scanner-video" autoPlay muted playsInline />
              <div className="qr-scanner-overlay" aria-hidden="true">
                <span className="qr-scanner-frame" />
                <span className="qr-scanner-scanline" />
              </div>
            </div>
            <div className="qr-scanner-status">{qrScannerBusy ? '正在启动摄像头...' : '将二维码放入取景框内'}</div>
            {qrScannerError ? <div className="form-hint error">{qrScannerError}</div> : null}
          </section>
        </div>
      ) : null}

      {showSiteInfoModal && selectedSite ? (
        <div className="site-id-backdrop" onClick={() => setShowSiteInfoModal(false)} role="presentation">
          <section className="site-id-card station-info-card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="驿站基本信息">
            <div className="site-id-head">
              <div>
                <h2>{selectedSite.name || '驿站信息'}</h2>
              </div>
              <button className="station-info-close-icon-btn" type="button" onClick={() => setShowSiteInfoModal(false)} aria-label="关闭">
                <CloseIcon />
              </button>
            </div>
            
            <div className="station-info-content">
              <div className="station-info-item">
                <div className="station-info-icon-col">
                  <svg viewBox="0 0 24 24" className="station-info-icon" aria-hidden="true">
                    <path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="station-info-detail-col">
                  <div className="station-info-label">地址</div>
                  <div className="station-info-value">{selectedSite.address || '暂无'}</div>
                </div>
              </div>

              <div className="station-info-item">
                <div className="station-info-icon-col">
                  <svg viewBox="0 0 24 24" className="station-info-icon" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <div className="station-info-detail-col">
                  <div className="station-info-label">营业时间</div>
                  <div className="station-info-value">
                    {selectedSite.businessHours?.start && selectedSite.businessHours?.end 
                      ? `${selectedSite.businessHours.start} - ${selectedSite.businessHours.end}`
                      : '暂无'}
                  </div>
                </div>
              </div>

              <div className="station-info-item">
                <div className="station-info-icon-col">
                  <svg viewBox="0 0 24 24" className="station-info-icon" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M7 7h10M7 12h10M7 17h10" />
                  </svg>
                </div>
                <div className="station-info-detail-col">
                  <div className="station-info-label">站点码</div>
                  <div className="station-info-value-row">
                    <span className="station-info-value">{selectedSite.siteCode || '暂无'}</span>
                    {selectedSite.siteCode ? (
                      <button 
                        className="station-info-copy-btn" 
                        type="button" 
                        onClick={() => {
                          navigator.clipboard.writeText(selectedSite.siteCode)
                          setActionToast({ message: '站点码已复制', tone: 'success' })
                        }}
                      >
                        复制
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="station-info-item">
                <div className="station-info-icon-col">
                  <svg viewBox="0 0 24 24" className="station-info-icon" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div className="station-info-detail-col">
                  <div className="station-info-label">联系电话</div>
                  <div className="station-info-value">
                    {selectedSite.phone ? (
                      <a href={`tel:${selectedSite.phone}`} className="station-info-phone-link">{selectedSite.phone}</a>
                    ) : '暂无'}
                  </div>
                </div>
              </div>
            </div>
            
            <button className="btn btn-primary btn-block station-info-close-btn" type="button" onClick={() => setShowSiteInfoModal(false)}>
              确定
            </button>
          </section>
        </div>
      ) : null}

      {!settingsRouteActive ? (
        assistantModeActive ? (
          <section className="assistant-page">
            <div className="assistant-transcript" ref={assistantTranscriptRef} onScroll={handleAssistantTranscriptScroll} role="log" aria-live="polite">
              {assistantHistoryLoadingOlder ? <div className="assistant-transcript-status">正在加载更早消息...</div> : null}
              {assistantHistoryOlderError ? <div className="assistant-transcript-status toast toast-error">{assistantHistoryOlderError}</div> : null}
              {assistantMessages.map((message) => (
                <AssistantMessageBubble
                  key={message.id}
                  message={message}
                  onChoice={handleAssistantCardChoice}
                  cardContext={{
                    appointmentForm: appointments.form,
                    profile,
                    currentSiteName,
                    siteSlots: site.slots,
                    appointmentSettings,
                    formSubmitting: appointments.formSubmitting,
                    formError: appointments.formError
                  }}
                />
              ))}
              {assistantConfirmDraft ? (
                <AssistantMessageBubble
                  message={{
                    id: 'assistant-confirm-draft',
                    role: 'assistant',
                    text: '请确认这次预约信息。',
                    cards: [{
                      type: 'appointment_confirm_card',
                      siteLabel: assistantConfirmDraft.siteLabel || currentSiteName || '当前驿站',
                      slotLabel: assistantConfirmDraft.slotLabel || resolveAppointmentTimePreferenceLabel(appointments.form.selectedTime),
                      countOptions: EXPECTED_PACKAGE_COUNT_OPTIONS
                    }],
                    actions: [],
                    pending: false,
                    error: ''
                  }}
                  onChoice={handleAssistantCardChoice}
                  cardContext={{
                    appointmentForm: appointments.form,
                    profile,
                    currentSiteName,
                    siteSlots: site.slots,
                    appointmentSettings,
                    formSubmitting: appointments.formSubmitting,
                    formError: appointments.formError
                  }}
                />
              ) : null}
              {assistantLoading ? (
                <AssistantMessageBubble
                  message={{
                    id: 'assistant-loading',
                    role: 'assistant',
                    text: '正在同步小助手会话...',
                    cards: [],
                    actions: [],
                    pending: true,
                    error: ''
                  }}
                />
              ) : null}
              {assistantError ? (
                <AssistantMessageBubble
                  message={{
                    id: 'assistant-error',
                    role: 'assistant',
                    text: '',
                    cards: [],
                    actions: [],
                    pending: false,
                    error: assistantError
                  }}
                />
              ) : null}
            </div>

            <div className="assistant-actions-bar" aria-label="预约快捷操作">
              <div className="assistant-current-appointment-anchor">
                <button
                  ref={assistantCurrentAppointmentButtonRef}
                  className={`assistant-composer-action assistant-current-appointment-action ${assistantAppointmentLocked ? 'has-badge' : ''}`}
                  type="button"
                  onClick={() => handleAssistantQuickAction('pickup')}
                  title={assistantAppointmentLocked ? '查看当前预约（有未完成预约）' : '预约取件'}
                  aria-label={assistantAppointmentLocked ? '当前预约，有未完成预约' : '预约取件'}
                  aria-haspopup={assistantAppointmentLocked ? 'dialog' : undefined}
                  aria-expanded={assistantAppointmentLocked ? showAssistantCurrentAppointmentPanel : undefined}
                >
                  <span>{assistantAppointmentLocked ? '当前预约' : '预约取件'}</span>
                  {assistantAppointmentLocked ? <span className="assistant-current-appointment-badge" aria-hidden="true" /> : null}
                </button>
              </div>
              <button
                className="assistant-composer-action"
                type="button"
                onClick={openHistoryPanel}
              >
                历史记录
              </button>
              <button
                className="assistant-composer-action assistant-clear-history-action"
                type="button"
                onClick={handleAssistantClearHistory}
                disabled={assistantClearing || assistantLoading || assistantSending}
                title="清除聊天记录"
              >
                {assistantClearing ? '清除中...' : '清空聊天'}
              </button>
            </div>

            <form className="assistant-composer" onSubmit={handleAssistantComposerSend}>
              <div className={`assistant-composer-input-shell ${assistantHasText ? 'has-text' : ''}`}>
                {!assistantHasText ? (
                  <button
                    className="assistant-composer-icon-btn assistant-composer-icon-btn-left"
                    type="button"
                    disabled={assistantSending}
                    aria-label="选择驿站"
                  >
                    <CameraIcon />
                  </button>
                ) : null}
                {assistantInputMode === 'keyboard' ? (
                  <input
                    ref={assistantInputRef}
                    value={assistantComposer}
                    onChange={(event) => setAssistantComposer(event.target.value)}
                    placeholder="发送消息或输入备注"
                    aria-label="发送消息或输入备注"
                    disabled={assistantSending}
                  />
                ) : (
                  <button
                    className={`assistant-composer-voice-pad ${assistantVoicePressing ? 'pressing' : ''} ${assistantVoiceListening ? 'recording' : ''} ${assistantVoiceCancelling ? 'cancelling' : ''}`}
                    type="button"
                    onPointerDown={handleAssistantVoicePressStart}
                    onPointerMove={handleAssistantVoicePressMove}
                    onPointerUp={handleAssistantVoicePressEnd}
                    onPointerCancel={handleAssistantVoicePressCancel}
                    aria-label={assistantVoicePadLabel}
                    disabled={assistantSending}
                  >
                    <span className="assistant-composer-voice-wave" aria-hidden="true" />
                  </button>
                )}
                {assistantHasText ? (
                  <button
                    className="assistant-composer-send-btn"
                    type="submit"
                    disabled={assistantSending}
                    aria-label="发送消息"
                  >
                    <SendIcon />
                  </button>
                ) : (
                  <div className="assistant-composer-icon-group" aria-label="辅助操作">
                    <button
                      className="assistant-composer-icon-btn"
                      type="button"
                      onClick={handleAssistantInputModeToggle}
                      disabled={assistantSending}
                      aria-label={assistantInputMode === 'keyboard' ? '切换到语音输入' : '切换回键盘输入'}
                    >
                      {assistantInputMode === 'keyboard' ? <MicrophoneIcon /> : <KeyboardIcon />}
                    </button>
                    <button
                      className="assistant-composer-icon-btn"
                      type="button"
                      disabled={assistantSending || assistantAppointmentLocked}
                      aria-label="更多快捷操作"
                    >
                      <PlusIcon />
                    </button>
                  </div>
                )}
              </div>
            </form>

            {showAssistantCurrentAppointmentPanel && assistantCurrentAppointmentCard ? (
              <div className="modal-backdrop" onClick={closeAssistantCurrentAppointmentPanel} role="presentation" style={{ zIndex: 140, placeItems: 'center' }}>
                <div
                  ref={assistantCurrentAppointmentPanelRef}
                  className="assistant-current-appointment-popover"
                  onClick={(event) => event.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                  aria-label="当前预约状态"
                >
                  <AssistantCurrentAppointmentSummary
                    card={assistantCurrentAppointmentCard}
                    variant="popover"
                    onClose={closeAssistantCurrentAppointmentPanel}
                    onAction={handleAppointmentAction}
                    busy={appointments.actionBusyId === String(assistantCurrentAppointmentCard?.appointment?._id || '')}
                  />
                </div>
              </div>
            ) : null}

            {showComposerMenu ? (
              <div className="assistant-menu-backdrop" onClick={closeComposerMenu} role="presentation">
                <section className="assistant-menu card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="更多快捷操作">
                  <div className="assistant-menu-head">
                    <div>
                      <div className="assistant-menu-kicker">快捷操作</div>
                      <h2>更多操作</h2>
                    </div>
                    <button className="icon-btn" type="button" onClick={closeComposerMenu}>关闭</button>
                  </div>
                  <div className="assistant-menu-grid">
                    <button className="assistant-menu-item" type="button" onClick={() => handleAssistantQuickAction('site')}>选择驿站</button>
                    <button className="assistant-menu-item" type="button" onClick={() => handleAssistantQuickAction('time')}>选择时段</button>
                    <button className="assistant-menu-item" type="button" onClick={() => handleAssistantQuickAction('count')}>切换单数</button>
                    {showShareOption ? (
                      <button className="assistant-menu-item" type="button" onClick={() => handleAssistantQuickAction('share')}>共享预约</button>
                    ) : null}
                    <button className="assistant-menu-item" type="button" onClick={() => handleAssistantQuickAction('clear')}>清空输入</button>
                  </div>
                </section>
              </div>
            ) : null}

            {assistantSheet === 'site' ? (
              <div className="assistant-sheet-backdrop" onClick={closeAssistantSheet} role="presentation">
                <section className="assistant-sheet card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="选择驿站">
                  <div className="assistant-sheet-head">
                    <div>
                      <div className="assistant-sheet-kicker">快捷选择</div>
                      <h2>选择驿站</h2>
                    </div>
                    <button className="icon-btn" type="button" onClick={closeAssistantSheet}>关闭</button>
                  </div>
                  {stationOptions.length ? (
                    <div className="assistant-chip-grid">
                      {stationOptions.map((item, index) => (
                        <button
                          key={item.value || index}
                          type="button"
                          className={`assistant-chip ${currentStationIndex === index ? 'selected' : ''}`}
                          style={{ borderColor: item.accentColor, backgroundColor: item.accentSoftColor }}
                          onClick={() => handleAssistantStationSelect(index)}
                        >
                          {item.label || item.value}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-note">没有最近驿站，先扫码输入一个站点。</div>
                  )}
                  <div className="assistant-inline-actions">
                    <button className="btn btn-light" type="button" onClick={scanQRCode}>扫码选择</button>
                    <button className="btn btn-ghost" type="button" onClick={() => void loadCurrentAppointments({ force: true })} disabled={isSiteBusy || isAppointmentsBusy}>刷新</button>
                  </div>
                </section>
              </div>
            ) : null}

            {assistantSheet === 'time' ? (
              <div className="assistant-sheet-backdrop" onClick={closeAssistantSheet} role="presentation">
                <section className="assistant-sheet card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="选择时段">
                  <div className="assistant-sheet-head">
                    <div>
                      <div className="assistant-sheet-kicker">快捷选择</div>
                      <h2>选择预约时段</h2>
                    </div>
                    <button className="icon-btn" type="button" onClick={closeAssistantSheet}>关闭</button>
                  </div>
                  {isSiteBusy && !slots.length ? (
                    <div className="slot-grid assistant-slot-grid">
                      <div className="empty-note-loading">正在加载可选时间段...</div>
                    </div>
                  ) : slots.length ? (
                    <div className="slot-grid assistant-slot-grid">
                      {slots.map((slot) => (
                        <button
                          key={slot.value}
                          type="button"
                          className={`slot-chip ${appointments.form.selectedTime === slot.value ? 'selected' : ''} ${slot.available === false ? 'disabled' : ''}`}
                          disabled={slot.available === false}
                          onClick={() => handleAssistantTimeSelect(slot.value)}
                        >
                          <span>{resolveAppointmentTimePreferenceLabel(slot.label || slot.value)}</span>
                          {slot.available === false ? <small>已满</small> : null}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-slot-note">当前驿站未配置可选时间段。</div>
                  )}
                </section>
              </div>
            ) : null}
          </section>
        ) : (
          <>
            <section className="hero-panel">
              <div className="hero-shell">
                <div className="hero-copy">
                  <div className="home-hero-bottom">
                    <button
                      className="station-info-trigger-btn"
                      type="button"
                      onClick={() => {
                        if (selectedSite) {
                          setShowSiteInfoModal(true)
                        } else {
                          setActionToast({ message: '请先选择驿站', tone: 'error' })
                        }
                      }}
                      title="查看驿站基本信息"
                      aria-label="查看驿站基本信息"
                    >
                      当前驿站
                      <span className="info-icon-wrapper" aria-hidden="true">
                        <svg viewBox="0 0 24 24" className="info-mini-icon">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v-4" />
                          <path d="M12 8h.01" />
                        </svg>
                      </span>
                    </button>
                    <div className="station-picker">
                      <button className="station-picker-display" type="button" onClick={toggleStationDropdown}>
                        <span className="station-picker-text">{hasSiteContext ? currentSiteName : '请选择驿站'}</span>
                        <span className="station-picker-arrow">{showStationDropdown ? '▲' : '▼'}</span>
                      </button>

                      {showStationDropdown ? (
                        <div className="station-dropdown">
                          {stationOptions.map((item, index) => (
                            <button
                              key={item.value || index}
                              type="button"
                              className={`station-dropdown-item ${currentStationIndex === index ? 'active' : ''}`}
                              onClick={() => handleStationChange({
                                currentTarget: {
                                  dataset: { index }
                                },
                                detail: {
                                  value: index
                                }
                              })}
                            >
                              <span className="station-dropdown-item-text">{item.label || item.value}</span>
                              {currentStationIndex === index ? <span className="station-dropdown-check">✓</span> : null}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <button className="scan-trigger" type="button" onClick={scanQRCode}>
                      <span className="scan-icon-frame">
                        <span className="scan-corner scan-corner-lt" />
                        <span className="scan-corner scan-corner-rt" />
                        <span className="scan-corner scan-corner-lb" />
                        <span className="scan-corner scan-corner-rb" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {notificationAppointments.length > 0 ? notificationAppointments.map((notifAppt) => {
              const notifId = String(notifAppt._id || '')
              const notifActionKind = getAppointmentActionKind(notifAppt)
              const notifSiteName = getAppointmentSiteName(notifAppt) || '驿站'
              const notifBusy = appointments.actionBusyId === notifId
              const notifVariant = notifActionKind === 'dismiss-invalid' ? 'is-invalid' : notifActionKind === 'dismiss-expired' ? 'is-expired' : 'is-eviction'
              const notifTail = String(notifAppt.phone_tail || notifAppt.phone || '').replace(/\D/g, '').slice(-4) || profilePhoneTail
              
              const notifStatusText = notifActionKind === 'dismiss-expired' ? '已过期' : notifActionKind === 'dismiss-eviction' ? '已清退' : '已失效'
              const expectedCount = normalizeExpectedPackageCount(notifAppt.expected_package_count ?? notifAppt.expectedPackageCount ?? 1, 1)

              const cleanRecipient = String(notifAppt.name || notifAppt.recipient || '').trim()
              const cleanProfileName = String(profile.name || '').trim()
              const notifRecipient = (cleanRecipient && cleanRecipient !== '未设置')
                ? cleanRecipient
                : (cleanProfileName && cleanProfileName !== '未设置')
                  ? cleanProfileName
                  : '未设置'

              const rawNotifBuilding = String(notifAppt.building || '').trim()
              const rawProfileBuilding = String(profile.building || '').trim()
              const notifBuilding = (rawNotifBuilding && rawNotifBuilding !== '暂无')
                ? rawNotifBuilding
                : (rawProfileBuilding && rawProfileBuilding !== '暂无')
                  ? rawProfileBuilding
                  : '----'

              const timePreferenceVal = notifAppt.time_preference || notifAppt.timePreference
              const notifTimePreference = timePreferenceVal ? resolveAppointmentTimePreferenceLabel(timePreferenceVal) : '----'

              return (
                <section key={notifId} className={`card appointment-current-summary-card is-notification ${notifVariant}`} aria-label="预约通知">
                  <div className="assistant-current-appointment-card is-inline">
                    <div className="assistant-current-appointment-head">
                      <div className="assistant-current-appointment-copy">
                        <strong>{notifSiteName}</strong>
                      </div>
                      <div className="assistant-current-appointment-head-actions">
                        {notifVariant !== 'is-invalid' ? (
                          <span className="assistant-appointment-preview-status is-invalid-status">
                            {notifStatusText}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="assistant-current-appointment-code-row">
                      <div className="assistant-current-appointment-code-block is-invalid-block">
                        <strong>{notifVariant === 'is-invalid' ? notifStatusText : (notifAppt.pickup_display_mode === 'pickup_code' ? (formatAppointmentCode(notifAppt) || '暂无') : notifTail)}</strong>
                        {notifVariant !== 'is-invalid' ? (
                          <span>{notifAppt.pickup_display_mode === 'pickup_code' ? '取件码' : '手机尾号'}</span>
                        ) : null}
                      </div>
                      <div className="assistant-current-appointment-count-block is-single-col is-invalid-count-block">
                        <div className="count-col">
                          <div className="count-value">
                            <strong>{expectedCount}</strong>
                            <span>件</span>
                          </div>
                          <div className="count-label">预约</div>
                        </div>
                      </div>
                    </div>

                    <div className="assistant-current-appointment-meta">
                      <span>取件人 {notifRecipient}</span>
                      <span>尾号 {notifTail}</span>
                      <span>楼号 {notifBuilding}</span>
                      <span>时间段 {notifTimePreference}</span>
                      {notifAppt.createdAt || notifAppt.appointment_time ? (
                        <span className="notif-appt-time">
                          提交时间：{formatDateTime(notifAppt.createdAt || notifAppt.appointment_time)}
                        </span>
                      ) : null}
                    </div>

                    <div className="assistant-current-appointment-actions" style={{ marginTop: '12px' }}>
                      <button
                        className="btn btn-secondary assistant-current-appointment-action-btn notif-dismiss-btn"
                        type="button"
                        onClick={() => handleAppointmentAction(notifAppt, notifActionKind)}
                        disabled={notifBusy}
                      >
                        {notifBusy ? '处理中...' : '我知道了'}
                      </button>
                    </div>
                  </div>
                </section>
              )
            }) : null}

            {!hasSiteContext && (
              <section className="welcome-setup-section">
                <div className="welcome-setup-icon" style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 20px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#a855f7'
                }}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: '32px', height: '32px', fill: 'none', stroke: 'currentColor', strokeWidth: '1.8', strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>欢迎使用智能柜服务</h3>
                <p style={{ margin: '0 0 24px', fontSize: '14px', lineHeight: '1.6', color: 'var(--muted)' }}>
                  {isPendingVirtual 
                    ? '这是一个临时免密验证账号。请先扫描驿站二维码，或点击上方选择驿站以完成账号绑定并开始使用。' 
                    : '请选择要使用的驿站。扫描驿站二维码，或点击上方选择驿站以开始使用。'}
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button className="btn btn-primary" type="button" onClick={() => scanQRCode()} style={{ padding: '10px 24px', borderRadius: '12px' }}>
                    扫码绑定
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={() => setShowSiteIdPrompt(true)} style={{ padding: '10px 24px', borderRadius: '12px' }}>
                    输入站点码
                  </button>
                </div>
              </section>
            )}

            {hasSiteContext ? (
              currentAppointmentSummaryCard ? (
                <section className="card appointment-current-summary-card" aria-label="当前预约信息">
                  <AssistantCurrentAppointmentSummary
                    card={currentAppointmentSummaryCard}
                    variant="inline"
                    onAction={handleAppointmentAction}
                    busy={appointments.actionBusyId === String(currentAppointmentSummaryCard?.appointment?._id || '')}
                  />
                </section>
              ) : (
                <section className="card form-card appointment-form-card">
                  <button className={`appointment-form-trigger ${appointments.appointmentExpanded ? 'is-expanded' : 'is-collapsed'}`} type="button" onClick={handleToggleAppointmentForm}>
                    <span className="appointment-form-trigger-main">填写预约信息</span>
                    <span className={`appointment-form-trigger-arrow ${appointments.appointmentExpanded ? 'is-expanded' : ''}`}>⌃</span>
                  </button>

                  <div className={`appointment-form-content ${appointments.appointmentExpanded ? 'is-expanded' : ''}`}>
                    <div className="appointment-readonly-info">
                      <span className="appointment-identity-line">取件人：{profile.name || '未设置'}  尾号：{profilePhoneTail}  楼号：{profile.building || '未设置'}</span>
                    </div>
                    {isWeakIdentifySession ? <div className="appointment-auth-notice">{authStatusPresentation.quotaHint}</div> : null}

                    <div className="appointment-form-body">
                      {appointmentSettings.enabled === false ? (
                        <div className="appointment-closed-notice" style={{ 
                          margin: '16px 0', 
                          padding: '24px 20px', 
                          background: 'rgba(244, 63, 94, 0.08)', 
                          border: '1px solid rgba(244, 63, 94, 0.18)', 
                          borderRadius: '16px', 
                          textAlign: 'center',
                          color: '#fecdd3'
                        }}>
                          <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: '48px', height: '48px', margin: '0 auto 12px', display: 'block', fill: 'none', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                            <circle cx="12" cy="12" r="10" />
                            <path d="m15 9-6 6" />
                            <path d="m9 9 6 6" />
                          </svg>
                          <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '800', color: '#fff1f2' }}>预约已关闭</h3>
                          <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5', opacity: 0.9 }}>当前驿站已关闭预约功能，暂停接收新的预约。</p>
                        </div>
                      ) : (
                        <form className="appointment-form" onSubmit={handleAppointmentSubmit}>
                          <div className="field">
                            <span>预约时间段</span>
                            {isSiteBusy && !slots.length ? (
                              <div className="slot-grid">
                                <div className="empty-note-loading">正在加载可选时间段...</div>
                              </div>
                            ) : slots.length ? (
                              <div className="slot-grid">
                                {slots.map((slot) => (
                                  <button
                                    key={slot.value}
                                    type="button"
                                    className={`slot-chip ${appointments.form.selectedTime === slot.value ? 'selected' : ''} ${slot.available === false ? 'disabled' : ''}`}
                                    disabled={hasBlockingCurrentAppointment || slot.available === false}
                                    onClick={() => handleAppointmentFieldChange('selectedTime', slot.value)}
                                  >
                                    <span>{resolveAppointmentTimePreferenceLabel(slot.label || slot.value)}</span>
                                    {slot.available === false ? <small>已满</small> : null}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="empty-slot-note">当前驿站未配置可选时间段。</div>
                            )}
                          </div>

                          <div className={`inline-fields ${showShareOption ? '' : 'single-column'}`}>
                            <label className="field">
                              <span>预约单数</span>
                              <select
                                value={appointments.form.expectedPackageCount}
                                disabled={hasBlockingCurrentAppointment}
                                onChange={(event) => handleAppointmentFieldChange('expectedPackageCount', event.target.value)}
                              >
                                {EXPECTED_PACKAGE_COUNT_OPTIONS.map((count) => (
                                  <option key={count} value={count}>
                                    {count}
                                  </option>
                                ))}
                              </select>
                            </label>
                            {showShareOption ? (
                              <label className="checkbox-field">
                                <input
                                  type="checkbox"
                                  checked={appointments.form.acceptShare}
                                  disabled={hasBlockingCurrentAppointment}
                                  onChange={(event) => handleAppointmentFieldChange('acceptShare', event.target.checked)}
                                />
                                <span>允许共享预约</span>
                              </label>
                            ) : null}
                          </div>

                          {showRemarkField ? (
                            <label className="field">
                              <span>备注</span>
                              <textarea
                                rows="3"
                                value={appointments.form.message}
                                disabled={hasBlockingCurrentAppointment}
                                onChange={(event) => handleAppointmentFieldChange('message', event.target.value)}
                                placeholder="如需说明可在这里补充"
                              />
                            </label>
                          ) : null}

                          {appointments.formError ? <div className="form-hint error">{appointments.formError}</div> : null}
                          <button className="btn btn-primary btn-block" type="submit" disabled={hasBlockingCurrentAppointment || appointments.currentLoading || appointments.formSubmitting || !currentSiteId || isSiteBusy}>
                            {hasBlockingCurrentAppointment ? '已有未完成预约' : appointments.currentLoading ? '同步当前预约中' : appointments.formSubmitting ? '提交中...' : '提交预约'}
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </section>
              )
            ) : null}

            {hasSiteContext && announcement ? (
              <section className="announcement-card card" onClick={handleToggleAnnouncement} role="button" tabIndex={0}>
                <div className="announcement-strip" style={{ backgroundColor: siteAccent }} />
                <div className="announcement-content">
                  <div className="announcement-head">
                    <div className="announcement-badge">公告</div>
                    <div className="announcement-time">{formatDateTime(announcement.publishedAt || announcement.updatedAt || announcement.createdAt)}</div>
                  </div>
                  <div className="announcement-title">{announcement.title || '站点公告'}</div>
                  <div className={`announcement-body ${site.announcementExpanded ? 'expanded' : ''}`}>
                    {announcement.summary || announcement.content}
                  </div>
                  <div className="announcement-footer">
                    <span>{site.announcementExpanded ? '收起' : '展开查看'}</span>
                    <span className="announcement-chev">{site.announcementExpanded ? '▴' : '▾'}</span>
                  </div>
                </div>
              </section>
            ) : null}
          </>
        )
      ) : null}

      {historyDetail ? (
        <div className="modal-backdrop" onClick={handleCloseHistoryDetail} role="presentation">
          <div className="modal-card detail-modal-card" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <div className="card-head detail-modal-head">
              <div>
                <h2>{String(historyDetail.name || historyDetail.recipient || '预约详情').trim()}</h2>
              </div>
            </div>
                <div className="detail-grid detail-grid-compact">
                  <div><span>驿站</span><strong>{getAppointmentSiteName(historyDetail) || '暂无驿站信息'}</strong></div>
                  <div><span>预约时间</span><strong>{formatDateTime(historyDetail.appointment_time) || resolveAppointmentTimePreferenceLabel(historyDetail.time_preference) || '暂无'}</strong></div>
                  {historyDetail.status === 'completed' ? (
                    <div><span>取件时段</span><strong>{resolveAppointmentTimePreferenceLabel(historyDetail.time_preference) || '暂无'}</strong></div>
                  ) : null}
                  <div><span>状态</span><strong>{getAppointmentStatusLabel(historyDetail.status)}</strong></div>
                  <div><span>楼号</span><strong>{historyDetail.building || '暂无'}</strong></div>
                  {formatAppointmentCode(historyDetail) ? (
                    <div><span>取件码</span><strong>{formatAppointmentCode(historyDetail)}</strong></div>
                  ) : null}
              {normalizeAppointmentStorageAllocations(historyDetail).length ? (
                <div className="detail-grid-span-2">
                  <span>位置</span>
                  <strong>
                    {normalizeAppointmentStorageAllocations(historyDetail)
                      .map((item) => `${item.compartmentLabel || item.compartmentId || '--'} (${item.packageCount}件)`)
                      .join('，')}
                  </strong>
                </div>
              ) : null}
              <div><span>备注</span><strong>{historyDetail.remark || '无'}</strong></div>
            </div>
            <div className="inline-actions detail-modal-actions">
              <button className="btn btn-ghost" type="button" onClick={handleCloseHistoryDetail}>关闭</button>
            </div>
          </div>
        </div>
      ) : null}

      {installHelpModal}
    </div>
  )
}

export default App
